"use strict";

import PerformanceTimer from "/app/deps/hologram/assets/js/performance_timer.mjs";

const startTime = performance.now();

globalThis.hologram.pageReachableFunctionDefs = (deps) => {
	const {
		Bitstring,
		ERTS,
		HologramBoxedError,
		HologramInterpreterError,
		Interpreter,
		MemoryStorage,
		Type,
		Utils,
	} = deps;

	Interpreter.defineErlangFunction("binary", "copy", 2, (subject, count) => {
		if (!Type.isBinary(subject)) {
			const msg = Type.isBitstring(subject)
				? "is a bitstring (expected a binary)"
				: "not a binary";

			Interpreter.raiseArgumentError(Interpreter.buildArgumentErrorMsg(1, msg));
		}

		if (!Type.isInteger(count)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not an integer"),
			);
		}

		if (count.value < 0n) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "out of range"),
			);
		}

		if (count.value === 0n) {
			return Bitstring.fromText("");
		}

		if (count.value === 1n) {
			return subject;
		}

		const countNumber = Number(count.value);

		if (subject.text !== null) {
			return Bitstring.fromText(subject.text.repeat(countNumber));
		}

		if (subject.bytes.length === 0) {
			return Bitstring.fromText("");
		}

		const sourceBytes = subject.bytes;
		const sourceLength = sourceBytes.length;
		const totalLength = sourceLength * countNumber;
		const resultBytes = new Uint8Array(totalLength);

		for (let i = 0; i < countNumber; i++) {
			resultBytes.set(sourceBytes, i * sourceLength);
		}

		return Bitstring.fromBytes(resultBytes);
	});

	Interpreter.defineNotImplementedErlangFunction("re", "import", 1);

	Interpreter.defineErlangFunction("string", "titlecase", 1, (subject) => {
		// Custom uppercase mapping where Erlang differs from JavaScript's toUpperCase()
		// This mapping is extracted from scripts/uppercase_mapping/comparison.txt
		// Format: { codepoint: [codepoints] }
		const MAPPING = {
			223: [83, 115],
			411: [411],
			452: [453],
			453: [453],
			454: [453],
			455: [456],
			456: [456],
			457: [456],
			458: [459],
			459: [459],
			460: [459],
			497: [498],
			498: [498],
			499: [498],
			612: [612],
			1415: [1333, 1410],
			4349: [4349],
			4350: [4350],
			4351: [4351],
			7306: [7306],
			8064: [8072],
			8065: [8073],
			8066: [8074],
			8067: [8075],
			8068: [8076],
			8069: [8077],
			8070: [8078],
			8071: [8079],
			8080: [8088],
			8081: [8089],
			8082: [8090],
			8083: [8091],
			8084: [8092],
			8085: [8093],
			8086: [8094],
			8087: [8095],
			8096: [8104],
			8097: [8105],
			8098: [8106],
			8099: [8107],
			8100: [8108],
			8101: [8109],
			8102: [8110],
			8103: [8111],
			8114: [8122, 837],
			8115: [8124],
			8116: [902, 837],
			8119: [913, 834, 837],
			8124: [8124],
			8130: [8138, 837],
			8131: [8140],
			8132: [905, 837],
			8135: [919, 834, 837],
			8140: [8140],
			8178: [8186, 837],
			8179: [8188],
			8180: [911, 837],
			8183: [937, 834, 837],
			8188: [8188],
			42957: [42957],
			42971: [42971],
			64256: [70, 102],
			64257: [70, 105],
			64258: [70, 108],
			64259: [70, 102, 105],
			64260: [70, 102, 108],
			64261: [83, 116],
			64262: [83, 116],
			64275: [1348, 1398],
			64276: [1348, 1381],
			64277: [1348, 1387],
			64278: [1358, 1398],
			64279: [1348, 1389],
		};

		// Helper: Extract first codepoint using unicode_util.cp/1
		const extractFirstCodepoint = (subject) => {
			const cpResult = Erlang_Unicode_Util["cp/1"](subject);

			// Check if unicode_util.cp/1 returned an error tuple
			if (Type.isTuple(cpResult)) {
				// Extract the binary from the error tuple {:error, binary}
				const errorBinary = cpResult.data[1];
				Interpreter.raiseArgumentError(
					`argument error: ${Interpreter.inspect(errorBinary)}`,
				);
			}

			// Return null for empty results
			if (cpResult.data.length === 0) {
				return null;
			}

			// Extract codepoint number and rest
			const firstCodepoint = cpResult.data[0];
			const codepointNum = Number(firstCodepoint.value);
			const rest = cpResult.data.slice(1);

			return { codepointNum, rest };
		};

		// Helper: Uppercase a single codepoint and return array of uppercased codepoints
		const uppercaseCodepoint = (codepoint) => {
			if (
				(codepoint >= 4304 && codepoint <= 4346) ||
				(codepoint >= 8072 && codepoint <= 8079) ||
				(codepoint >= 8088 && codepoint <= 8095) ||
				(codepoint >= 8104 && codepoint <= 8111) ||
				(codepoint >= 68976 && codepoint <= 68997)
			) {
				return [codepoint];
			} else if (Object.hasOwn(MAPPING, codepoint)) {
				return MAPPING[codepoint];
			} else {
				const char = String.fromCodePoint(codepoint);
				const uppercased = char.toUpperCase();
				return Array.from(uppercased).map((c) => c.codePointAt(0));
			}
		};

		// Handle binary strings
		if (Type.isBinary(subject)) {
			const extraction = extractFirstCodepoint(subject);

			if (extraction === null) {
				return Type.bitstring("");
			}

			const { codepointNum, rest } = extraction;
			const restBinary = rest[0]; // Tail of the improper list
			const restText = Bitstring.toText(restBinary);

			const uppercasedCodepoints = uppercaseCodepoint(codepointNum);
			const uppercasedFirst = String.fromCodePoint(...uppercasedCodepoints);

			return Type.bitstring(uppercasedFirst + restText);
		}

		// Handle lists (charlists/iolists)
		if (Type.isList(subject)) {
			const extraction = extractFirstCodepoint(subject);

			if (extraction === null) {
				return Type.list();
			}

			const { codepointNum, rest } = extraction;
			const uppercasedCodepoints = uppercaseCodepoint(codepointNum).map((cp) =>
				Type.integer(cp),
			);

			const firstRest = rest.length > 0 ? rest[0] : null;

			// Check for ligature grouping: ligatures (64256-64262) that expand to multiple
			// codepoints should be grouped when followed by a non-empty binary
			const isLigature = codepointNum >= 64_256 && codepointNum <= 64_262;
			if (
				isLigature &&
				uppercasedCodepoints.length > 1 &&
				firstRest &&
				Type.isBitstring(firstRest) &&
				firstRest.text.length > 0
			) {
				return Type.list([Type.list(uppercasedCodepoints), ...rest]);
			}

			// Filter out empty binary at the end (for improper list tails)
			if (
				rest.length === 1 &&
				Type.isBitstring(firstRest) &&
				firstRest.text.length === 0
			) {
				return Type.list([...uppercasedCodepoints]);
			}

			// Default: combine uppercased codepoint(s) with the rest
			return Type.list([...uppercasedCodepoints, ...rest]);
		}

		// If subject is neither binary nor list, raise FunctionClauseError
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":string.titlecase/1", [subject]),
		);
	});

	Interpreter.defineElixirFunction(
		"Calendar",
		"compatible_calendars?",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("calendar_0"),
					Type.variablePattern("calendar_0"),
				],
				guards: [],
				body: (context) => {
					return Type.atom("true");
				},
			},
			{
				params: (context) => [
					Type.variablePattern("calendar1_0"),
					Type.variablePattern("calendar2_1"),
				],
				guards: [],
				body: (context) => {
					return Erlang["==/2"](
						Interpreter.callNamedFunction(
							context.vars.calendar1_0,
							Type.atom("day_rollover_relative_to_midnight_utc"),
							Type.list([]),
							context,
						),
						Interpreter.callNamedFunction(
							context.vars.calendar2_1,
							Type.atom("day_rollover_relative_to_midnight_utc"),
							Type.list([]),
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction("Date", "convert", 2, "public", [
		{
			params: (context) => [
				Type.map([
					[Type.atom("calendar"), Type.variablePattern("calendar_0")],
					[Type.atom("day"), Type.variablePattern("day_3")],
					[Type.atom("month"), Type.variablePattern("month_2")],
					[Type.atom("year"), Type.variablePattern("year_1")],
				]),
				Type.variablePattern("calendar_0"),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("ok"),
					Elixir_Date["__struct__/1"](
						Type.list([
							Type.tuple([Type.atom("calendar"), context.vars.calendar_0]),
							Type.tuple([Type.atom("year"), context.vars.year_1]),
							Type.tuple([Type.atom("month"), context.vars.month_2]),
							Type.tuple([Type.atom("day"), context.vars.day_3]),
						]),
					),
				]);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_1"),
					Type.map([
						[Type.atom("calendar"), Type.variablePattern("calendar_0")],
					]),
					context,
				),
				Type.variablePattern("target_calendar_2"),
			],
			guards: [],
			body: (context) => {
				globalThis.hologram.return = Interpreter.case(
					Elixir_Calendar["compatible_calendars?/2"](
						context.vars.calendar_0,
						context.vars.target_calendar_2,
					),
					[
						{
							match: Type.variablePattern("x_3"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.tuple([
									Type.atom("error"),
									Type.atom("incompatible_calendars"),
								]);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Elixir_Date["from_iso_days/2"](
										Elixir_Date["to_iso_days/1"](context.vars.date_1),
										context.vars.target_calendar_2,
									),
									Type.variablePattern("result_date_4"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Type.tuple([
									Type.atom("ok"),
									context.vars.result_date_4,
								]);
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.hologram.return;
			},
		},
	]);

	Interpreter.defineElixirFunction("Date", "convert!", 2, "public", [
		{
			params: (context) => [
				Type.variablePattern("date_0"),
				Type.variablePattern("calendar_1"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Date["convert/2"](
						context.vars.date_0,
						context.vars.calendar_1,
					),
					[
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("value_2"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.value_2;
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.variablePattern("reason_3"),
							]),
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(Type.string("cannot convert "), {
												type: "binary",
											}),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.date_0),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Type.string(" to target calendar "),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.calendar_1),
												{ type: "binary" },
											),
											Type.bitstringSegment(Type.string(", "), {
												type: "binary",
											}),
											Type.bitstringSegment(Type.string("reason: "), {
												type: "binary",
											}),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.reason_3),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
					],
					context,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("Date", "from_iso_days", 2, "private", [
		{
			params: (context) => [
				Type.tuple([Type.variablePattern("days_0"), Type.matchPlaceholder()]),
				Type.atom("Elixir.Calendar.ISO"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Calendar_ISO["date_from_iso_days/1"](context.vars.days_0),
					Type.tuple([
						Type.variablePattern("year_1"),
						Type.variablePattern("month_2"),
						Type.variablePattern("day_3"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Date["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("year"), context.vars.year_1]),
						Type.tuple([Type.atom("month"), context.vars.month_2]),
						Type.tuple([Type.atom("day"), context.vars.day_3]),
						Type.tuple([
							Type.atom("calendar"),
							Type.atom("Elixir.Calendar.ISO"),
						]),
					]),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("iso_days_0"),
				Type.variablePattern("target_calendar_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.callNamedFunction(
						context.vars.target_calendar_1,
						Type.atom("naive_datetime_from_iso_days"),
						Type.list([context.vars.iso_days_0]),
						context,
					),
					Type.tuple([
						Type.variablePattern("year_2"),
						Type.variablePattern("month_3"),
						Type.variablePattern("day_4"),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Date["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("year"), context.vars.year_2]),
						Type.tuple([Type.atom("month"), context.vars.month_3]),
						Type.tuple([Type.atom("day"), context.vars.day_4]),
						Type.tuple([Type.atom("calendar"), context.vars.target_calendar_1]),
					]),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("Date", "to_iso8601", 1, "public", [
		{
			params: (context) => [Type.variablePattern("x0_0")],
			guards: [],
			body: (context) => {
				return Elixir_Date["to_iso8601/2"](
					context.vars.x0_0,
					Type.atom("extended"),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("Date", "to_iso8601", 2, "public", [
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_0"),
					Type.map([[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")]]),
					context,
				),
				Type.variablePattern("format_1"),
			],
			guards: [
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["=:=/2"](context.vars.format_1, Type.atom("basic")),
						(context) =>
							Erlang["=:=/2"](context.vars.format_1, Type.atom("extended")),
						context,
					),
			],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.date_0,
					Type.map([
						[Type.atom("day"), Type.variablePattern("day_4")],
						[Type.atom("month"), Type.variablePattern("month_3")],
						[Type.atom("year"), Type.variablePattern("year_2")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Calendar_ISO["date_to_string/4"](
					context.vars.year_2,
					context.vars.month_3,
					context.vars.day_4,
					context.vars.format_1,
				);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_0"),
					Type.map([[Type.atom("calendar"), Type.matchPlaceholder()]]),
					context,
				),
				Type.variablePattern("format_1"),
			],
			guards: [
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["=:=/2"](context.vars.format_1, Type.atom("basic")),
						(context) =>
							Erlang["=:=/2"](context.vars.format_1, Type.atom("extended")),
						context,
					),
			],
			body: (context) => {
				return Elixir_Date["to_iso8601/1"](
					Elixir_Date["convert!/2"](
						context.vars.date_0,
						Type.atom("Elixir.Calendar.ISO"),
					),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction(
		"Ecto.Association.NotLoaded",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[Type.atom("__cardinality__"), Type.atom("nil")],
						[Type.atom("__field__"), Type.atom("nil")],
						[Type.atom("__owner__"), Type.atom("nil")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Ecto.Association.NotLoaded"),
						],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Ecto.Association.NotLoaded",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[Type.atom("__cardinality__"), Type.atom("nil")],
							[Type.atom("__field__"), Type.atom("nil")],
							[Type.atom("__owner__"), Type.atom("nil")],
							[
								Type.atom("__struct__"),
								Type.atom("Elixir.Ecto.Association.NotLoaded"),
							],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Ecto.Schema.Metadata",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Ecto.Schema.Metadata")],
						[Type.atom("context"), Type.atom("nil")],
						[Type.atom("prefix"), Type.atom("nil")],
						[Type.atom("schema"), Type.atom("nil")],
						[Type.atom("source"), Type.atom("nil")],
						[Type.atom("state"), Type.atom("nil")],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Ecto.Schema.Metadata",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[
								Type.atom("__struct__"),
								Type.atom("Elixir.Ecto.Schema.Metadata"),
							],
							[Type.atom("context"), Type.atom("nil")],
							[Type.atom("prefix"), Type.atom("nil")],
							[Type.atom("schema"), Type.atom("nil")],
							[Type.atom("source"), Type.atom("nil")],
							[Type.atom("state"), Type.atom("nil")],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction("Enum", "reject", 2, "public", [
		{
			params: (context) => [
				Type.variablePattern("enumerable_0"),
				Type.variablePattern("fun_1"),
			],
			guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
			body: (context) => {
				return Elixir_Enum["reject_list/2"](
					context.vars.enumerable_0,
					context.vars.fun_1,
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("enumerable_0"),
				Type.variablePattern("fun_1"),
			],
			guards: [],
			body: (context) => {
				return Erlang_Lists["reverse/1"](
					Elixir_Enum["reduce/3"](
						context.vars.enumerable_0,
						Type.list([]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.variablePattern("entry_2"),
										Type.variablePattern("acc_3"),
									],
									guards: [],
									body: (context) => {
										return Interpreter.case(
											Interpreter.callAnonymousFunction(context.vars.fun_1, [
												context.vars.entry_2,
											]),
											[
												{
													match: Type.variablePattern("x_4"),
													guards: [
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_4,
																		Type.atom("false"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_4,
																		Type.atom("nil"),
																	),
																context,
															),
													],
													body: (context) => {
														return Interpreter.consOperator(
															context.vars.entry_2,
															context.vars.acc_3,
														);
													},
												},
												{
													match: Type.matchPlaceholder(),
													guards: [],
													body: (context) => {
														return context.vars.acc_3;
													},
												},
											],
											context,
										);
									},
								},
							],
							context,
						),
					),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("Enum", "reject_list", 2, "private", [
		{
			params: (context) => [
				Type.consPattern(
					Type.variablePattern("head_0"),
					Type.variablePattern("tail_1"),
				),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Interpreter.callAnonymousFunction(context.vars.fun_2, [
						context.vars.head_0,
					]),
					[
						{
							match: Type.variablePattern("x_3"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Interpreter.consOperator(
									context.vars.head_0,
									Elixir_Enum["reject_list/2"](
										context.vars.tail_1,
										context.vars.fun_2,
									),
								);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Elixir_Enum["reject_list/2"](
									context.vars.tail_1,
									context.vars.fun_2,
								);
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.list([]), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.list([]);
			},
		},
	]);

	Interpreter.defineElixirFunction(
		"Hologram.Router.Helpers",
		"page_bundle_path",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("page_digest_0")],
				guards: [],
				body: (context) => {
					return Type.bitstring([
						Type.bitstringSegment(Type.string("/hologram/page-"), {
							type: "binary",
						}),
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.page_digest_0),
							{ type: "binary" },
						),
						Type.bitstringSegment(Type.string(".js"), { type: "binary" }),
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.Server",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[
							Type.atom("__meta__"),
							Type.map([
								[
									Type.atom("__struct__"),
									Type.atom("Elixir.Hologram.Server.Metadata"),
								],
								[Type.atom("cookie_ops"), Type.map([])],
								[Type.atom("session_ops"), Type.map([])],
							]),
						],
						[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Server")],
						[Type.atom("cookies"), Type.map([])],
						[Type.atom("next_action"), Type.atom("nil")],
						[Type.atom("session"), Type.map([])],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.Server",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[
								Type.atom("__meta__"),
								Type.map([
									[
										Type.atom("__struct__"),
										Type.atom("Elixir.Hologram.Server.Metadata"),
									],
									[Type.atom("cookie_ops"), Type.map([])],
									[Type.atom("session_ops"), Type.map([])],
								]),
							],
							[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Server")],
							[Type.atom("cookies"), Type.map([])],
							[Type.atom("next_action"), Type.atom("nil")],
							[Type.atom("session"), Type.map([])],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.Server.Metadata",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Hologram.Server.Metadata"),
						],
						[Type.atom("cookie_ops"), Type.map([])],
						[Type.atom("session_ops"), Type.map([])],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.Server.Metadata",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[
								Type.atom("__struct__"),
								Type.atom("Elixir.Hologram.Server.Metadata"),
							],
							[Type.atom("cookie_ops"), Type.map([])],
							[Type.atom("session_ops"), Type.map([])],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.UI.Runtime",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("page_mounted?"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.Hologram.Runtime"),
											Type.atom("page_mounted?"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("page_digest"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.Hologram.Runtime"),
											Type.atom("page_digest"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("initial_page?"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.Hologram.Runtime"),
											Type.atom("initial_page?"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("csrf_token"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.Hologram.Runtime"),
											Type.atom("csrf_token"),
										]),
									]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Hologram.UI.Runtime",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Interpreter.case(
											Interpreter.case(
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("initial_page?"),
												),
												[
													{
														match: Type.variablePattern("x_1"),
														guards: [
															(context) =>
																Erlang["orelse/2"](
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_1,
																			Type.atom("false"),
																		),
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_1,
																			Type.atom("nil"),
																		),
																	context,
																),
														],
														body: (context) => {
															return context.vars.x_1;
														},
													},
													{
														match: Type.matchPlaceholder(),
														guards: [],
														body: (context) => {
															return Interpreter.case(
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("page_mounted?"),
																),
																[
																	{
																		match: Type.variablePattern("x_2"),
																		guards: [
																			(context) =>
																				Erlang["orelse/2"](
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_2,
																							Type.atom("false"),
																						),
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_2,
																							Type.atom("nil"),
																						),
																					context,
																				),
																		],
																		body: (context) => {
																			return Type.atom("true");
																		},
																	},
																	{
																		match: Type.matchPlaceholder(),
																		guards: [],
																		body: (context) => {
																			return Type.atom("false");
																		},
																	},
																],
																context,
															);
														},
													},
												],
												context,
											),
											[
												{
													match: Type.variablePattern("x_3"),
													guards: [
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_3,
																		Type.atom("false"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_3,
																		Type.atom("nil"),
																	),
																context,
															),
													],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.matchPlaceholder(),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("script"),
																Type.list([]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring(
																			'\n    globalThis.hologram ??= {};\n    globalThis.hologram.csrfToken = "',
																		),
																	]),
																	Type.tuple([
																		Type.atom("expression"),
																		Type.tuple([
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("csrf_token"),
																			),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring(
																			'";\n    globalThis.hologram.assetManifest = $ASSET_MANIFEST_JS_PLACEHOLDER\n  ',
																		),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
										Type.tuple([Type.atom("text"), Type.bitstring("\n\n")]),
										Interpreter.case(
											Interpreter.case(
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("page_mounted?"),
												),
												[
													{
														match: Type.variablePattern("x_4"),
														guards: [
															(context) =>
																Erlang["orelse/2"](
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_4,
																			Type.atom("false"),
																		),
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_4,
																			Type.atom("nil"),
																		),
																	context,
																),
														],
														body: (context) => {
															return Type.atom("true");
														},
													},
													{
														match: Type.matchPlaceholder(),
														guards: [],
														body: (context) => {
															return Type.atom("false");
														},
													},
												],
												context,
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("script"),
																Type.list([]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring(
																			"\n    \n      globalThis.hologram.pageMountData = (deps) => {\n        const Type = deps.Type;\n        \n        return {\n          componentRegistry: $COMPONENT_REGISTRY_JS_PLACEHOLDER,\n          pageModule: $PAGE_MODULE_JS_PLACEHOLDER,\n          pageParams: $PAGE_PARAMS_JS_PLACEHOLDER\n        };\n      };\n    ",
																		),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n  "),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
										Type.tuple([Type.atom("text"), Type.bitstring("\n\n")]),
										Interpreter.case(
											Interpreter.case(
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("initial_page?"),
												),
												[
													{
														match: Type.variablePattern("x_5"),
														guards: [
															(context) =>
																Erlang["orelse/2"](
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_5,
																			Type.atom("false"),
																		),
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_5,
																			Type.atom("nil"),
																		),
																	context,
																),
														],
														body: (context) => {
															return context.vars.x_5;
														},
													},
													{
														match: Type.matchPlaceholder(),
														guards: [],
														body: (context) => {
															return Interpreter.case(
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("page_mounted?"),
																),
																[
																	{
																		match: Type.variablePattern("x_6"),
																		guards: [
																			(context) =>
																				Erlang["orelse/2"](
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_6,
																							Type.atom("false"),
																						),
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_6,
																							Type.atom("nil"),
																						),
																					context,
																				),
																		],
																		body: (context) => {
																			return Type.atom("true");
																		},
																	},
																	{
																		match: Type.matchPlaceholder(),
																		guards: [],
																		body: (context) => {
																			return Type.atom("false");
																		},
																	},
																],
																context,
															);
														},
													},
												],
												context,
											),
											[
												{
													match: Type.variablePattern("x_7"),
													guards: [
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_7,
																		Type.atom("false"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_7,
																		Type.atom("nil"),
																	),
																context,
															),
													],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.matchPlaceholder(),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("script"),
																Type.list([
																	Type.tuple([
																		Type.bitstring("async"),
																		Type.list([]),
																	]),
																	Type.tuple([
																		Type.bitstring("src"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Elixir_Hologram_Router_Helpers[
																						"asset_path/1"
																					](
																						Type.bitstring(
																							"hologram/runtime.js",
																						),
																					),
																				]),
																			]),
																		]),
																	]),
																]),
																Type.list([]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
										Type.tuple([Type.atom("text"), Type.bitstring("\n\n")]),
										Interpreter.case(
											Interpreter.case(
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("page_mounted?"),
												),
												[
													{
														match: Type.variablePattern("x_8"),
														guards: [
															(context) =>
																Erlang["orelse/2"](
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_8,
																			Type.atom("false"),
																		),
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.x_8,
																			Type.atom("nil"),
																		),
																	context,
																),
														],
														body: (context) => {
															return Type.atom("true");
														},
													},
													{
														match: Type.matchPlaceholder(),
														guards: [],
														body: (context) => {
															return Type.atom("false");
														},
													},
												],
												context,
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("script"),
																Type.list([
																	Type.tuple([
																		Type.bitstring("async"),
																		Type.list([]),
																	]),
																	Type.tuple([
																		Type.bitstring("src"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Elixir_Hologram_Router_Helpers[
																						"page_bundle_path/1"
																					](
																						Interpreter.dotOperator(
																							context.vars.vars_0,
																							Type.atom("page_digest"),
																						),
																					),
																				]),
																			]),
																		]),
																	]),
																]),
																Type.list([]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Jason.EncodeError",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[Type.atom("__struct__"), Type.atom("Elixir.Jason.EncodeError")],
						[Type.atom("message"), Type.atom("nil")],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Jason.EncodeError",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[Type.atom("__exception__"), Type.atom("true")],
							[Type.atom("__struct__"), Type.atom("Elixir.Jason.EncodeError")],
							[Type.atom("message"), Type.atom("nil")],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Jason.Fragment",
		"__struct__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Jason.Fragment")],
						[Type.atom("encode"), Type.atom("nil")],
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Jason.Fragment",
		"__struct__",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("kv_0")],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reduce/3"](
						context.vars.kv_0,
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Jason.Fragment")],
							[Type.atom("encode"), Type.atom("nil")],
						]),
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.tuple([
											Type.variablePattern("key_1"),
											Type.variablePattern("val_2"),
										]),
										Type.variablePattern("map_3"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										);
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Leaply.Social.ProfileDemographics",
		"ethnicity_label_key",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("value_0"),
					Type.variablePattern("locale_1"),
				],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_Leaply_Social_ProfileDemographics["normalize_known_value/2"](
							context.vars.value_0,
							Type.list([
								Type.bitstring("asian"),
								Type.bitstring("indian"),
								Type.bitstring("black_or_african_descent"),
								Type.bitstring("hispanic_or_latino"),
								Type.bitstring("middle_eastern_or_north_african"),
								Type.bitstring("mixed_or_multiracial"),
								Type.bitstring("native_or_indigenous"),
								Type.bitstring("pacific_islander"),
								Type.bitstring("white"),
								Type.bitstring("other"),
								Type.bitstring("black_african"),
								Type.bitstring("south_african_coloured"),
							]),
						),
						[
							{
								match: Type.atom("nil"),
								guards: [],
								body: (context) => {
									return Type.atom("nil");
								},
							},
							{
								match: Type.bitstring("south_african_coloured"),
								guards: [],
								body: (context) => {
									return Interpreter.case(
										Elixir_Leaply_Social_ProfileDemographics[
											"south_africa_locale?/1"
										](context.vars.locale_1),
										[
											{
												match: Type.variablePattern("x_2"),
												guards: [
													(context) =>
														Erlang["orelse/2"](
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_2,
																	Type.atom("false"),
																),
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_2,
																	Type.atom("nil"),
																),
															context,
														),
												],
												body: (context) => {
													return Type.bitstring(
														"social_profile.ethnicity.south_african_coloured",
													);
												},
											},
											{
												match: Type.matchPlaceholder(),
												guards: [],
												body: (context) => {
													return Type.bitstring(
														"social_profile.ethnicity.south_african_coloured.za",
													);
												},
											},
										],
										context,
									);
								},
							},
							{
								match: Type.variablePattern("normalized_3"),
								guards: [],
								body: (context) => {
									return Type.bitstring([
										Type.bitstringSegment(
											Type.string("social_profile.ethnicity."),
											{ type: "binary" },
										),
										Type.bitstringSegment(
											Elixir_String_Chars["to_string/1"](
												context.vars.normalized_3,
											),
											{ type: "binary" },
										),
									]);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Leaply.Social.ProfileDemographics",
		"gender_identity_label_key",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_Leaply_Social_ProfileDemographics["normalize_known_value/2"](
							context.vars.value_0,
							Type.list([
								Type.bitstring("male"),
								Type.bitstring("female"),
								Type.bitstring("trans_man"),
								Type.bitstring("trans_woman"),
								Type.bitstring("non_binary"),
								Type.bitstring("prefer_not_to_say"),
								Type.bitstring("other"),
							]),
						),
						[
							{
								match: Type.atom("nil"),
								guards: [],
								body: (context) => {
									return Type.atom("nil");
								},
							},
							{
								match: Type.variablePattern("normalized_1"),
								guards: [],
								body: (context) => {
									return Type.bitstring([
										Type.bitstringSegment(
											Type.string("social_profile.gender_identity."),
											{ type: "binary" },
										),
										Type.bitstringSegment(
											Elixir_String_Chars["to_string/1"](
												context.vars.normalized_1,
											),
											{ type: "binary" },
										),
									]);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Leaply.Social.ProfileDemographics",
		"normalize_known_value",
		2,
		"private",
		[
			{
				params: (context) => [
					Type.variablePattern("value_0"),
					Type.variablePattern("known_values_1"),
				],
				guards: [(context) => Erlang["is_atom/1"](context.vars.value_0)],
				body: (context) => {
					return Elixir_Leaply_Social_ProfileDemographics[
						"normalize_known_value/2"
					](
						Erlang["atom_to_binary/1"](context.vars.value_0),
						context.vars.known_values_1,
					);
				},
			},
			{
				params: (context) => [
					Type.variablePattern("value_0"),
					Type.variablePattern("known_values_1"),
				],
				guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)],
				body: (context) => {
					Interpreter.matchOperator(
						Elixir_String["downcase/1"](
							Elixir_String["trim/1"](context.vars.value_0),
						),
						Type.variablePattern("normalized_2"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Interpreter.case(
						Elixir_Enum["member?/2"](
							context.vars.known_values_1,
							context.vars.normalized_2,
						),
						[
							{
								match: Type.variablePattern("x_3"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.atom("nil");
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return context.vars.normalized_2;
								},
							},
						],
						context,
					);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Leaply.Social.ProfileDemographics",
		"normalize_locale_parts",
		1,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("locale_0")],
				guards: [(context) => Erlang["is_binary/1"](context.vars.locale_0)],
				body: (context) => {
					return Interpreter.case(
						Elixir_String["split/3"](
							Elixir_String["replace/3"](
								Elixir_String["trim/1"](context.vars.locale_0),
								Type.bitstring("-"),
								Type.bitstring("_"),
							),
							Type.map([
								[Type.atom("__struct__"), Type.atom("Elixir.Regex")],
								[Type.atom("opts"), Type.list([])],
								[
									Type.atom("re_pattern"),
									Erlang_Re["import/1"](
										Type.tuple([
											Type.atom("re_exported_pattern"),
											Type.bitstring("re-PCRE2\v\xAA\xA4\xC1\u{1}\u{0}"),
											Type.bitstring("[_@.]"),
											Type.list([Type.atom("export")]),
											Type.bitstring(
												"S2RP\n\u{0}/\u{0}\u{1}\b\b\u{0}\u{1}\u{0}\u{0}\u{0}\u{0}\u{1}\u{2}\u{3}\u{4}\u{5}\u{6}\x07\b\t\n\v\f\r\u{E}\u{F}\u{10}\u{11}\u{12}\u{13}\u{14}\u{15}\u{16}\u{17}\u{18}\u{19}\u{1A}\u{1C}\u{1D}\u{1E}\u{1F} !\"#$%&'()*+,-./0123456789:;<=>?@abcdefghijklmnopqrstuvwxyz[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~\x80\x81\x82\x83\x84\x85\x86\x87\x88\x89\x8A\x8B\x8C\x8D\x8E\x8F\x90\x91\x92\x93\x94\x95\x96\x97\x98\x99\x9A\x9B\x9C\x9D\x9E\x9F\xA0\xA1\xA2\xA3\xA4\xA5\xA6\xA7\xA8\xA9\xAA\xAB\xAC\xAD\xAE\xAF\xB0\xB1\xB2\xB3\xB4\xB5\xB6\xB7\xB8\xB9\xBA\xBB\xBC\xBD\xBE\xBF\xC0\xC1\xC2\xC3\xC4\xC5\xC6\xC7\xC8\xC9\xCA\xCB\xCC\xCD\xCE\xCF\xD0\xD1\xD2\xD3\xD4\xD5\xD6\xD7\xD8\xD9\xDA\xDB\xDC\xDD\xDE\xDF\xE0\xE1\xE2\xE3\xE4\xE5\xE6\xE7\xE8\xE9\xEA\xEB\xEC\xED\xEE\xEF\xF0\xF1\xF2\xF3\xF4\xF5\xF6\xF7\xF8\xF9\xFA\xFB\xFC\xFD\xFE\xFF\u{0}\u{1}\u{2}\u{3}\u{4}\u{5}\u{6}\x07\b\t\n\v\f\r\u{E}\u{F}\u{10}\u{11}\u{12}\u{13}\u{14}\u{15}\u{16}\u{17}\u{18}\u{19}\u{1A}\u{1C}\u{1D}\u{1E}\u{1F} !\"#$%&'()*+,-./0123456789:;<=>?@abcdefghijklmnopqrstuvwxyz[\\]^_`ABCDEFGHIJKLMNOPQRSTUVWXYZ{|}~\x80\x81\x82\x83\x84\x85\x86\x87\x88\x89\x8A\x8B\x8C\x8D\x8E\x8F\x90\x91\x92\x93\x94\x95\x96\x97\x98\x99\x9A\x9B\x9C\x9D\x9E\x9F\xA0\xA1\xA2\xA3\xA4\xA5\xA6\xA7\xA8\xA9\xAA\xAB\xAC\xAD\xAE\xAF\xB0\xB1\xB2\xB3\xB4\xB5\xB6\xB7\xB8\xB9\xBA\xBB\xBC\xBD\xBE\xBF\xC0\xC1\xC2\xC3\xC4\xC5\xC6\xC7\xC8\xC9\xCA\xCB\xCC\xCD\xCE\xCF\xD0\xD1\xD2\xD3\xD4\xD5\xD6\xD7\xD8\xD9\xDA\xDB\xDC\xDD\xDE\xDF\xE0\xE1\xE2\xE3\xE4\xE5\xE6\xE7\xE8\xE9\xEA\xEB\xEC\xED\xEE\xEF\xF0\xF1\xF2\xF3\xF4\xF5\xF6\xF7\xF8\xF9\xFA\xFB\xFC\xFD\xFE\xFF\u{0}>\u{0}\u{0}\u{1}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFF\u{3}~\u{0}\u{0}\u{0}~\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFF\u{3}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFE\xFF\xFF\x07\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFE\xFF\xFF\x07\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFF\u{3}\xFE\xFF\xFF\x87\xFE\xFF\xFF\x07\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFE\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFE\xFF\u{0}\xFC\u{1}\u{0}\u{0}\xF8\u{1}\u{0}\u{0}x\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xFF\xFF\xFF\xFF\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\x80\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{1}\u{1}\u{1}\u{1}\u{1}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{1}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{18}\u{18}\u{18}\u{18}\u{18}\u{18}\u{18}\u{18}\u{18}\u{18}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{12}\u{0}\u{0}\u{0}\u{0}\u{10}\u{0}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{16}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}@\u{0}\u{0}\u{1}\u{0}\u{0}\x80\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\xC0\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\x98\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}ERCP\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}A\u{0}\u{0}\u{0}\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{1}\u{0}\u{2}\u{0}\u{0}\u{0}\u{1}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\x07\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\x89\u{0}$n\u{0}\u{0}\u{0}\u{0}\u{0}@\u{0}\u{0}\u{1}\u{0}\u{0}\x80\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}\u{0}z\u{0}$\u{0}",
											),
										]),
									),
								],
								[Type.atom("source"), Type.bitstring("[_@.]")],
							]),
							Type.list([Type.tuple([Type.atom("parts"), Type.integer(3n)])]),
						),
						[
							{
								match: Type.consPattern(
									Type.variablePattern("language_1"),
									Type.consPattern(
										Type.variablePattern("region_2"),
										Type.matchPlaceholder(),
									),
								),
								guards: [],
								body: (context) => {
									return Type.list([
										Elixir_String["downcase/1"](context.vars.language_1),
										Elixir_String["upcase/1"](context.vars.region_2),
									]);
								},
							},
							{
								match: Type.variablePattern("parts_4"),
								guards: [],
								body: (context) => {
									return context.vars.parts_4;
								},
							},
						],
						context,
					);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.list([]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"Leaply.Social.ProfileDemographics",
		"south_africa_locale?",
		1,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("locale_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_Leaply_Social_ProfileDemographics[
							"normalize_locale_parts/1"
						](context.vars.locale_0),
						[
							{
								match: Type.list([
									Type.matchPlaceholder(),
									Type.bitstring("ZA"),
								]),
								guards: [],
								body: (context) => {
									return Type.atom("true");
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.atom("false");
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationBar",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("navigation"),
								Type.atom("map"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.LeaplyWeb.ApplicationNavigation"),
											Type.atom("navigation"),
										]),
									]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationBar",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Interpreter.case(
											Interpreter.dotOperator(
												context.vars.vars_0,
												Type.atom("navigation"),
											),
											[
												{
													match: Type.variablePattern("x_1"),
													guards: [
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_1,
																		Type.atom("false"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_1,
																		Type.atom("nil"),
																	),
																context,
															),
													],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.matchPlaceholder(),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("component"),
																Type.atom(
																	"Elixir.LeaplyWeb.Hologram.Components.ApplicationHeader",
																),
																Type.list([
																	Type.tuple([
																		Type.bitstring("cid"),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("application-header"),
																			]),
																		]),
																	]),
																]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("div"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring(
																							"ml-auto flex items-center gap-1 sm:gap-2",
																						),
																					]),
																				]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("component"),
																				Type.atom(
																					"Elixir.LeaplyWeb.Hologram.Components.ThemeToggle",
																				),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("cid"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"application-theme-toggle",
																								),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("component"),
																				Type.atom(
																					"Elixir.LeaplyWeb.Hologram.Components.ApplicationMenu",
																				),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("cid"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"application-menu",
																								),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n  "),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationHeader",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("preview"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("false")]),
								]),
							]),
							Type.tuple([
								Type.atom("home_path"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.bitstring("/")]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationHeader",
		"home_label",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_CatalogText["get/2"](
						Type.bitstring("shared"),
						Type.bitstring("shared.navigation.home"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationHeader",
		"logo_size",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring(
						"@min-[40rem]/story:h-10 @min-[40rem]/story:w-auto",
					);
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring("sm:h-10 sm:w-auto");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationHeader",
		"spacing",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring(
						"@min-[40rem]/story:gap-3 @min-[40rem]/story:px-6 @min-[64rem]/story:px-8",
					);
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring("sm:gap-3 sm:px-6 lg:px-8");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationHeader",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("header"),
											Type.list([
												Type.tuple([
													Type.bitstring("data-application-bar"),
													Type.list([]),
												]),
												Type.tuple([
													Type.bitstring("class"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring(
																"sticky top-0 z-40 border-b border-base-300 bg-base-100 text-base-content",
															),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("div"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("expression"),
																	Type.tuple([
																		Type.bitstring([
																			Type.bitstringSegment(
																				Type.string(
																					"flex h-[var(--application-header-height)] items-center gap-1 px-3 ",
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Elixir_LeaplyWeb_Hologram_Components_ApplicationHeader[
																					"spacing/1"
																				](
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("preview"),
																					),
																				),
																				{ type: "binary" },
																			),
																		]),
																	]),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("a"),
															Type.list([
																Type.tuple([
																	Type.bitstring("href"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("home_path"),
																				),
																			]),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("aria-label"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([
																				Elixir_LeaplyWeb_Hologram_Components_ApplicationHeader[
																					"home_label/0"
																				](),
																			]),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("class"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"group inline-flex min-h-11 shrink-0 items-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("img"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("src"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("/images/leaply.svg"),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("width"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("120"),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("height"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("40"),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("alt"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("Leaply"),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("class"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Type.string(
																									"h-auto w-16 transition duration-200 group-hover:-translate-y-0.5 motion-reduce:transform-none ",
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Elixir_LeaplyWeb_Hologram_Components_ApplicationHeader[
																									"logo_size/1"
																								](
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("preview"),
																									),
																								),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([]),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("slot"),
															Type.list([]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("preview"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("false")]),
								]),
							]),
							Type.tuple([
								Type.atom("id_prefix"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("applicant-profile-menu"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("account_url"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("/me/profile/settings"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("professional_url"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("/me/profile"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("request_navigation"),
								Type.atom("map"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.LeaplyWeb.ApplicationNavigation"),
											Type.atom("navigation"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("navigation"),
								Type.atom("map"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"aria_current",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring("page");
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"icon",
		1,
		"public",
		[
			{
				params: (context) => [Type.bitstring("opportunities")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-magnifying-glass");
				},
			},
			{
				params: (context) => [Type.bitstring("applications")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-document-text");
				},
			},
			{
				params: (context) => [Type.bitstring("professional")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-user-circle");
				},
			},
			{
				params: (context) => [Type.bitstring("settings")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-cog-6-tooth");
				},
			},
			{
				params: (context) => [Type.bitstring("managed-listings")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-building-office-2");
				},
			},
			{
				params: (context) => [Type.bitstring("system-questions")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-question-mark-circle");
				},
			},
			{
				params: (context) => [Type.bitstring("identity-reviews")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-identification");
				},
			},
			{
				params: (context) => [Type.bitstring("handbooks")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-book-open");
				},
			},
			{
				params: (context) => [Type.bitstring("institution-reviews")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-building-office");
				},
			},
			{
				params: (context) => [Type.bitstring("academic-catalogue")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-academic-cap");
				},
			},
			{
				params: (context) => [Type.bitstring("data-browser")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-circle-stack");
				},
			},
			{
				params: (context) => [Type.bitstring("design-system")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-swatch");
				},
			},
			{
				params: (context) => [Type.bitstring("documentation")],
				guards: [],
				body: (context) => {
					return Type.bitstring("hero-book-open");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"initial",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("name_0")],
				guards: [],
				body: (context) => {
					return Elixir_String["upcase/1"](
						Interpreter.case(
							Elixir_String["first/1"](
								Elixir_String["trim/1"](context.vars.name_0),
							),
							[
								{
									match: Type.variablePattern("x_1"),
									guards: [
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
												(context) =>
													Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
												context,
											),
									],
									body: (context) => {
										return Type.bitstring("P");
									},
								},
								{
									match: Type.variablePattern("x_2"),
									guards: [],
									body: (context) => {
										return context.vars.x_2;
									},
								},
							],
							context,
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"link_class",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring("menu-active min-h-11 content-center");
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring("min-h-11 content-center");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"link_path",
		4,
		"public",
		[
			{
				params: (context) => [
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
					Type.atom("true"),
				],
				guards: [],
				body: (context) => {
					return Type.bitstring("#compositions");
				},
			},
			{
				params: (context) => [
					Type.map([[Type.atom("id"), Type.bitstring("professional")]]),
					Type.variablePattern("professional_0"),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
				],
				guards: [],
				body: (context) => {
					return context.vars.professional_0;
				},
			},
			{
				params: (context) => [
					Type.map([[Type.atom("id"), Type.bitstring("settings")]]),
					Type.matchPlaceholder(),
					Type.variablePattern("settings_1"),
					Type.matchPlaceholder(),
				],
				guards: [],
				body: (context) => {
					return context.vars.settings_1;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("entry_0"),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
				],
				guards: [],
				body: (context) => {
					return Interpreter.dotOperator(
						context.vars.entry_0,
						Type.atom("path"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"name_visibility",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring("@min-[40rem]/story:block");
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring("sm:block");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"projection",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("navigation_0"),
					Type.variablePattern("request_navigation_1"),
				],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						context.vars.navigation_0,
						[
							{
								match: Type.variablePattern("x_2"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_2, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_2, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return context.vars.request_navigation_1;
								},
							},
							{
								match: Type.variablePattern("x_3"),
								guards: [],
								body: (context) => {
									return context.vars.x_3;
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Interpreter.case(
											Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
												"projection/2"
											](
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("navigation"),
												),
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("request_navigation"),
												),
											),
											[
												{
													match: Type.variablePattern("x_1"),
													guards: [
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_1,
																		Type.atom("false"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.x_1,
																		Type.atom("nil"),
																	),
																context,
															),
													],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.matchPlaceholder(),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("details"),
																Type.list([
																	Type.tuple([
																		Type.bitstring("class"),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring(
																					"dropdown dropdown-end relative shrink-0 text-base-content",
																				),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.bitstring("id"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("id_prefix"),
																					),
																				]),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.bitstring("data-profile-menu"),
																		Type.list([]),
																	]),
																]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("summary"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring(
																							"btn btn-ghost min-h-11 max-w-60 gap-2 px-2",
																						),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring("aria-label"),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																								"text/1"
																							](Type.bitstring("menu")),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring("aria-controls"),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Type.bitstring([
																								Type.bitstringSegment(
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("id_prefix"),
																									),
																									{ type: "binary" },
																								),
																								Type.bitstringSegment(
																									Type.string("-panel"),
																									{ type: "binary" },
																								),
																							]),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring("aria-haspopup"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("true"),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring("id"),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Type.bitstring([
																								Type.bitstringSegment(
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("id_prefix"),
																									),
																									{ type: "binary" },
																								),
																								Type.bitstringSegment(
																									Type.string("-trigger"),
																									{ type: "binary" },
																								),
																							]),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring(
																					"data-profile-menu-trigger",
																				),
																				Type.list([]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("span"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("class"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-base-300 font-semibold",
																								),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("aria-hidden"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("true"),
																							]),
																						]),
																					]),
																				]),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																								"initial/1"
																							](
																								Interpreter.dotOperator(
																									Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																										"projection/2"
																									](
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("navigation"),
																										),
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom(
																												"request_navigation",
																											),
																										),
																									),
																									Type.atom("display_name"),
																								),
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("span"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("class"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Type.bitstring([
																										Type.bitstringSegment(
																											Type.string(
																												"hidden max-w-36 truncate ",
																											),
																											{ type: "binary" },
																										),
																										Type.bitstringSegment(
																											Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																												"name_visibility/1"
																											](
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("preview"),
																												),
																											),
																											{ type: "binary" },
																										),
																									]),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Interpreter.dotOperator(
																								Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																									"projection/2"
																								](
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("navigation"),
																									),
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"request_navigation",
																										),
																									),
																								),
																								Type.atom("display_name"),
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("span"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("class"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"hero-chevron-down h-4 w-4 shrink-0",
																								),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("aria-hidden"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("true"),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("div"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring(
																							"dropdown-content absolute right-0 z-50 mt-2 max-h-[min(36rem,calc(var(--admin-workspace-height,100dvh)-var(--application-header-height)-2rem),var(--application-menu-height,calc(100dvh-14rem)))] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-lg border border-base-300 bg-base-100 p-2 shadow-lg",
																						),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring("id"),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Type.bitstring([
																								Type.bitstringSegment(
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("id_prefix"),
																									),
																									{ type: "binary" },
																								),
																								Type.bitstringSegment(
																									Type.string("-panel"),
																									{ type: "binary" },
																								),
																							]),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.bitstring(
																					"data-profile-menu-panel",
																				),
																				Type.list([]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("p"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("class"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"break-words px-3 py-2 text-sm font-semibold",
																								),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring(
																							"data-navigation-identity",
																						),
																						Type.list([]),
																					]),
																				]),
																				Type.list([
																					Type.tuple([
																						Type.atom("expression"),
																						Type.tuple([
																							Interpreter.dotOperator(
																								Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																									"projection/2"
																								](
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("navigation"),
																									),
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"request_navigation",
																										),
																									),
																								),
																								Type.atom("display_name"),
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("nav"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("aria-label"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																										"text/1"
																									](Type.bitstring("menu")),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n        "),
																					]),
																					Interpreter.comprehension(
																						[
																							{
																								match:
																									Type.variablePattern(
																										"group_2",
																									),
																								guards: [],
																								body: (context) =>
																									Interpreter.dotOperator(
																										Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																											"projection/2"
																										](
																											Interpreter.dotOperator(
																												context.vars.vars_0,
																												Type.atom("navigation"),
																											),
																											Interpreter.dotOperator(
																												context.vars.vars_0,
																												Type.atom(
																													"request_navigation",
																												),
																											),
																										),
																										Type.atom("groups"),
																									),
																							},
																						],
																						[],
																						Type.list([]),
																						false,
																						(context) => {
																							return Type.list([
																								Type.tuple([
																									Type.atom("text"),
																									Type.bitstring(
																										"\n          ",
																									),
																								]),
																								Type.tuple([
																									Type.atom("element"),
																									Type.bitstring("ul"),
																									Type.list([
																										Type.tuple([
																											Type.bitstring("class"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"menu w-full p-0 text-sm",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.bitstring(
																												"aria-label",
																											),
																											Type.list([
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Interpreter.dotOperator(
																															context.vars
																																.group_2,
																															Type.atom(
																																"label",
																															),
																														),
																													]),
																												]),
																											]),
																										]),
																									]),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n            ",
																											),
																										]),
																										Type.tuple([
																											Type.atom("element"),
																											Type.bitstring("li"),
																											Type.list([
																												Type.tuple([
																													Type.bitstring(
																														"class",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"menu-title text-base-content/75",
																															),
																														]),
																													]),
																												]),
																											]),
																											Type.list([
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Interpreter.dotOperator(
																															context.vars
																																.group_2,
																															Type.atom(
																																"label",
																															),
																														),
																													]),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n            ",
																											),
																										]),
																										Interpreter.comprehension(
																											[
																												{
																													match:
																														Type.variablePattern(
																															"entry_3",
																														),
																													guards: [],
																													body: (context) =>
																														Interpreter.dotOperator(
																															context.vars
																																.group_2,
																															Type.atom(
																																"entries",
																															),
																														),
																												},
																											],
																											[],
																											Type.list([]),
																											false,
																											(context) => {
																												return Type.list([
																													Type.tuple([
																														Type.atom("text"),
																														Type.bitstring(
																															"\n              ",
																														),
																													]),
																													Type.tuple([
																														Type.atom(
																															"element",
																														),
																														Type.bitstring(
																															"li",
																														),
																														Type.list([]),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"element",
																																),
																																Type.bitstring(
																																	"a",
																																),
																																Type.list([
																																	Type.tuple([
																																		Type.bitstring(
																																			"href",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.atom(
																																						"expression",
																																					),
																																					Type.tuple(
																																						[
																																							Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																																								"link_path/4"
																																							](
																																								context
																																									.vars
																																									.entry_3,
																																								Interpreter.dotOperator(
																																									context
																																										.vars
																																										.vars_0,
																																									Type.atom(
																																										"professional_url",
																																									),
																																								),
																																								Interpreter.dotOperator(
																																									context
																																										.vars
																																										.vars_0,
																																									Type.atom(
																																										"account_url",
																																									),
																																								),
																																								Interpreter.dotOperator(
																																									context
																																										.vars
																																										.vars_0,
																																									Type.atom(
																																										"preview",
																																									),
																																								),
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.tuple([
																																		Type.bitstring(
																																			"class",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.atom(
																																						"expression",
																																					),
																																					Type.tuple(
																																						[
																																							Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																																								"link_class/1"
																																							](
																																								Interpreter.dotOperator(
																																									context
																																										.vars
																																										.entry_3,
																																									Type.atom(
																																										"current",
																																									),
																																								),
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.tuple([
																																		Type.bitstring(
																																			"aria-current",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.atom(
																																						"expression",
																																					),
																																					Type.tuple(
																																						[
																																							Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																																								"aria_current/1"
																																							](
																																								Interpreter.dotOperator(
																																									context
																																										.vars
																																										.entry_3,
																																									Type.atom(
																																										"current",
																																									),
																																								),
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.tuple([
																																		Type.bitstring(
																																			"id",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.atom(
																																						"expression",
																																					),
																																					Type.tuple(
																																						[
																																							Type.bitstring(
																																								[
																																									Type.bitstringSegment(
																																										Interpreter.dotOperator(
																																											context
																																												.vars
																																												.vars_0,
																																											Type.atom(
																																												"id_prefix",
																																											),
																																										),
																																										{
																																											type: "binary",
																																										},
																																									),
																																									Type.bitstringSegment(
																																										Type.string(
																																											"-",
																																										),
																																										{
																																											type: "binary",
																																										},
																																									),
																																									Type.bitstringSegment(
																																										Interpreter.dotOperator(
																																											context
																																												.vars
																																												.entry_3,
																																											Type.atom(
																																												"id",
																																											),
																																										),
																																										{
																																											type: "binary",
																																										},
																																									),
																																									Type.bitstringSegment(
																																										Type.string(
																																											"-link",
																																										),
																																										{
																																											type: "binary",
																																										},
																																									),
																																								],
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																																Type.list([
																																	Type.tuple([
																																		Type.atom(
																																			"element",
																																		),
																																		Type.bitstring(
																																			"span",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.bitstring(
																																						"class",
																																					),
																																					Type.list(
																																						[
																																							Type.tuple(
																																								[
																																									Type.atom(
																																										"expression",
																																									),
																																									Type.tuple(
																																										[
																																											Type.bitstring(
																																												[
																																													Type.bitstringSegment(
																																														Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																																															"icon/1"
																																														](
																																															Interpreter.dotOperator(
																																																context
																																																	.vars
																																																	.entry_3,
																																																Type.atom(
																																																	"id",
																																																),
																																															),
																																														),
																																														{
																																															type: "binary",
																																														},
																																													),
																																													Type.bitstringSegment(
																																														Type.string(
																																															" h-5 w-5 shrink-0",
																																														),
																																														{
																																															type: "binary",
																																														},
																																													),
																																												],
																																											),
																																										],
																																									),
																																								],
																																							),
																																						],
																																					),
																																				],
																																			),
																																			Type.tuple(
																																				[
																																					Type.bitstring(
																																						"aria-hidden",
																																					),
																																					Type.list(
																																						[
																																							Type.tuple(
																																								[
																																									Type.atom(
																																										"text",
																																									),
																																									Type.bitstring(
																																										"true",
																																									),
																																								],
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																		Type.list(
																																			[],
																																		),
																																	]),
																																	Type.tuple([
																																		Type.atom(
																																			"element",
																																		),
																																		Type.bitstring(
																																			"span",
																																		),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.bitstring(
																																						"class",
																																					),
																																					Type.list(
																																						[
																																							Type.tuple(
																																								[
																																									Type.atom(
																																										"text",
																																									),
																																									Type.bitstring(
																																										"min-w-0",
																																									),
																																								],
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																		Type.list([
																																			Type.tuple(
																																				[
																																					Type.atom(
																																						"expression",
																																					),
																																					Type.tuple(
																																						[
																																							Interpreter.dotOperator(
																																								context
																																									.vars
																																									.entry_3,
																																								Type.atom(
																																									"label",
																																								),
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]),
																														]),
																													]),
																													Type.tuple([
																														Type.atom("text"),
																														Type.bitstring(
																															"\n            ",
																														),
																													]),
																												]);
																											},
																											context,
																										),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n          ",
																											),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.atom("text"),
																									Type.bitstring("\n        "),
																								]),
																							]);
																						},
																						context,
																					),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n      "),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Interpreter.case(
																				Interpreter.case(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("preview"),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_4"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_4,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_4,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("true");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.atom("false");
																							},
																						},
																					],
																					context,
																				),
																				[
																					{
																						match: Type.atom("false"),
																						guards: [],
																						body: (context) => {
																							return Type.atom("nil");
																						},
																					},
																					{
																						match: Type.atom("true"),
																						guards: [],
																						body: (context) => {
																							return Type.list([
																								Type.tuple([
																									Type.atom("text"),
																									Type.bitstring("\n        "),
																								]),
																								Type.tuple([
																									Type.atom("element"),
																									Type.bitstring("form"),
																									Type.list([
																										Type.tuple([
																											Type.bitstring("action"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"/logout",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.bitstring("method"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"post",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.bitstring("class"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"mt-2 border-t border-base-300 pt-2",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.bitstring("id"),
																											Type.list([
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Type.bitstring([
																															Type.bitstringSegment(
																																Interpreter.dotOperator(
																																	context.vars
																																		.vars_0,
																																	Type.atom(
																																		"id_prefix",
																																	),
																																),
																																{
																																	type: "binary",
																																},
																															),
																															Type.bitstringSegment(
																																Type.string(
																																	"-logout-form",
																																),
																																{
																																	type: "binary",
																																},
																															),
																														]),
																													]),
																												]),
																											]),
																										]),
																									]),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n          ",
																											),
																										]),
																										Type.tuple([
																											Type.atom("element"),
																											Type.bitstring("input"),
																											Type.list([
																												Type.tuple([
																													Type.bitstring(
																														"type",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"hidden",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring(
																														"name",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"_csrf_token",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring(
																														"value",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom(
																																"expression",
																															),
																															Type.tuple([
																																Interpreter.dotOperator(
																																	Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																																		"projection/2"
																																	](
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"navigation",
																																			),
																																		),
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"request_navigation",
																																			),
																																		),
																																	),
																																	Type.atom(
																																		"csrf_token",
																																	),
																																),
																															]),
																														]),
																													]),
																												]),
																											]),
																											Type.list([]),
																										]),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n          ",
																											),
																										]),
																										Type.tuple([
																											Type.atom("element"),
																											Type.bitstring("button"),
																											Type.list([
																												Type.tuple([
																													Type.bitstring(
																														"type",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"submit",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring(
																														"class",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"btn btn-ghost min-h-11 w-full justify-start",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring("id"),
																													Type.list([
																														Type.tuple([
																															Type.atom(
																																"expression",
																															),
																															Type.tuple([
																																Type.bitstring([
																																	Type.bitstringSegment(
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"id_prefix",
																																			),
																																		),
																																		{
																																			type: "binary",
																																		},
																																	),
																																	Type.bitstringSegment(
																																		Type.string(
																																			"-logout-button",
																																		),
																																		{
																																			type: "binary",
																																		},
																																	),
																																]),
																															]),
																														]),
																													]),
																												]),
																											]),
																											Type.list([
																												Type.tuple([
																													Type.atom("element"),
																													Type.bitstring(
																														"span",
																													),
																													Type.list([
																														Type.tuple([
																															Type.bitstring(
																																"class",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"text",
																																	),
																																	Type.bitstring(
																																		"hero-arrow-right-start-on-rectangle h-5 w-5",
																																	),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.bitstring(
																																"aria-hidden",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"text",
																																	),
																																	Type.bitstring(
																																		"true",
																																	),
																																]),
																															]),
																														]),
																													]),
																													Type.list([]),
																												]),
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Elixir_LeaplyWeb_Hologram_Components_ApplicationMenu[
																															"text/1"
																														](
																															Type.bitstring(
																																"logout",
																															),
																														),
																													]),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n        ",
																											),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.atom("text"),
																									Type.bitstring("\n      "),
																								]),
																							]);
																						},
																					},
																				],
																				context,
																			),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n  "),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ApplicationMenu",
		"text",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("key_0")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_CatalogText["get/2"](
						Type.bitstring("shared"),
						Type.bitstring([
							Type.bitstringSegment(Type.string("shared.navigation."), {
								type: "binary",
							}),
							Type.bitstringSegment(
								Elixir_String_Chars["to_string/1"](context.vars.key_0),
								{ type: "binary" },
							),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Head.StructuredData",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("json"),
								Type.atom("string"),
								Type.list([]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Head.StructuredData",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [
									Type.map([
										[Type.atom("json"), Type.variablePattern("json_0")],
									]),
								],
								guards: [],
								body: (context) => {
									return Type.list([
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("script"),
											Type.list([
												Type.tuple([
													Type.bitstring("type"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("application/ld+json"),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), context.vars.json_0]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.BehavioralCompetenciesSummary",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("heading_level"),
								Type.atom("integer"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.integer(2n)]),
								]),
							]),
							Type.tuple([Type.atom("id"), Type.atom("string"), Type.list([])]),
							Type.tuple([
								Type.atom("entries"),
								Type.atom("list"),
								Type.list([Type.tuple([Type.atom("default"), Type.list([])])]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.BehavioralCompetenciesSummary",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Interpreter.case(
											Erlang["/=/2"](
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("entries"),
												),
												Type.list([]),
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("section"),
																Type.list([
																	Type.tuple([
																		Type.bitstring("aria-labelledby"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Type.bitstring([
																						Type.bitstringSegment(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("id"),
																							),
																							{ type: "binary" },
																						),
																						Type.bitstringSegment(
																							Type.string("-heading"),
																							{ type: "binary" },
																						),
																					]),
																				]),
																			]),
																		]),
																	]),
																]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Interpreter.case(
																		Erlang["==/2"](
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("heading_level"),
																			),
																			Type.integer(4n),
																		),
																		[
																			{
																				match: Type.atom("false"),
																				guards: [],
																				body: (context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("h2"),
																							Type.list([
																								Type.tuple([
																									Type.bitstring("id"),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Type.bitstring([
																													Type.bitstringSegment(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom("id"),
																														),
																														{ type: "binary" },
																													),
																													Type.bitstringSegment(
																														Type.string(
																															"-heading",
																														),
																														{ type: "binary" },
																													),
																												]),
																											]),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.bitstring("class"),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"text-base font-semibold",
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.list([
																								Type.tuple([
																									Type.atom("expression"),
																									Type.tuple([
																										Elixir_LeaplyWeb_Hologram_Components_Social_BehavioralCompetenciesSummary[
																											"title/0"
																										](),
																									]),
																								]),
																							]),
																						]),
																					]);
																				},
																			},
																			{
																				match: Type.atom("true"),
																				guards: [],
																				body: (context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("h4"),
																							Type.list([
																								Type.tuple([
																									Type.bitstring("id"),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Type.bitstring([
																													Type.bitstringSegment(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom("id"),
																														),
																														{ type: "binary" },
																													),
																													Type.bitstringSegment(
																														Type.string(
																															"-heading",
																														),
																														{ type: "binary" },
																													),
																												]),
																											]),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.bitstring("class"),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"text-base font-semibold",
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.list([
																								Type.tuple([
																									Type.atom("expression"),
																									Type.tuple([
																										Elixir_LeaplyWeb_Hologram_Components_Social_BehavioralCompetenciesSummary[
																											"title/0"
																										](),
																									]),
																								]),
																							]),
																						]),
																					]);
																				},
																			},
																		],
																		context,
																	),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("ol"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("mt-3 space-y-4"),
																					]),
																				]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Interpreter.comprehension(
																				[
																					{
																						match:
																							Type.variablePattern("entry_1"),
																						guards: [],
																						body: (context) =>
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("entries"),
																							),
																					},
																				],
																				[],
																				Type.list([]),
																				false,
																				(context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n        "),
																						]),
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("li"),
																							Type.list([]),
																							Type.list([
																								Interpreter.case(
																									Erlang["==/2"](
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom(
																												"heading_level",
																											),
																										),
																										Type.integer(4n),
																									),
																									[
																										{
																											match: Type.atom("false"),
																											guards: [],
																											body: (context) => {
																												return Type.list([
																													Type.tuple([
																														Type.atom(
																															"element",
																														),
																														Type.bitstring(
																															"h3",
																														),
																														Type.list([
																															Type.tuple([
																																Type.bitstring(
																																	"class",
																																),
																																Type.list([
																																	Type.tuple([
																																		Type.atom(
																																			"text",
																																		),
																																		Type.bitstring(
																																			"text-sm font-semibold",
																																		),
																																	]),
																																]),
																															]),
																														]),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"expression",
																																),
																																Type.tuple([
																																	Interpreter.dotOperator(
																																		context.vars
																																			.entry_1,
																																		Type.atom(
																																			"name",
																																		),
																																	),
																																]),
																															]),
																														]),
																													]),
																												]);
																											},
																										},
																										{
																											match: Type.atom("true"),
																											guards: [],
																											body: (context) => {
																												return Type.list([
																													Type.tuple([
																														Type.atom(
																															"element",
																														),
																														Type.bitstring(
																															"h5",
																														),
																														Type.list([
																															Type.tuple([
																																Type.bitstring(
																																	"class",
																																),
																																Type.list([
																																	Type.tuple([
																																		Type.atom(
																																			"text",
																																		),
																																		Type.bitstring(
																																			"text-sm font-semibold",
																																		),
																																	]),
																																]),
																															]),
																														]),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"expression",
																																),
																																Type.tuple([
																																	Interpreter.dotOperator(
																																		context.vars
																																			.entry_1,
																																		Type.atom(
																																			"name",
																																		),
																																	),
																																]),
																															]),
																														]),
																													]),
																												]);
																											},
																										},
																									],
																									context,
																								),
																								Type.tuple([
																									Type.atom("element"),
																									Type.bitstring("p"),
																									Type.list([
																										Type.tuple([
																											Type.bitstring("class"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"mt-1 text-sm leading-6 text-base-content/80",
																													),
																												]),
																											]),
																										]),
																									]),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Interpreter.dotOperator(
																													context.vars.entry_1,
																													Type.atom(
																														"description",
																													),
																												),
																											]),
																										]),
																									]),
																								]),
																							]),
																						]),
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n      "),
																						]),
																					]);
																				},
																				context,
																			),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n  "),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.BehavioralCompetenciesSummary",
		"title",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.behavioral_competencies.title"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.LanguagesSummary",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("class"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.bitstring("")]),
								]),
							]),
							Type.tuple([
								Type.atom("heading_level"),
								Type.atom("integer"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.integer(2n)]),
								]),
							]),
							Type.tuple([Type.atom("id"), Type.atom("string"), Type.list([])]),
							Type.tuple([
								Type.atom("entries"),
								Type.atom("list"),
								Type.list([Type.tuple([Type.atom("default"), Type.list([])])]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.LanguagesSummary",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Interpreter.case(
											Erlang["/=/2"](
												Interpreter.dotOperator(
													context.vars.vars_0,
													Type.atom("entries"),
												),
												Type.list([]),
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("nil");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Type.list([
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n  "),
															]),
															Type.tuple([
																Type.atom("element"),
																Type.bitstring("section"),
																Type.list([
																	Type.tuple([
																		Type.bitstring("aria-labelledby"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Type.bitstring([
																						Type.bitstringSegment(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("id"),
																							),
																							{ type: "binary" },
																						),
																						Type.bitstringSegment(
																							Type.string("-heading"),
																							{ type: "binary" },
																						),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.bitstring("class"),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("class"),
																					),
																				]),
																			]),
																		]),
																	]),
																]),
																Type.list([
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Interpreter.case(
																		Erlang["==/2"](
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("heading_level"),
																			),
																			Type.integer(4n),
																		),
																		[
																			{
																				match: Type.atom("false"),
																				guards: [],
																				body: (context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n      "),
																						]),
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("h2"),
																							Type.list([
																								Type.tuple([
																									Type.bitstring("id"),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Type.bitstring([
																													Type.bitstringSegment(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom("id"),
																														),
																														{ type: "binary" },
																													),
																													Type.bitstringSegment(
																														Type.string(
																															"-heading",
																														),
																														{ type: "binary" },
																													),
																												]),
																											]),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.bitstring("class"),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"text-base font-semibold",
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.list([
																								Type.tuple([
																									Type.atom("expression"),
																									Type.tuple([
																										Elixir_LeaplyWeb_Hologram_Components_Social_LanguagesSummary[
																											"title/0"
																										](),
																									]),
																								]),
																							]),
																						]),
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n    "),
																						]),
																					]);
																				},
																			},
																			{
																				match: Type.atom("true"),
																				guards: [],
																				body: (context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n      "),
																						]),
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("h4"),
																							Type.list([
																								Type.tuple([
																									Type.bitstring("id"),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Type.bitstring([
																													Type.bitstringSegment(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom("id"),
																														),
																														{ type: "binary" },
																													),
																													Type.bitstringSegment(
																														Type.string(
																															"-heading",
																														),
																														{ type: "binary" },
																													),
																												]),
																											]),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.bitstring("class"),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"text-base font-semibold",
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.list([
																								Type.tuple([
																									Type.atom("expression"),
																									Type.tuple([
																										Elixir_LeaplyWeb_Hologram_Components_Social_LanguagesSummary[
																											"title/0"
																										](),
																									]),
																								]),
																							]),
																						]),
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("\n    "),
																						]),
																					]);
																				},
																			},
																		],
																		context,
																	),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n    "),
																	]),
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("ol"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("mt-3 space-y-2"),
																					]),
																				]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Interpreter.comprehension(
																				[
																					{
																						match:
																							Type.variablePattern("entry_1"),
																						guards: [],
																						body: (context) =>
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("entries"),
																							),
																					},
																				],
																				[],
																				Type.list([]),
																				false,
																				(context) => {
																					return Type.list([
																						Type.tuple([
																							Type.atom("element"),
																							Type.bitstring("li"),
																							Type.list([
																								Type.tuple([
																									Type.bitstring("class"),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring("text-sm"),
																										]),
																									]),
																								]),
																							]),
																							Type.list([
																								Type.tuple([
																									Type.atom("element"),
																									Type.bitstring("span"),
																									Type.list([
																										Type.tuple([
																											Type.bitstring("class"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"font-semibold",
																													),
																												]),
																											]),
																										]),
																									]),
																									Type.list([
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Interpreter.dotOperator(
																													context.vars.entry_1,
																													Type.atom("name"),
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.tuple([
																									Type.atom("element"),
																									Type.bitstring("span"),
																									Type.list([
																										Type.tuple([
																											Type.bitstring("class"),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"text-base-content/80",
																													),
																												]),
																											]),
																										]),
																									]),
																									Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(" — "),
																										]),
																										Type.tuple([
																											Type.atom("expression"),
																											Type.tuple([
																												Interpreter.dotOperator(
																													context.vars.entry_1,
																													Type.atom(
																														"proficiency_label",
																													),
																												),
																											]),
																										]),
																									]),
																								]),
																							]),
																						]),
																					]);
																				},
																				context,
																			),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]),
																	]),
																	Type.tuple([
																		Type.atom("text"),
																		Type.bitstring("\n  "),
																	]),
																]),
															]),
															Type.tuple([
																Type.atom("text"),
																Type.bitstring("\n"),
															]),
														]);
													},
												},
											],
											context,
										),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.LanguagesSummary",
		"title",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.languages.title"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"avatar_initial",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("profile_0"),
					Type.variablePattern("fallback_1"),
				],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_String["first/1"](
							Elixir_String["trim/1"](
								Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
									"profile_name/2"
								](context.vars.profile_0, context.vars.fallback_1),
							),
						),
						[
							{
								match: Type.atom("nil"),
								guards: [],
								body: (context) => {
									return Type.bitstring("P");
								},
							},
							{
								match: Type.variablePattern("initial_2"),
								guards: [],
								body: (context) => {
									return Elixir_String["upcase/1"](context.vars.initial_2);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"humanize",
		1,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [],
				body: (context) => {
					return Elixir_String["capitalize/1"](
						Elixir_String["replace/3"](
							context.vars.value_0,
							Type.bitstring("_"),
							Type.bitstring(" "),
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"links_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.links.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"qualifications_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.qualifications.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"interests_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.interests.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"platform_label",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("linkedin")],
				guards: [],
				body: (context) => {
					return Type.bitstring("LinkedIn");
				},
			},
			{
				params: (context) => [Type.atom("facebook")],
				guards: [],
				body: (context) => {
					return Type.bitstring("Facebook");
				},
			},
			{
				params: (context) => [Type.atom("twitter")],
				guards: [],
				body: (context) => {
					return Type.bitstring("X / Twitter");
				},
			},
			{
				params: (context) => [Type.atom("website")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.form.social_links.website"),
					);
				},
			},
			{
				params: (context) => [Type.atom("github")],
				guards: [],
				body: (context) => {
					return Type.bitstring("GitHub");
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.bitstring("Link");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"competencies_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.competencies.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"disability_badge",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.disability"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"format_month_year",
		1,
		"private",
		[
			{
				params: (context) => [
					Interpreter.matchOperator(
						Type.variablePattern("date_0"),
						Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Date")]]),
						context,
					),
				],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Elixir_String["pad_leading/3"](
							Erlang["integer_to_binary/1"](
								Interpreter.dotOperator(
									context.vars.date_0,
									Type.atom("month"),
								),
							),
							Type.integer(2n),
							Type.bitstring("0"),
						),
						Type.variablePattern("month_1"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Type.bitstring([
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](
								Interpreter.dotOperator(context.vars.date_0, Type.atom("year")),
							),
							{ type: "binary" },
						),
						Type.bitstringSegment(Type.string("-"), { type: "binary" }),
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.month_1),
							{ type: "binary" },
						),
					]);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"experience_period",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("record_0")],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"format_month_year/1"
						](
							Elixir_Map["get/2"](
								context.vars.record_0,
								Type.atom("start_date"),
							),
						),
						Type.variablePattern("start_label_1"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					Interpreter.matchOperator(
						Interpreter.case(
							Elixir_Map["get/2"](context.vars.record_0, Type.atom("current")),
							[
								{
									match: Type.variablePattern("x_2"),
									guards: [
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["=:=/2"](context.vars.x_2, Type.atom("false")),
												(context) =>
													Erlang["=:=/2"](context.vars.x_2, Type.atom("nil")),
												context,
											),
									],
									body: (context) => {
										return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
											"format_month_year/1"
										](
											Elixir_Map["get/2"](
												context.vars.record_0,
												Type.atom("end_date"),
											),
										);
									},
								},
								{
									match: Type.matchPlaceholder(),
									guards: [],
									body: (context) => {
										return Type.bitstring("Present");
									},
								},
							],
							context,
						),
						Type.variablePattern("end_label_3"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"join_period/2"
					](context.vars.start_label_1, context.vars.end_label_3);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"demographic_label",
		2,
		"private",
		[
			{
				params: (context) => [
					Type.atom("nil"),
					Type.variablePattern("value_0"),
				],
				guards: [],
				body: (context) => {
					return context.vars.value_0;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("key_0"),
					Type.matchPlaceholder(),
				],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						context.vars.key_0,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"public_demographics",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("profile_0"),
					Type.variablePattern("locale_1"),
				],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"maybe_add_detail/3"
					](
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"maybe_add_detail/3"
						](
							Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
								"maybe_add_detail/3"
							](
								Type.list([]),
								Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
									Type.bitstring("social_profile.public.date_of_birth"),
								),
								Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
									"format_date/1"
								](
									Interpreter.dotOperator(
										context.vars.profile_0,
										Type.atom("date_of_birth"),
									),
								),
							),
							Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
								Type.bitstring("social_profile.public.ethnicity"),
							),
							Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
								"demographic_label/2"
							](
								Elixir_Leaply_Social_ProfileDemographics[
									"ethnicity_label_key/2"
								](
									Interpreter.dotOperator(
										context.vars.profile_0,
										Type.atom("ethnicity"),
									),
									context.vars.locale_1,
								),
								Interpreter.dotOperator(
									context.vars.profile_0,
									Type.atom("ethnicity"),
								),
							),
						),
						Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
							Type.bitstring("social_profile.public.gender_identity"),
						),
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"demographic_label/2"
						](
							Elixir_Leaply_Social_ProfileDemographics[
								"gender_identity_label_key/1"
							](
								Interpreter.dotOperator(
									context.vars.profile_0,
									Type.atom("gender_identity"),
								),
							),
							Interpreter.dotOperator(
								context.vars.profile_0,
								Type.atom("gender_identity"),
							),
						),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"opportunities_badge",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.opportunities"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"discipline_label",
		1,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)],
				body: (context) => {
					return context.vars.value_0;
				},
			},
			{
				params: (context) => [
					Type.map([[Type.atom("name"), Type.variablePattern("name_0")]]),
				],
				guards: [(context) => Erlang["is_binary/1"](context.vars.name_0)],
				body: (context) => {
					return context.vars.name_0;
				},
			},
			{
				params: (context) => [
					Type.map([[Type.bitstring("name"), Type.variablePattern("name_0")]]),
				],
				guards: [(context) => Erlang["is_binary/1"](context.vars.name_0)],
				body: (context) => {
					return context.vars.name_0;
				},
			},
			{
				params: (context) => [
					Type.map([
						[Type.atom("discipline_id"), Type.variablePattern("id_0")],
					]),
				],
				guards: [],
				body: (context) => {
					return Type.bitstring([
						Type.bitstringSegment(Type.string("Discipline #"), {
							type: "binary",
						}),
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.id_0),
							{ type: "binary" },
						),
					]);
				},
			},
			{
				params: (context) => [
					Type.map([
						[Type.bitstring("discipline_id"), Type.variablePattern("id_0")],
					]),
				],
				guards: [],
				body: (context) => {
					return Type.bitstring([
						Type.bitstringSegment(Type.string("Discipline #"), {
							type: "binary",
						}),
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.id_0),
							{ type: "binary" },
						),
					]);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.bitstring("Discipline");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"profile_name",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("profile_0"),
					Type.variablePattern("fallback_1"),
				],
				guards: [],
				body: (context) => {
					return Interpreter.cond(
						[
							{
								condition: (context) =>
									Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
										"present?/1"
									](
										Interpreter.dotOperator(
											context.vars.profile_0,
											Type.atom("display_name"),
										),
									),
								body: (context) => {
									return Interpreter.dotOperator(
										context.vars.profile_0,
										Type.atom("display_name"),
									);
								},
							},
							{
								condition: (context) =>
									Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
										"present?/1"
									](context.vars.fallback_1),
								body: (context) => {
									return context.vars.fallback_1;
								},
							},
							{
								condition: (context) =>
									Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
										"present?/1"
									](
										Interpreter.dotOperator(
											context.vars.profile_0,
											Type.atom("handle"),
										),
									),
								body: (context) => {
									return Interpreter.dotOperator(
										context.vars.profile_0,
										Type.atom("handle"),
									);
								},
							},
							{
								condition: (context) => Type.atom("true"),
								body: (context) => {
									return Type.bitstring("Your profile");
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"has_items?",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("items_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Erlang["is_list/1"](context.vars.items_0),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.atom("false");
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Erlang["/=/2"](context.vars.items_0, Type.list([]));
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"join_period",
		2,
		"private",
		[
			{
				params: (context) => [Type.atom("nil"), Type.atom("nil")],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
			{
				params: (context) => [
					Type.variablePattern("start_label_0"),
					Type.atom("nil"),
				],
				guards: [],
				body: (context) => {
					return context.vars.start_label_0;
				},
			},
			{
				params: (context) => [
					Type.atom("nil"),
					Type.variablePattern("end_label_0"),
				],
				guards: [],
				body: (context) => {
					return context.vars.end_label_0;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("start_label_0"),
					Type.variablePattern("end_label_1"),
				],
				guards: [],
				body: (context) => {
					return Type.bitstring([
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.start_label_0),
							{ type: "binary" },
						),
						Type.bitstringSegment(Type.string(" – "), { type: "binary" }),
						Type.bitstringSegment(
							Elixir_String_Chars["to_string/1"](context.vars.end_label_1),
							{ type: "binary" },
						),
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"disability_category_present?",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("profile_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Erlang["==/2"](
							Interpreter.dotOperator(
								context.vars.profile_0,
								Type.atom("has_limiting_disability"),
							),
							Type.atom("true"),
						),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.atom("false");
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Erlang["not/1"](
										Erlang["==/2"](
											Interpreter.dotOperator(
												context.vars.profile_0,
												Type.atom("disability_category"),
											),
											Type.atom("nil"),
										),
									);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"academic_period",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("record_0")],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"format_year/1"
						](
							Elixir_Map["get/2"](
								context.vars.record_0,
								Type.atom("started_academic_year"),
							),
						),
						Type.variablePattern("start_label_1"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					Interpreter.matchOperator(
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"format_year/1"
						](
							Elixir_Map["get/2"](
								context.vars.record_0,
								Type.atom("ended_academic_year"),
							),
						),
						Type.variablePattern("end_label_2"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"join_period/2"
					](context.vars.start_label_1, context.vars.end_label_2);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"skills_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.skills.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"tools_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.tools.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"maybe_add_detail",
		3,
		"private",
		[
			{
				params: (context) => [
					Type.variablePattern("items_0"),
					Type.matchPlaceholder(),
					Type.atom("nil"),
				],
				guards: [],
				body: (context) => {
					return context.vars.items_0;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("items_0"),
					Type.matchPlaceholder(),
					Type.bitstring(""),
				],
				guards: [],
				body: (context) => {
					return context.vars.items_0;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("items_0"),
					Type.variablePattern("label_1"),
					Type.variablePattern("value_2"),
				],
				guards: [],
				body: (context) => {
					return Erlang["++/2"](
						context.vars.items_0,
						Type.list([
							Type.map([
								[Type.atom("label"), context.vars.label_1],
								[Type.atom("value"), context.vars.value_2],
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"experience_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.experience.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"profile_path",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("profile_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
							"present?/1"
						](
							Interpreter.dotOperator(
								context.vars.profile_0,
								Type.atom("handle"),
							),
						),
						[
							{
								match: Type.variablePattern("x_1"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.bitstring("Public handle not chosen");
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.bitstring([
										Type.bitstringSegment(Type.string("/people/"), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_String_Chars["to_string/1"](
												Interpreter.dotOperator(
													context.vars.profile_0,
													Type.atom("handle"),
												),
											),
											{ type: "binary" },
										),
									]);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"disability_label",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("autism_spectrum_disorder")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.autism_spectrum_disorder",
						),
					);
				},
			},
			{
				params: (context) => [Type.atom("hearing_impairment")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.hearing_impairment",
						),
					);
				},
			},
			{
				params: (context) => [Type.atom("mental_health_condition")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.mental_health_condition",
						),
					);
				},
			},
			{
				params: (context) => [Type.atom("physical_disability")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.physical_disability",
						),
					);
				},
			},
			{
				params: (context) => [Type.atom("progressive_chronic_condition")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.progressive_chronic_condition",
						),
					);
				},
			},
			{
				params: (context) => [Type.atom("vision_impairment")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring(
							"social_profile.disability_category.vision_impairment",
						),
					);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"disability_badge/0"
					]();
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"format_date",
		1,
		"private",
		[
			{
				params: (context) => [
					Interpreter.matchOperator(
						Type.variablePattern("date_0"),
						Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Date")]]),
						context,
					),
				],
				guards: [],
				body: (context) => {
					return Elixir_Date["to_iso8601/1"](context.vars.date_0);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"discipline_labels",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("record_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_Map["get/2"](
							context.vars.record_0,
							Type.atom("disciplines"),
						),
						[
							{
								match: Type.variablePattern("disciplines_1"),
								guards: [
									(context) => Erlang["is_list/1"](context.vars.disciplines_1),
								],
								body: (context) => {
									return Elixir_Enum["map/2"](
										context.vars.disciplines_1,
										Type.functionCapture(
											"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
											"discipline_label",
											1,
											[
												{
													params: (context) => [Type.variablePattern("$1")],
													guards: [],
													body: (context) => {
														return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
															"discipline_label/1"
														](context.vars["$1"]);
													},
												},
											],
											context,
										),
									);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.list([]);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"surface_label",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.profile.label"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"awarded_on_label",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("record_0")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"format_date/1"
					](
						Elixir_Map["get/2"](context.vars.record_0, Type.atom("awarded_on")),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("article"),
											Type.list([
												Type.tuple([
													Type.bitstring("id"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("surface_id"),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-profile-public-surface"),
													Type.list([]),
												]),
												Type.tuple([
													Type.bitstring("class"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring(
																"overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-sm",
															),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("aria-labelledby"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("heading_id"),
																),
															]),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("header"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"border-b border-app-border bg-app-surface-muted px-5 py-7 sm:px-8",
																	),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("div"),
															Type.list([
																Type.tuple([
																	Type.bitstring("class"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"flex flex-col gap-5 sm:flex-row sm:items-center",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Interpreter.case(
																	Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																		"has_avatar?/1"
																	](
																		Interpreter.dotOperator(
																			context.vars.vars_0,
																			Type.atom("profile"),
																		),
																	),
																	[
																		{
																			match: Type.variablePattern("x_1"),
																			guards: [
																				(context) =>
																					Erlang["orelse/2"](
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.x_1,
																								Type.atom("false"),
																							),
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.x_1,
																								Type.atom("nil"),
																							),
																						context,
																					),
																			],
																			body: (context) => {
																				return Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n        "),
																					]),
																					Type.tuple([
																						Type.atom("element"),
																						Type.bitstring("div"),
																						Type.list([
																							Type.tuple([
																								Type.bitstring("class"),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-blue-100 text-2xl font-black text-blue-700 dark:bg-blue-950 dark:text-blue-300",
																										),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.bitstring("aria-hidden"),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring("true"),
																									]),
																								]),
																							]),
																						]),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																										"avatar_initial/2"
																									](
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("profile"),
																										),
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("display_name"),
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n      "),
																					]),
																				]);
																			},
																		},
																		{
																			match: Type.matchPlaceholder(),
																			guards: [],
																			body: (context) => {
																				return Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n        "),
																					]),
																					Type.tuple([
																						Type.atom("element"),
																						Type.bitstring("img"),
																						Type.list([
																							Type.tuple([
																								Type.bitstring("src"),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Interpreter.dotOperator(
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Type.atom("avatar"),
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.bitstring("alt"),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(""),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.bitstring("class"),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"h-20 w-20 rounded-full object-cover ring-4 ring-blue-100 dark:ring-blue-950",
																										),
																									]),
																								]),
																							]),
																						]),
																						Type.list([]),
																					]),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n      "),
																					]),
																				]);
																			},
																		},
																	],
																	context,
																),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("div"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("class"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("min-w-0"),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("p"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-xs font-bold uppercase tracking-[0.14em] text-app-muted",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"surface_label/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("primary_heading"),
																			),
																			[
																				{
																					match: Type.variablePattern("x_2"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_2,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_2,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("h2"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("id"),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Interpreter.dotOperator(
																														context.vars.vars_0,
																														Type.atom(
																															"heading_id",
																														),
																													),
																												]),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-1 text-3xl font-black tracking-tight text-app-ink",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"profile_name/2"
																											](
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom(
																														"display_name",
																													),
																												),
																											),
																										]),
																									]),
																								]),
																							]),
																						]);
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("h1"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("id"),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Interpreter.dotOperator(
																														context.vars.vars_0,
																														Type.atom(
																															"heading_id",
																														),
																													),
																												]),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-1 text-3xl font-black tracking-tight text-app-ink",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"profile_name/2"
																											](
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom(
																														"display_name",
																													),
																												),
																											),
																										]),
																									]),
																								]),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("show_profile_path"),
																			),
																			[
																				{
																					match: Type.variablePattern("x_3"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_3,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_3,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.atom("nil");
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-1 break-all text-sm text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"profile_path/1"
																											](
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																											),
																										]),
																									]),
																								]),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"present?/1"
																			](
																				Interpreter.dotOperator(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("profile"),
																					),
																					Type.atom("headline"),
																				),
																			),
																			[
																				{
																					match: Type.variablePattern("x_4"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_4,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_4,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.atom("nil");
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 text-lg font-semibold text-app-ink",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Interpreter.dotOperator(
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Type.atom("headline"),
																											),
																										]),
																									]),
																								]),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("div"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"mt-3 flex flex-wrap gap-2",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																						"present?/1"
																					](
																						Interpreter.dotOperator(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom("location"),
																						),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_5"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_5,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_5,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("span"),
																										Type.list([
																											Type.tuple([
																												Type.bitstring("class"),
																												Type.list([
																													Type.tuple([
																														Type.atom("text"),
																														Type.bitstring(
																															"inline-flex rounded-md bg-app-surface px-2.5 py-1 text-xs font-bold text-app-muted ring-1 ring-inset ring-app-border",
																														),
																													]),
																												]),
																											]),
																										]),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Interpreter.dotOperator(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"profile",
																															),
																														),
																														Type.atom(
																															"location",
																														),
																													),
																												]),
																											]),
																										]),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Interpreter.dotOperator(
																						Interpreter.dotOperator(
																							context.vars.vars_0,
																							Type.atom("profile"),
																						),
																						Type.atom(
																							"looking_for_opportunities",
																						),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_6"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_6,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_6,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("span"),
																										Type.list([
																											Type.tuple([
																												Type.bitstring("class"),
																												Type.list([
																													Type.tuple([
																														Type.atom("text"),
																														Type.bitstring(
																															"inline-flex rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-200 dark:ring-emerald-800",
																														),
																													]),
																												]),
																											]),
																										]),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																														"opportunities_badge/0"
																													](),
																												]),
																											]),
																										]),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n        "),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n    "),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([
													Type.atom("text"),
													Type.bitstring("\n\n  "),
												]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("div"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"grid gap-6 p-5 sm:p-8 lg:grid-cols-[minmax(0,1fr)_260px]",
																	),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("div"),
															Type.list([
																Type.tuple([
																	Type.bitstring("class"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("space-y-6"),
																		]),
																	]),
																]),
															]),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("section"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("aria-labelledby"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string("-about-heading"),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("h2"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("expression"),
																							Type.tuple([
																								Type.bitstring([
																									Type.bitstringSegment(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("surface_id"),
																										),
																										{ type: "binary" },
																									),
																									Type.bitstringSegment(
																										Type.string(
																											"-about-heading",
																										),
																										{ type: "binary" },
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-lg font-black text-app-ink",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"summary_heading/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"present?/1"
																			](
																				Interpreter.dotOperator(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("profile"),
																					),
																					Type.atom("summary"),
																				),
																			),
																			[
																				{
																					match: Type.variablePattern("x_7"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_7,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_7,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 rounded-lg border border-dashed border-app-border px-4 py-5 text-sm text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"no_records_label/0"
																											](),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 whitespace-pre-wrap text-sm leading-7 text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Interpreter.dotOperator(
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Type.atom("summary"),
																											),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("section"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("aria-labelledby"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string(
																									"-experience-heading",
																								),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("h2"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("expression"),
																							Type.tuple([
																								Type.bitstring([
																									Type.bitstringSegment(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("surface_id"),
																										),
																										{ type: "binary" },
																									),
																									Type.bitstringSegment(
																										Type.string(
																											"-experience-heading",
																										),
																										{ type: "binary" },
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-lg font-black text-app-ink",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"experience_heading/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"has_items?/1"
																			](
																				Interpreter.dotOperator(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("profile"),
																					),
																					Type.atom("experience_records"),
																				),
																			),
																			[
																				{
																					match: Type.variablePattern("x_8"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_8,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_8,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 text-sm text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"no_records_label/0"
																											](),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("div"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("id"),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Type.bitstring([
																														Type.bitstringSegment(
																															Interpreter.dotOperator(
																																context.vars
																																	.vars_0,
																																Type.atom(
																																	"surface_id",
																																),
																															),
																															{
																																type: "binary",
																															},
																														),
																														Type.bitstringSegment(
																															Type.string(
																																"-experience-records",
																															),
																															{
																																type: "binary",
																															},
																														),
																													]),
																												]),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 space-y-3",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.comprehension(
																										[
																											{
																												match:
																													Type.variablePattern(
																														"record_9",
																													),
																												guards: [],
																												body: (context) =>
																													Interpreter.dotOperator(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"profile",
																															),
																														),
																														Type.atom(
																															"experience_records",
																														),
																													),
																											},
																										],
																										[],
																										Type.list([]),
																										false,
																										(context) => {
																											return Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n              ",
																													),
																												]),
																												Type.tuple([
																													Type.atom("element"),
																													Type.bitstring(
																														"article",
																													),
																													Type.list([
																														Type.tuple([
																															Type.bitstring(
																																"class",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"text",
																																	),
																																	Type.bitstring(
																																		"rounded-lg border border-app-border bg-app-surface-muted p-4",
																																	),
																																]),
																															]),
																														]),
																													]),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"h3",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"text-sm font-black text-app-ink",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"expression",
																																	),
																																	Type.tuple([
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.record_9,
																																			Type.atom(
																																				"title",
																																			),
																																		),
																																	]),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Interpreter.dotOperator(
																																context.vars
																																	.record_9,
																																Type.atom(
																																	"subtitle",
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_10",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_10,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_10,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-1 text-sm text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Interpreter.dotOperator(
																																													context
																																														.vars
																																														.record_9,
																																													Type.atom(
																																														"subtitle",
																																													),
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"experience_period/1"
																															](
																																context.vars
																																	.record_9,
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_11",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_11,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_11,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-2 text-xs font-semibold text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"experience_period/1"
																																												](
																																													context
																																														.vars
																																														.record_9,
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Interpreter.dotOperator(
																																context.vars
																																	.record_9,
																																Type.atom(
																																	"summary",
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_12",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_12,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_12,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-2 text-sm leading-6 text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Interpreter.dotOperator(
																																													context
																																														.vars
																																														.record_9,
																																													Type.atom(
																																														"summary",
																																													),
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"verification_label/1"
																															](
																																context.vars
																																	.record_9,
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_13",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_13,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_13,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-2 text-xs text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"text",
																																										),
																																										Type.bitstring(
																																											"Verification: ",
																																										),
																																									],
																																								),
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"verification_label/1"
																																												](
																																													context
																																														.vars
																																														.record_9,
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"has_items?/1"
																															](
																																Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																	"discipline_labels/1"
																																](
																																	context.vars
																																		.record_9,
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_14",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_14,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_14,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"div",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-2 flex flex-wrap gap-1.5",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Interpreter.comprehension(
																																									[
																																										{
																																											match:
																																												Type.variablePattern(
																																													"discipline_15",
																																												),
																																											guards:
																																												[],
																																											body: (
																																												context,
																																											) =>
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"discipline_labels/1"
																																												](
																																													context
																																														.vars
																																														.record_9,
																																												),
																																										},
																																									],
																																									[],
																																									Type.list(
																																										[],
																																									),
																																									false,
																																									(
																																										context,
																																									) => {
																																										return Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"element",
																																														),
																																														Type.bitstring(
																																															"span",
																																														),
																																														Type.list(
																																															[
																																																Type.tuple(
																																																	[
																																																		Type.bitstring(
																																																			"class",
																																																		),
																																																		Type.list(
																																																			[
																																																				Type.tuple(
																																																					[
																																																						Type.atom(
																																																							"text",
																																																						),
																																																						Type.bitstring(
																																																							"rounded-md bg-app-surface px-2 py-0.5 text-xs font-semibold text-app-muted ring-1 ring-inset ring-app-border",
																																																						),
																																																					],
																																																				),
																																																			],
																																																		),
																																																	],
																																																),
																																															],
																																														),
																																														Type.list(
																																															[
																																																Type.tuple(
																																																	[
																																																		Type.atom(
																																																			"expression",
																																																		),
																																																		Type.tuple(
																																																			[
																																																				context
																																																					.vars
																																																					.discipline_15,
																																																			],
																																																		),
																																																	],
																																																),
																																															],
																																														),
																																													],
																																												),
																																											],
																																										);
																																									},
																																									context,
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n              ",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																											]);
																										},
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("section"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("aria-labelledby"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string(
																									"-education-heading",
																								),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("h2"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("expression"),
																							Type.tuple([
																								Type.bitstring([
																									Type.bitstringSegment(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("surface_id"),
																										),
																										{ type: "binary" },
																									),
																									Type.bitstringSegment(
																										Type.string(
																											"-education-heading",
																										),
																										{ type: "binary" },
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-lg font-black text-app-ink",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"qualifications_heading/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"has_items?/1"
																			](
																				Interpreter.dotOperator(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("profile"),
																					),
																					Type.atom("academic_records"),
																				),
																			),
																			[
																				{
																					match: Type.variablePattern("x_16"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_16,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_16,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 text-sm text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"no_records_label/0"
																											](),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("div"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("id"),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Type.bitstring([
																														Type.bitstringSegment(
																															Interpreter.dotOperator(
																																context.vars
																																	.vars_0,
																																Type.atom(
																																	"surface_id",
																																),
																															),
																															{
																																type: "binary",
																															},
																														),
																														Type.bitstringSegment(
																															Type.string(
																																"-academic-records",
																															),
																															{
																																type: "binary",
																															},
																														),
																													]),
																												]),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 space-y-3",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.comprehension(
																										[
																											{
																												match:
																													Type.variablePattern(
																														"record_17",
																													),
																												guards: [],
																												body: (context) =>
																													Interpreter.dotOperator(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"profile",
																															),
																														),
																														Type.atom(
																															"academic_records",
																														),
																													),
																											},
																										],
																										[],
																										Type.list([]),
																										false,
																										(context) => {
																											return Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n              ",
																													),
																												]),
																												Type.tuple([
																													Type.atom("element"),
																													Type.bitstring(
																														"article",
																													),
																													Type.list([
																														Type.tuple([
																															Type.bitstring(
																																"class",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"text",
																																	),
																																	Type.bitstring(
																																		"rounded-lg border border-app-border bg-app-surface-muted p-4",
																																	),
																																]),
																															]),
																														]),
																													]),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"h3",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"text-sm font-black text-app-ink",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"expression",
																																	),
																																	Type.tuple([
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.record_17,
																																			Type.atom(
																																				"title",
																																			),
																																		),
																																	]),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Interpreter.dotOperator(
																																context.vars
																																	.record_17,
																																Type.atom(
																																	"subtitle",
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_18",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_18,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_18,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-1 text-sm text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Interpreter.dotOperator(
																																													context
																																														.vars
																																														.record_17,
																																													Type.atom(
																																														"subtitle",
																																													),
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Interpreter.dotOperator(
																																context.vars
																																	.record_17,
																																Type.atom(
																																	"qualification_type",
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_19",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_19,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_19,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-2 text-xs font-semibold text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"record_state/1"
																																												](
																																													Interpreter.dotOperator(
																																														context
																																															.vars
																																															.record_17,
																																														Type.atom(
																																															"qualification_type",
																																														),
																																													),
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"academic_period/1"
																															](
																																context.vars
																																	.record_17,
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_20",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_20,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_20,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-1 text-xs text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"academic_period/1"
																																												](
																																													context
																																														.vars
																																														.record_17,
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Interpreter.dotOperator(
																																context.vars
																																	.record_17,
																																Type.atom(
																																	"progress_state",
																																),
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_21",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_21,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_21,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-1 text-xs text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"text",
																																										),
																																										Type.bitstring(
																																											"Status: ",
																																										),
																																									],
																																								),
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"record_state/1"
																																												](
																																													Interpreter.dotOperator(
																																														context
																																															.vars
																																															.record_17,
																																														Type.atom(
																																															"progress_state",
																																														),
																																													),
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n                ",
																															),
																														]),
																														Interpreter.case(
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"awarded_on_label/1"
																															](
																																context.vars
																																	.record_17,
																															),
																															[
																																{
																																	match:
																																		Type.variablePattern(
																																			"x_22",
																																		),
																																	guards: [
																																		(context) =>
																																			Erlang[
																																				"orelse/2"
																																			](
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_22,
																																						Type.atom(
																																							"false",
																																						),
																																					),
																																				(
																																					context,
																																				) =>
																																					Erlang[
																																						"=:=/2"
																																					](
																																						context
																																							.vars
																																							.x_22,
																																						Type.atom(
																																							"nil",
																																						),
																																					),
																																				context,
																																			),
																																	],
																																	body: (
																																		context,
																																	) => {
																																		return Type.atom(
																																			"nil",
																																		);
																																	},
																																},
																																{
																																	match:
																																		Type.matchPlaceholder(),
																																	guards: [],
																																	body: (
																																		context,
																																	) => {
																																		return Type.list(
																																			[
																																				Type.tuple(
																																					[
																																						Type.atom(
																																							"element",
																																						),
																																						Type.bitstring(
																																							"p",
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.bitstring(
																																											"class",
																																										),
																																										Type.list(
																																											[
																																												Type.tuple(
																																													[
																																														Type.atom(
																																															"text",
																																														),
																																														Type.bitstring(
																																															"mt-1 text-xs text-app-muted",
																																														),
																																													],
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																						Type.list(
																																							[
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"text",
																																										),
																																										Type.bitstring(
																																											"Awarded: ",
																																										),
																																									],
																																								),
																																								Type.tuple(
																																									[
																																										Type.atom(
																																											"expression",
																																										),
																																										Type.tuple(
																																											[
																																												Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																													"awarded_on_label/1"
																																												](
																																													context
																																														.vars
																																														.record_17,
																																												),
																																											],
																																										),
																																									],
																																								),
																																							],
																																						),
																																					],
																																				),
																																			],
																																		);
																																	},
																																},
																															],
																															context,
																														),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n              ",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																											]);
																										},
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n    "),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("aside"),
															Type.list([
																Type.tuple([
																	Type.bitstring("class"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"space-y-5 lg:border-l lg:border-app-border lg:pl-6",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Type.tuple([
																	Type.atom("component"),
																	Type.atom(
																		"Elixir.LeaplyWeb.Hologram.Components.Social.BehavioralCompetenciesSummary",
																	),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("id"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string(
																									"-behavioral-competencies",
																								),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("entries"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_Map["get/3"](
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom(
																								"behavioral_competencies",
																							),
																							Type.list([]),
																						),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Type.tuple([
																	Type.atom("component"),
																	Type.atom(
																		"Elixir.LeaplyWeb.Hologram.Components.Social.LanguagesSummary",
																	),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("id"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string("-languages"),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("entries"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_Map["get/3"](
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom("languages"),
																							Type.list([]),
																						),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("section"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("aria-labelledby"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string("-skills-heading"),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("h2"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("expression"),
																							Type.tuple([
																								Type.bitstring([
																									Type.bitstringSegment(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("surface_id"),
																										),
																										{ type: "binary" },
																									),
																									Type.bitstringSegment(
																										Type.string(
																											"-skills-heading",
																										),
																										{ type: "binary" },
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-base font-black text-app-ink",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"skills_heading/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("div"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("mt-3 space-y-4"),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																						"has_items?/1"
																					](
																						Interpreter.dotOperator(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom(
																								"technical_competencies",
																							),
																						),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_23"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_23,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_23,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("div"),
																										Type.list([]),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("h3"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Type.tuple([
																														Type.atom(
																															"expression",
																														),
																														Type.tuple([
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"competencies_heading/0"
																															](),
																														]),
																													]),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("div"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"mt-2 flex flex-wrap gap-2",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Interpreter.comprehension(
																														[
																															{
																																match:
																																	Type.variablePattern(
																																		"item_24",
																																	),
																																guards: [],
																																body: (
																																	context,
																																) =>
																																	Interpreter.dotOperator(
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"profile",
																																			),
																																		),
																																		Type.atom(
																																			"technical_competencies",
																																		),
																																	),
																															},
																														],
																														[],
																														Type.list([]),
																														false,
																														(context) => {
																															return Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"span",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"rounded-md bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-800 dark:bg-blue-950/60 dark:text-blue-200",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					context
																																						.vars
																																						.item_24,
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]);
																														},
																														context,
																													),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n            ",
																												),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																						"has_items?/1"
																					](
																						Interpreter.dotOperator(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom("professional_tools"),
																						),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_25"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_25,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_25,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("div"),
																										Type.list([]),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("h3"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Type.tuple([
																														Type.atom(
																															"expression",
																														),
																														Type.tuple([
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"tools_heading/0"
																															](),
																														]),
																													]),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("div"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"mt-2 flex flex-wrap gap-2",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Interpreter.comprehension(
																														[
																															{
																																match:
																																	Type.variablePattern(
																																		"item_26",
																																	),
																																guards: [],
																																body: (
																																	context,
																																) =>
																																	Interpreter.dotOperator(
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"profile",
																																			),
																																		),
																																		Type.atom(
																																			"professional_tools",
																																		),
																																	),
																															},
																														],
																														[],
																														Type.list([]),
																														false,
																														(context) => {
																															return Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"span",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"rounded-md bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-800 dark:bg-violet-950/60 dark:text-violet-200",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					context
																																						.vars
																																						.item_26,
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]);
																														},
																														context,
																													),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n            ",
																												),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																						"has_items?/1"
																					](
																						Interpreter.dotOperator(
																							Interpreter.dotOperator(
																								context.vars.vars_0,
																								Type.atom("profile"),
																							),
																							Type.atom("interests"),
																						),
																					),
																					[
																						{
																							match:
																								Type.variablePattern("x_27"),
																							guards: [
																								(context) =>
																									Erlang["orelse/2"](
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_27,
																												Type.atom("false"),
																											),
																										(context) =>
																											Erlang["=:=/2"](
																												context.vars.x_27,
																												Type.atom("nil"),
																											),
																										context,
																									),
																							],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.matchPlaceholder(),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("div"),
																										Type.list([]),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("h3"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Type.tuple([
																														Type.atom(
																															"expression",
																														),
																														Type.tuple([
																															Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																"interests_heading/0"
																															](),
																														]),
																													]),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n              ",
																												),
																											]),
																											Type.tuple([
																												Type.atom("element"),
																												Type.bitstring("div"),
																												Type.list([
																													Type.tuple([
																														Type.bitstring(
																															"class",
																														),
																														Type.list([
																															Type.tuple([
																																Type.atom(
																																	"text",
																																),
																																Type.bitstring(
																																	"mt-2 flex flex-wrap gap-2",
																																),
																															]),
																														]),
																													]),
																												]),
																												Type.list([
																													Interpreter.comprehension(
																														[
																															{
																																match:
																																	Type.variablePattern(
																																		"item_28",
																																	),
																																guards: [],
																																body: (
																																	context,
																																) =>
																																	Interpreter.dotOperator(
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.vars_0,
																																			Type.atom(
																																				"profile",
																																			),
																																		),
																																		Type.atom(
																																			"interests",
																																		),
																																	),
																															},
																														],
																														[],
																														Type.list([]),
																														false,
																														(context) => {
																															return Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"span",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"rounded-md bg-app-surface-muted px-2.5 py-1 text-xs font-bold text-app-muted ring-1 ring-inset ring-app-border",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					context
																																						.vars
																																						.item_28,
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]);
																														},
																														context,
																													),
																												]),
																											]),
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"\n            ",
																												),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Interpreter.case(
																					Interpreter.case(
																						Interpreter.case(
																							Interpreter.case(
																								Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																									"has_items?/1"
																								](
																									Interpreter.dotOperator(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("profile"),
																										),
																										Type.atom(
																											"technical_competencies",
																										),
																									),
																								),
																								[
																									{
																										match:
																											Type.variablePattern(
																												"x_29",
																											),
																										guards: [
																											(context) =>
																												Erlang["orelse/2"](
																													(context) =>
																														Erlang["=:=/2"](
																															context.vars.x_29,
																															Type.atom(
																																"false",
																															),
																														),
																													(context) =>
																														Erlang["=:=/2"](
																															context.vars.x_29,
																															Type.atom("nil"),
																														),
																													context,
																												),
																										],
																										body: (context) => {
																											return Type.atom("true");
																										},
																									},
																									{
																										match:
																											Type.matchPlaceholder(),
																										guards: [],
																										body: (context) => {
																											return Type.atom("false");
																										},
																									},
																								],
																								context,
																							),
																							[
																								{
																									match: Type.atom("false"),
																									guards: [],
																									body: (context) => {
																										return Type.atom("false");
																									},
																								},
																								{
																									match: Type.atom("true"),
																									guards: [],
																									body: (context) => {
																										return Interpreter.case(
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"has_items?/1"
																											](
																												Interpreter.dotOperator(
																													Interpreter.dotOperator(
																														context.vars.vars_0,
																														Type.atom(
																															"profile",
																														),
																													),
																													Type.atom(
																														"professional_tools",
																													),
																												),
																											),
																											[
																												{
																													match:
																														Type.variablePattern(
																															"x_30",
																														),
																													guards: [
																														(context) =>
																															Erlang[
																																"orelse/2"
																															](
																																(context) =>
																																	Erlang[
																																		"=:=/2"
																																	](
																																		context.vars
																																			.x_30,
																																		Type.atom(
																																			"false",
																																		),
																																	),
																																(context) =>
																																	Erlang[
																																		"=:=/2"
																																	](
																																		context.vars
																																			.x_30,
																																		Type.atom(
																																			"nil",
																																		),
																																	),
																																context,
																															),
																													],
																													body: (context) => {
																														return Type.atom(
																															"true",
																														);
																													},
																												},
																												{
																													match:
																														Type.matchPlaceholder(),
																													guards: [],
																													body: (context) => {
																														return Type.atom(
																															"false",
																														);
																													},
																												},
																											],
																											context,
																										);
																									},
																								},
																							],
																							context,
																						),
																						[
																							{
																								match: Type.atom("false"),
																								guards: [],
																								body: (context) => {
																									return Type.atom("false");
																								},
																							},
																							{
																								match: Type.atom("true"),
																								guards: [],
																								body: (context) => {
																									return Interpreter.case(
																										Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																											"has_items?/1"
																										](
																											Interpreter.dotOperator(
																												Interpreter.dotOperator(
																													context.vars.vars_0,
																													Type.atom("profile"),
																												),
																												Type.atom("interests"),
																											),
																										),
																										[
																											{
																												match:
																													Type.variablePattern(
																														"x_31",
																													),
																												guards: [
																													(context) =>
																														Erlang["orelse/2"](
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_31,
																																	Type.atom(
																																		"false",
																																	),
																																),
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_31,
																																	Type.atom(
																																		"nil",
																																	),
																																),
																															context,
																														),
																												],
																												body: (context) => {
																													return Type.atom(
																														"true",
																													);
																												},
																											},
																											{
																												match:
																													Type.matchPlaceholder(),
																												guards: [],
																												body: (context) => {
																													return Type.atom(
																														"false",
																													);
																												},
																											},
																										],
																										context,
																									);
																								},
																							},
																						],
																						context,
																					),
																					[
																						{
																							match: Type.atom("false"),
																							guards: [],
																							body: (context) => {
																								return Type.atom("nil");
																							},
																						},
																						{
																							match: Type.atom("true"),
																							guards: [],
																							body: (context) => {
																								return Type.list([
																									Type.tuple([
																										Type.atom("element"),
																										Type.bitstring("p"),
																										Type.list([
																											Type.tuple([
																												Type.bitstring("class"),
																												Type.list([
																													Type.tuple([
																														Type.atom("text"),
																														Type.bitstring(
																															"text-sm text-app-muted",
																														),
																													]),
																												]),
																											]),
																										]),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																														"no_records_label/0"
																													](),
																												]),
																											]),
																										]),
																									]),
																								]);
																							},
																						},
																					],
																					context,
																				),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n        "),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("section"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("aria-labelledby"),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Type.bitstring([
																							Type.bitstringSegment(
																								Interpreter.dotOperator(
																									context.vars.vars_0,
																									Type.atom("surface_id"),
																								),
																								{ type: "binary" },
																							),
																							Type.bitstringSegment(
																								Type.string("-links-heading"),
																								{ type: "binary" },
																							),
																						]),
																					]),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("h2"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("expression"),
																							Type.tuple([
																								Type.bitstring([
																									Type.bitstringSegment(
																										Interpreter.dotOperator(
																											context.vars.vars_0,
																											Type.atom("surface_id"),
																										),
																										{ type: "binary" },
																									),
																									Type.bitstringSegment(
																										Type.string(
																											"-links-heading",
																										),
																										{ type: "binary" },
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"text-base font-black text-app-ink",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																							"links_heading/0"
																						](),
																					]),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Interpreter.case(
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"has_items?/1"
																			](
																				Interpreter.dotOperator(
																					Interpreter.dotOperator(
																						context.vars.vars_0,
																						Type.atom("profile"),
																					),
																					Type.atom("social_links"),
																				),
																			),
																			[
																				{
																					match: Type.variablePattern("x_32"),
																					guards: [
																						(context) =>
																							Erlang["orelse/2"](
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_32,
																										Type.atom("false"),
																									),
																								(context) =>
																									Erlang["=:=/2"](
																										context.vars.x_32,
																										Type.atom("nil"),
																									),
																								context,
																							),
																					],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("p"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 text-sm text-app-muted",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"no_records_label/0"
																											](),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																				{
																					match: Type.matchPlaceholder(),
																					guards: [],
																					body: (context) => {
																						return Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("ul"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 space-y-2",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.comprehension(
																										[
																											{
																												match:
																													Type.variablePattern(
																														"link_33",
																													),
																												guards: [],
																												body: (context) =>
																													Interpreter.dotOperator(
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"profile",
																															),
																														),
																														Type.atom(
																															"social_links",
																														),
																													),
																											},
																										],
																										[],
																										Type.list([]),
																										false,
																										(context) => {
																											return Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n              ",
																													),
																												]),
																												Type.tuple([
																													Type.atom("element"),
																													Type.bitstring("li"),
																													Type.list([]),
																													Type.list([
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"a",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"href",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					Interpreter.dotOperator(
																																						context
																																							.vars
																																							.link_33,
																																						Type.atom(
																																							"url",
																																						),
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																																Type.tuple([
																																	Type.bitstring(
																																		"target",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"_blank",
																																			),
																																		]),
																																	]),
																																]),
																																Type.tuple([
																																	Type.bitstring(
																																		"rel",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"noopener noreferrer",
																																			),
																																		]),
																																	]),
																																]),
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"inline-flex min-h-10 w-full items-center justify-between rounded-md border border-app-border px-3 text-sm font-bold text-blue-700 transition hover:bg-blue-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 dark:text-blue-300 dark:hover:bg-blue-950/60",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"expression",
																																	),
																																	Type.tuple([
																																		Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																			"platform_label/1"
																																		](
																																			Interpreter.dotOperator(
																																				context
																																					.vars
																																					.link_33,
																																				Type.atom(
																																					"platform",
																																				),
																																			),
																																		),
																																	]),
																																]),
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"span",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"hero-arrow-up-right h-4 w-4",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																		Type.tuple([
																																			Type.bitstring(
																																				"aria-hidden",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"true",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([]),
																																]),
																															]),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																											]);
																										},
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]);
																					},
																				},
																			],
																			context,
																		),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Interpreter.case(
																	Interpreter.case(
																		Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																			"has_items?/1"
																		](
																			Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																				"public_demographics/2"
																			](
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("profile"),
																				),
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("demographic_locale"),
																				),
																			),
																		),
																		[
																			{
																				match: Type.atom("false"),
																				guards: [],
																				body: (context) => {
																					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																						"disability_status_present?/1"
																					](
																						Interpreter.dotOperator(
																							context.vars.vars_0,
																							Type.atom("profile"),
																						),
																					);
																				},
																			},
																			{
																				match: Type.atom("true"),
																				guards: [],
																				body: (context) => {
																					return Type.atom("true");
																				},
																			},
																			{
																				match: Type.variablePattern("other_34"),
																				guards: [],
																				body: (context) => {
																					return Erlang["error/1"](
																						Type.tuple([
																							Type.atom("badbool"),
																							Type.atom("or"),
																							context.vars.other_34,
																						]),
																					);
																				},
																			},
																		],
																		context,
																	),
																	[
																		{
																			match: Type.variablePattern("x_35"),
																			guards: [
																				(context) =>
																					Erlang["orelse/2"](
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.x_35,
																								Type.atom("false"),
																							),
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.x_35,
																								Type.atom("nil"),
																							),
																						context,
																					),
																			],
																			body: (context) => {
																				return Type.atom("nil");
																			},
																		},
																		{
																			match: Type.matchPlaceholder(),
																			guards: [],
																			body: (context) => {
																				return Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n        "),
																					]),
																					Type.tuple([
																						Type.atom("element"),
																						Type.bitstring("section"),
																						Type.list([
																							Type.tuple([
																								Type.bitstring(
																									"aria-labelledby",
																								),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Type.bitstring([
																												Type.bitstringSegment(
																													Interpreter.dotOperator(
																														context.vars.vars_0,
																														Type.atom(
																															"surface_id",
																														),
																													),
																													{ type: "binary" },
																												),
																												Type.bitstringSegment(
																													Type.string(
																														"-details-heading",
																													),
																													{ type: "binary" },
																												),
																											]),
																										]),
																									]),
																								]),
																							]),
																						]),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("h2"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("id"),
																										Type.list([
																											Type.tuple([
																												Type.atom("expression"),
																												Type.tuple([
																													Type.bitstring([
																														Type.bitstringSegment(
																															Interpreter.dotOperator(
																																context.vars
																																	.vars_0,
																																Type.atom(
																																	"surface_id",
																																),
																															),
																															{
																																type: "binary",
																															},
																														),
																														Type.bitstringSegment(
																															Type.string(
																																"-details-heading",
																															),
																															{
																																type: "binary",
																															},
																														),
																													]),
																												]),
																											]),
																										]),
																									]),
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"text-base font-black text-app-ink",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("expression"),
																										Type.tuple([
																											Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																												"personal_details_heading/0"
																											](),
																										]),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n          "),
																							]),
																							Type.tuple([
																								Type.atom("element"),
																								Type.bitstring("dl"),
																								Type.list([
																									Type.tuple([
																										Type.bitstring("class"),
																										Type.list([
																											Type.tuple([
																												Type.atom("text"),
																												Type.bitstring(
																													"mt-3 space-y-2",
																												),
																											]),
																										]),
																									]),
																								]),
																								Type.list([
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.case(
																										Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																											"disability_status_present?/1"
																										](
																											Interpreter.dotOperator(
																												context.vars.vars_0,
																												Type.atom("profile"),
																											),
																										),
																										[
																											{
																												match:
																													Type.variablePattern(
																														"x_36",
																													),
																												guards: [
																													(context) =>
																														Erlang["orelse/2"](
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_36,
																																	Type.atom(
																																		"false",
																																	),
																																),
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_36,
																																	Type.atom(
																																		"nil",
																																	),
																																),
																															context,
																														),
																												],
																												body: (context) => {
																													return Type.atom(
																														"nil",
																													);
																												},
																											},
																											{
																												match:
																													Type.matchPlaceholder(),
																												guards: [],
																												body: (context) => {
																													return Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n              ",
																															),
																														]),
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"div",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"rounded-md border border-app-border bg-app-surface-muted p-3",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"dt",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"Disability status",
																																			),
																																		]),
																																	]),
																																]),
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"dd",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"mt-1 text-sm font-semibold text-app-ink",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																						"disability_status_label/1"
																																					](
																																						Interpreter.dotOperator(
																																							Interpreter.dotOperator(
																																								context
																																									.vars
																																									.vars_0,
																																								Type.atom(
																																									"profile",
																																								),
																																							),
																																							Type.atom(
																																								"has_limiting_disability",
																																							),
																																						),
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n            ",
																															),
																														]),
																													]);
																												},
																											},
																										],
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.case(
																										Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																											"disability_category_present?/1"
																										](
																											Interpreter.dotOperator(
																												context.vars.vars_0,
																												Type.atom("profile"),
																											),
																										),
																										[
																											{
																												match:
																													Type.variablePattern(
																														"x_37",
																													),
																												guards: [
																													(context) =>
																														Erlang["orelse/2"](
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_37,
																																	Type.atom(
																																		"false",
																																	),
																																),
																															(context) =>
																																Erlang["=:=/2"](
																																	context.vars
																																		.x_37,
																																	Type.atom(
																																		"nil",
																																	),
																																),
																															context,
																														),
																												],
																												body: (context) => {
																													return Type.atom(
																														"nil",
																													);
																												},
																											},
																											{
																												match:
																													Type.matchPlaceholder(),
																												guards: [],
																												body: (context) => {
																													return Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n              ",
																															),
																														]),
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"div",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"rounded-md border border-app-border bg-app-surface-muted p-3",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"dt",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																						"disability_badge/0"
																																					](),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																																Type.tuple([
																																	Type.atom(
																																		"element",
																																	),
																																	Type.bitstring(
																																		"dd",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.bitstring(
																																				"class",
																																			),
																																			Type.list(
																																				[
																																					Type.tuple(
																																						[
																																							Type.atom(
																																								"text",
																																							),
																																							Type.bitstring(
																																								"mt-1 text-sm font-semibold text-app-ink",
																																							),
																																						],
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"expression",
																																			),
																																			Type.tuple(
																																				[
																																					Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																																						"disability_label/1"
																																					](
																																						Interpreter.dotOperator(
																																							Interpreter.dotOperator(
																																								context
																																									.vars
																																									.vars_0,
																																								Type.atom(
																																									"profile",
																																								),
																																							),
																																							Type.atom(
																																								"disability_category",
																																							),
																																						),
																																					),
																																				],
																																			),
																																		]),
																																	]),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"\n            ",
																															),
																														]),
																													]);
																												},
																											},
																										],
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n            ",
																										),
																									]),
																									Interpreter.comprehension(
																										[
																											{
																												match:
																													Type.variablePattern(
																														"detail_38",
																													),
																												guards: [],
																												body: (context) =>
																													Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
																														"public_demographics/2"
																													](
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"profile",
																															),
																														),
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"demographic_locale",
																															),
																														),
																													),
																											},
																										],
																										[],
																										Type.list([]),
																										false,
																										(context) => {
																											return Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n              ",
																													),
																												]),
																												Type.tuple([
																													Type.atom("element"),
																													Type.bitstring("div"),
																													Type.list([
																														Type.tuple([
																															Type.bitstring(
																																"class",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"text",
																																	),
																																	Type.bitstring(
																																		"rounded-md border border-app-border bg-app-surface-muted p-3",
																																	),
																																]),
																															]),
																														]),
																													]),
																													Type.list([
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"dt",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"text-xs font-bold uppercase tracking-[0.12em] text-app-muted",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"expression",
																																	),
																																	Type.tuple([
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.detail_38,
																																			Type.atom(
																																				"label",
																																			),
																																		),
																																	]),
																																]),
																															]),
																														]),
																														Type.tuple([
																															Type.atom(
																																"element",
																															),
																															Type.bitstring(
																																"dd",
																															),
																															Type.list([
																																Type.tuple([
																																	Type.bitstring(
																																		"class",
																																	),
																																	Type.list([
																																		Type.tuple([
																																			Type.atom(
																																				"text",
																																			),
																																			Type.bitstring(
																																				"mt-1 text-sm font-semibold text-app-ink",
																																			),
																																		]),
																																	]),
																																]),
																															]),
																															Type.list([
																																Type.tuple([
																																	Type.atom(
																																		"expression",
																																	),
																																	Type.tuple([
																																		Interpreter.dotOperator(
																																			context
																																				.vars
																																				.detail_38,
																																			Type.atom(
																																				"value",
																																			),
																																		),
																																	]),
																																]),
																															]),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																											]);
																										},
																										context,
																									),
																									Type.tuple([
																										Type.atom("text"),
																										Type.bitstring(
																											"\n          ",
																										),
																									]),
																								]),
																							]),
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("\n        "),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n      "),
																					]),
																				]);
																			},
																		},
																	],
																	context,
																),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n    "),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"disability_status_label",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring("Yes");
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring("No");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"no_records_label",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.empty"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"verification_label",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("record_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Elixir_Enum["reject/2"](
							Type.list([
								Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
									"record_state/1"
								](
									Elixir_Map["get/2"](
										context.vars.record_0,
										Type.atom("verification_level"),
									),
								),
								Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
									"record_state/1"
								](
									Elixir_Map["get/2"](
										context.vars.record_0,
										Type.atom("verification_status"),
									),
								),
							]),
							Type.anonymousFunction(
								1,
								[
									{
										params: (context) => [Type.variablePattern("$108")],
										guards: [],
										body: (context) => {
											return Erlang["==/2"](
												context.vars["$108"],
												Type.atom("nil"),
											);
										},
									},
								],
								context,
							),
						),
						[
							{
								match: Type.list([]),
								guards: [],
								body: (context) => {
									return Type.atom("nil");
								},
							},
							{
								match: Type.variablePattern("labels_2"),
								guards: [],
								body: (context) => {
									return Elixir_Enum["join/2"](
										context.vars.labels_2,
										Type.bitstring(" · "),
									);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"record_state",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_atom/1"](context.vars.value_0)],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"humanize/1"
					](Erlang["atom_to_binary/1"](context.vars.value_0));
				},
			},
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Components_Social_ProfilePublicSurface[
						"humanize/1"
					](context.vars.value_0);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("show_profile_path"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("true")]),
								]),
							]),
							Type.tuple([
								Type.atom("primary_heading"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("true")]),
								]),
							]),
							Type.tuple([
								Type.atom("heading_id"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("profile-public-heading"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("surface_id"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("profile-public-surface"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("demographic_locale"),
								Type.atom("any"),
								Type.list([]),
							]),
							Type.tuple([
								Type.atom("display_name"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("profile"),
								Type.atom("map"),
								Type.list([]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"has_avatar?",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("profile_0")],
				guards: [],
				body: (context) => {
					return Interpreter.case(
						Erlang["is_binary/1"](
							Interpreter.dotOperator(
								context.vars.profile_0,
								Type.atom("avatar"),
							),
						),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.atom("false");
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Erlang["/=/2"](
										Interpreter.dotOperator(
											context.vars.profile_0,
											Type.atom("avatar"),
										),
										Type.bitstring(""),
									);
								},
							},
						],
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"summary_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.summary.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"format_year",
		1,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_integer/1"](context.vars.value_0)],
				body: (context) => {
					return Erlang["integer_to_binary/1"](context.vars.value_0);
				},
			},
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)],
				body: (context) => {
					return context.vars.value_0;
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("nil");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"personal_details_heading",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.personal_details.heading"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"disability_status_present?",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("profile_0")],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Interpreter.dotOperator(
							context.vars.profile_0,
							Type.atom("has_limiting_disability"),
						),
						Type.variablePattern("var_1"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Erlang["orelse/2"](
						(context) => Erlang["=:=/2"](context.vars.var_1, Type.atom("true")),
						(context) =>
							Erlang["=:=/2"](context.vars.var_1, Type.atom("false")),
						context,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
		"present?",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("value_0")],
				guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)],
				body: (context) => {
					return Erlang["/=/2"](
						Elixir_String["trim/1"](context.vars.value_0),
						Type.bitstring(""),
					);
				},
			},
			{
				params: (context) => [Type.matchPlaceholder()],
				guards: [],
				body: (context) => {
					return Type.atom("false");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ThemeToggle",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("icon_only"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("false")]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ThemeToggle",
		"button_class",
		1,
		"public",
		[
			{
				params: (context) => [Type.atom("true")],
				guards: [],
				body: (context) => {
					return Type.bitstring("btn btn-ghost btn-square min-h-11 min-w-11");
				},
			},
			{
				params: (context) => [Type.atom("false")],
				guards: [],
				body: (context) => {
					return Type.bitstring(
						"btn btn-ghost min-h-11 min-w-11 px-2.5 xl:px-4",
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ThemeToggle",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("button"),
											Type.list([
												Type.tuple([
													Type.bitstring("type"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("button"),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-theme-toggle"),
													Type.list([]),
												]),
												Type.tuple([
													Type.bitstring("data-switch-to-light-label"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](
																	Type.bitstring(
																		"shared.theme.switch_to_light",
																	),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-switch-to-dark-label"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](
																	Type.bitstring("shared.theme.switch_to_dark"),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-light-label"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](Type.bitstring("shared.theme.light")),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-dark-label"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](Type.bitstring("shared.theme.dark")),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("aria-label"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](
																	Type.bitstring("shared.theme.switch_to_dark"),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("title"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"text/1"
																](
																	Type.bitstring("shared.theme.switch_to_dark"),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("class"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																	"button_class/1"
																](
																	Interpreter.dotOperator(
																		context.vars.vars_0,
																		Type.atom("icon_only"),
																	),
																),
															]),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("span"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"hero-sun h-5 w-5 dark:hidden",
																	),
																]),
															]),
														]),
														Type.tuple([
															Type.bitstring("aria-hidden"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("true"),
																]),
															]),
														]),
													]),
													Type.list([]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("span"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"hero-moon hidden h-5 w-5 dark:block",
																	),
																]),
															]),
														]),
														Type.tuple([
															Type.bitstring("aria-hidden"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("true"),
																]),
															]),
														]),
													]),
													Type.list([]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Interpreter.case(
													Interpreter.case(
														Interpreter.dotOperator(
															context.vars.vars_0,
															Type.atom("icon_only"),
														),
														[
															{
																match: Type.variablePattern("x_1"),
																guards: [
																	(context) =>
																		Erlang["orelse/2"](
																			(context) =>
																				Erlang["=:=/2"](
																					context.vars.x_1,
																					Type.atom("false"),
																				),
																			(context) =>
																				Erlang["=:=/2"](
																					context.vars.x_1,
																					Type.atom("nil"),
																				),
																			context,
																		),
																],
																body: (context) => {
																	return Type.atom("true");
																},
															},
															{
																match: Type.matchPlaceholder(),
																guards: [],
																body: (context) => {
																	return Type.atom("false");
																},
															},
														],
														context,
													),
													[
														{
															match: Type.atom("false"),
															guards: [],
															body: (context) => {
																return Type.atom("nil");
															},
														},
														{
															match: Type.atom("true"),
															guards: [],
															body: (context) => {
																return Type.list([
																	Type.tuple([
																		Type.atom("element"),
																		Type.bitstring("span"),
																		Type.list([
																			Type.tuple([
																				Type.bitstring(
																					"data-theme-toggle-label",
																				),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.bitstring("class"),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring(
																							"hidden text-sm font-semibold xl:inline",
																						),
																					]),
																				]),
																			]),
																		]),
																		Type.list([
																			Type.tuple([
																				Type.atom("expression"),
																				Type.tuple([
																					Elixir_LeaplyWeb_Hologram_Components_ThemeToggle[
																						"text/1"
																					](
																						Type.bitstring("shared.theme.dark"),
																					),
																				]),
																			]),
																		]),
																	]),
																]);
															},
														},
													],
													context,
												),
												Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Components.ThemeToggle",
		"text",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("key_0")],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ThemeText["get/1"](
						context.vars.key_0,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Helpers.CatalogText",
		"get",
		2,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("domain_0"),
					Type.variablePattern("key_1"),
				],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_CatalogText["get/3"](
						context.vars.domain_0,
						context.vars.key_1,
						Type.list([]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Helpers.CatalogText",
		"get",
		3,
		"public",
		[
			{
				params: (context) => [
					Type.variablePattern("domain_0"),
					Type.variablePattern("key_1"),
					Type.variablePattern("bindings_2"),
				],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Elixir_Map["get/3"](
							Type.map([
								[
									Type.bitstring("listings"),
									Type.map([
										[
											Type.atom("plural"),
											Type.map([
												[
													Type.bitstring("listings.accounts.count.one"),
													Type.tuple([
														Type.bitstring("listings.accounts.count.other"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} account"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} accounts"),
															],
														]),
													]),
												],
												[
													Type.bitstring("listings.workspace.count.one"),
													Type.tuple([
														Type.bitstring("listings.workspace.count.other"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} listing"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} listings"),
															],
														]),
													]),
												],
											]),
										],
										[
											Type.atom("singular"),
											Type.map([
												[
													Type.bitstring(
														"applications.screening.attention_heading",
													),
													Type.bitstring(
														"Your screening eligibility needs attention",
													),
												],
												[
													Type.bitstring(
														"applications.screening.stable_status_help",
													),
													Type.bitstring(
														"Your application remains in review. Its status and the opportunity team's pipeline totals have not been changed.",
													),
												],
												[
													Type.bitstring("listings.accounts.account_fallback"),
													Type.bitstring("Account"),
												],
												[
													Type.bitstring(
														"listings.accounts.back_to_opportunities",
													),
													Type.bitstring("Back to opportunities"),
												],
												[
													Type.bitstring("listings.accounts.card_action"),
													Type.bitstring(
														"View listings and configure applicant screening",
													),
												],
												[
													Type.bitstring("listings.accounts.description"),
													Type.bitstring(
														"Choose an organisation to manage its listings, screening questions, and applicants.",
													),
												],
												[
													Type.bitstring("listings.accounts.empty.action"),
													Type.bitstring("Browse opportunities"),
												],
												[
													Type.bitstring("listings.accounts.empty.description"),
													Type.bitstring(
														"Accounts you own or help administer will appear here. Your applicant profile and opportunities remain available as normal.",
													),
												],
												[
													Type.bitstring("listings.accounts.empty.heading"),
													Type.bitstring("No managed accounts yet"),
												],
												[
													Type.bitstring("listings.accounts.eyebrow"),
													Type.bitstring("Publisher workspace"),
												],
												[
													Type.bitstring("listings.accounts.heading"),
													Type.bitstring("Managed accounts"),
												],
												[
													Type.bitstring("listings.accounts.page_title"),
													Type.bitstring("Accounts"),
												],
												[
													Type.bitstring("listings.common.breadcrumb"),
													Type.bitstring("Breadcrumb"),
												],
												[
													Type.bitstring("listings.common.cancel"),
													Type.bitstring("Cancel"),
												],
												[
													Type.bitstring("listings.common.language_missing"),
													Type.bitstring("Language not specified"),
												],
												[
													Type.bitstring("listings.common.locale_missing"),
													Type.bitstring("Locale not set"),
												],
												[
													Type.bitstring("listings.common.optional"),
													Type.bitstring("(optional)"),
												],
												[
													Type.bitstring("listings.common.profile_fallback"),
													Type.bitstring("Your profile"),
												],
												[
													Type.bitstring("listings.discovery.all_languages"),
													Type.bitstring("All languages"),
												],
												[
													Type.bitstring("listings.discovery.language"),
													Type.bitstring("Language"),
												],
												[
													Type.bitstring("listings.discovery.language_filter"),
													Type.bitstring("Language: %{locale}"),
												],
												[
													Type.bitstring("listings.flash.created"),
													Type.bitstring(
														"Screening scenario created. Add the questions applicants should answer.",
													),
												],
												[
													Type.bitstring("listings.flash.updated"),
													Type.bitstring("Listing details updated."),
												],
												[
													Type.bitstring("listings.form.back_to_account"),
													Type.bitstring("%{account} listings"),
												],
												[
													Type.bitstring("listings.form.description"),
													Type.bitstring(
														"Set the opportunity context used to test screening. New scenarios stay private as drafts until a separate publication workflow is added.",
													),
												],
												[
													Type.bitstring("listings.form.error.create"),
													Type.bitstring(
														"We could not create this listing. Review the highlighted fields.",
													),
												],
												[
													Type.bitstring("listings.form.error.update"),
													Type.bitstring(
														"We could not update this listing. Review the highlighted fields.",
													),
												],
												[
													Type.bitstring("listings.form.eyebrow.edit"),
													Type.bitstring("Listing details"),
												],
												[
													Type.bitstring("listings.form.eyebrow.new"),
													Type.bitstring("New draft listing"),
												],
												[
													Type.bitstring("listings.form.heading.edit"),
													Type.bitstring("Edit screening scenario"),
												],
												[
													Type.bitstring("listings.form.heading.new"),
													Type.bitstring("Create a new listing"),
												],
												[
													Type.bitstring("listings.form.kind.help"),
													Type.bitstring(
														"Used to present the scenario consistently across listing surfaces.",
													),
												],
												[
													Type.bitstring("listings.form.kind.label"),
													Type.bitstring("Opportunity type"),
												],
												[
													Type.bitstring("listings.form.locale.help"),
													Type.bitstring(
														"Choose the language and region applicants will use for this listing.",
													),
												],
												[
													Type.bitstring("listings.form.locale.invalid_option"),
													Type.bitstring("Invalid locale: %{locale}"),
												],
												[
													Type.bitstring("listings.form.locale.label"),
													Type.bitstring("Listing locale"),
												],
												[
													Type.bitstring(
														"listings.form.locale_locked.description",
													),
													Type.bitstring(
														"This listing already owns or uses screening questions. Keep this locale aligned, or create a new scenario for another locale.",
													),
												],
												[
													Type.bitstring("listings.form.locale_locked.heading"),
													Type.bitstring("Locale locked."),
												],
												[
													Type.bitstring(
														"listings.form.locale_notice.description",
													),
													Type.bitstring(
														"The listing locale automatically filters reusable questions and keeps applicant-facing copy aligned. Create another listing when you need to test a different locale.",
													),
												],
												[
													Type.bitstring("listings.form.locale_notice.heading"),
													Type.bitstring("One locale per scenario"),
												],
												[
													Type.bitstring("listings.form.page_title"),
													Type.bitstring("Listing details — Leaply"),
												],
												[
													Type.bitstring("listings.form.submit.edit"),
													Type.bitstring("Save listing"),
												],
												[
													Type.bitstring("listings.form.submit.new"),
													Type.bitstring("Create and configure screening"),
												],
												[
													Type.bitstring("listings.form.summary.help"),
													Type.bitstring(
														"Add a short note about the applicant scenario or screening behavior you are testing.",
													),
												],
												[
													Type.bitstring("listings.form.summary.label"),
													Type.bitstring("Scenario summary"),
												],
												[
													Type.bitstring("listings.form.title.help"),
													Type.bitstring(
														"Use a descriptive name so you can distinguish this scenario in the workspace.",
													),
												],
												[
													Type.bitstring("listings.form.title.label"),
													Type.bitstring("Listing title"),
												],
												[
													Type.bitstring("listings.kind.bursary"),
													Type.bitstring("Bursary"),
												],
												[
													Type.bitstring("listings.kind.event"),
													Type.bitstring("Event"),
												],
												[
													Type.bitstring("listings.kind.internship"),
													Type.bitstring("Internship"),
												],
												[
													Type.bitstring("listings.kind.job"),
													Type.bitstring("Job"),
												],
												[
													Type.bitstring("listings.kind.learnership"),
													Type.bitstring("Learnership"),
												],
												[
													Type.bitstring("listings.kind.opportunity"),
													Type.bitstring("Opportunity"),
												],
												[
													Type.bitstring("listings.kind.other"),
													Type.bitstring("Other"),
												],
												[
													Type.bitstring("listings.kind.scholarship"),
													Type.bitstring("Scholarship"),
												],
												[
													Type.bitstring("listings.kind.survey"),
													Type.bitstring("Survey"),
												],
												[
													Type.bitstring("listings.shell.managed_listings"),
													Type.bitstring("Managed listings"),
												],
												[
													Type.bitstring("listings.state.archived"),
													Type.bitstring("Archived"),
												],
												[
													Type.bitstring("listings.state.closed"),
													Type.bitstring("Closed"),
												],
												[
													Type.bitstring("listings.state.complete"),
													Type.bitstring("Complete"),
												],
												[
													Type.bitstring("listings.state.draft"),
													Type.bitstring("Draft"),
												],
												[
													Type.bitstring("listings.state.open"),
													Type.bitstring("Open"),
												],
												[
													Type.bitstring("listings.state.published"),
													Type.bitstring("Published"),
												],
												[
													Type.bitstring("listings.state.unknown"),
													Type.bitstring("Unknown"),
												],
												[
													Type.bitstring("listings.validation.locale.invalid"),
													Type.bitstring("is invalid"),
												],
												[
													Type.bitstring("listings.validation.locale.locked"),
													Type.bitstring(
														"can't be changed after screening questions are configured",
													),
												],
												[
													Type.bitstring("listings.workspace.back_to_accounts"),
													Type.bitstring("Managed accounts"),
												],
												[
													Type.bitstring("listings.workspace.description"),
													Type.bitstring(
														"Configure the questions applicants answer, then review submitted applications when tracking is enabled.",
													),
												],
												[
													Type.bitstring("listings.workspace.edit_action"),
													Type.bitstring("Edit listing"),
												],
												[
													Type.bitstring("listings.workspace.empty.action"),
													Type.bitstring("Create your first listing"),
												],
												[
													Type.bitstring(
														"listings.workspace.empty.description",
													),
													Type.bitstring(
														"Create a private scenario, choose its locale, then build the screening flow applicants will complete.",
													),
												],
												[
													Type.bitstring("listings.workspace.empty.heading"),
													Type.bitstring("No listings for this account yet"),
												],
												[
													Type.bitstring("listings.workspace.eyebrow"),
													Type.bitstring("Listing management"),
												],
												[
													Type.bitstring("listings.workspace.heading"),
													Type.bitstring("%{account} — Listings"),
												],
												[
													Type.bitstring("listings.workspace.new_action"),
													Type.bitstring("New Listing"),
												],
												[
													Type.bitstring("listings.workspace.page_title"),
													Type.bitstring("Account Listings"),
												],
												[
													Type.bitstring(
														"listings.workspace.screening.access_required",
													),
													Type.bitstring(
														"A listing manager or account administrator must configure screening.",
													),
												],
												[
													Type.bitstring("listings.workspace.screening.action"),
													Type.bitstring("Configure screening"),
												],
												[
													Type.bitstring(
														"listings.workspace.screening.description",
													),
													Type.bitstring(
														"Build the question flow applicants complete for this listing.",
													),
												],
												[
													Type.bitstring(
														"listings.workspace.screening.heading",
													),
													Type.bitstring("Screening questions"),
												],
												[
													Type.bitstring("listings.workspace.summary_empty"),
													Type.bitstring(
														"No listing summary has been added yet.",
													),
												],
												[
													Type.bitstring(
														"listings.workspace.tracker.access_required",
													),
													Type.bitstring(
														"Listing manager or screener access is required to view applicants",
													),
												],
												[
													Type.bitstring("listings.workspace.tracker.action"),
													Type.bitstring("View applicants"),
												],
												[
													Type.bitstring("listings.workspace.tracker.disabled"),
													Type.bitstring(
														"Applicant tracking is not enabled for this listing",
													),
												],
												[
													Type.bitstring("requirements.generic.failure"),
													Type.bitstring(
														"You don't meet one of this opportunity's eligibility requirements.",
													),
												],
												[
													Type.bitstring("requirements.generic.unverified"),
													Type.bitstring(
														"We couldn't verify one of this opportunity's eligibility requirements yet.",
													),
												],
												[
													Type.bitstring(
														"requirements.minimum-qualification.failure",
													),
													Type.bitstring(
														"You don't meet this opportunity's minimum qualification requirement.",
													),
												],
												[
													Type.bitstring(
														"requirements.minimum-qualification.unverified",
													),
													Type.bitstring(
														"We couldn't verify your minimum qualification yet.",
													),
												],
												[
													Type.bitstring(
														"requirements.open-to-opportunities.failure",
													),
													Type.bitstring(
														"Your profile must show that you are open to opportunities.",
													),
												],
												[
													Type.bitstring(
														"requirements.open-to-opportunities.unverified",
													),
													Type.bitstring(
														"We couldn't verify whether your profile shows that you are open to opportunities yet.",
													),
												],
											]),
										],
									]),
								],
								[
									Type.bitstring("screening"),
									Type.map([
										[
											Type.atom("plural"),
											Type.map([
												[
													Type.bitstring("screening.admin.page.one"),
													Type.tuple([
														Type.bitstring("screening.admin.page.many"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("Page %{page} of %{count}"),
															],
															[
																Type.integer(1n),
																Type.bitstring("Page %{page} of %{count}"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.available_result"),
													Type.tuple([
														Type.bitstring("screening.count.available_results"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} available result"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} available results"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.configured_question"),
													Type.tuple([
														Type.bitstring(
															"screening.count.configured_questions",
														),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} configured question"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} configured questions"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.group"),
													Type.tuple([
														Type.bitstring("screening.count.groups"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} group"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} groups"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.matching_question"),
													Type.tuple([
														Type.bitstring(
															"screening.count.matching_questions",
														),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} matching question"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} matching questions"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.question"),
													Type.tuple([
														Type.bitstring("screening.count.questions"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} question"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} questions"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.count.total_question"),
													Type.tuple([
														Type.bitstring("screening.count.total_questions"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} total"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} total"),
															],
														]),
													]),
												],
												[
													Type.bitstring("screening.eligibility.rule_count"),
													Type.tuple([
														Type.bitstring("screening.eligibility.rules_count"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} eligibility rule"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} eligibility rules"),
															],
														]),
													]),
												],
											]),
										],
										[
											Type.atom("singular"),
											Type.map([
												[
													Type.bitstring("screening.action.add"),
													Type.bitstring("Add"),
												],
												[
													Type.bitstring("screening.action.add_group"),
													Type.bitstring("Add group"),
												],
												[
													Type.bitstring("screening.action.adding"),
													Type.bitstring("Adding…"),
												],
												[
													Type.bitstring("screening.action.apply"),
													Type.bitstring("Apply"),
												],
												[
													Type.bitstring("screening.action.clear"),
													Type.bitstring("Clear"),
												],
												[
													Type.bitstring("screening.action.create_and_add"),
													Type.bitstring("Create and add question"),
												],
												[
													Type.bitstring("screening.action.creating"),
													Type.bitstring("Creating…"),
												],
												[
													Type.bitstring(
														"screening.action.deactivate_question",
													),
													Type.bitstring("Deactivate question"),
												],
												[
													Type.bitstring("screening.action.delete_group"),
													Type.bitstring("Delete group"),
												],
												[
													Type.bitstring("screening.action.discard_draft"),
													Type.bitstring("Discard draft"),
												],
												[
													Type.bitstring("screening.action.drag_question"),
													Type.bitstring("Reorder question"),
												],
												[
													Type.bitstring("screening.action.make_primary"),
													Type.bitstring("Make primary"),
												],
												[
													Type.bitstring("screening.action.manage_eligibility"),
													Type.bitstring("Manage eligibility"),
												],
												[
													Type.bitstring(
														"screening.action.manage_system_questions",
													),
													Type.bitstring("Manage system questions"),
												],
												[
													Type.bitstring("screening.action.move_down"),
													Type.bitstring("Move down"),
												],
												[
													Type.bitstring("screening.action.move_up"),
													Type.bitstring("Move up"),
												],
												[
													Type.bitstring(
														"screening.action.reactivate_question",
													),
													Type.bitstring("Reactivate question"),
												],
												[
													Type.bitstring("screening.action.remove"),
													Type.bitstring("Remove"),
												],
												[
													Type.bitstring("screening.action.remove_from_group"),
													Type.bitstring("Remove question from this group"),
												],
												[
													Type.bitstring("screening.action.save_changes"),
													Type.bitstring("Save changes"),
												],
												[
													Type.bitstring(
														"screening.action.save_prompt_settings",
													),
													Type.bitstring("Save settings"),
												],
												[
													Type.bitstring("screening.action.save_wording"),
													Type.bitstring("Save wording"),
												],
												[
													Type.bitstring("screening.action.saving"),
													Type.bitstring("Saving…"),
												],
												[
													Type.bitstring("screening.action.search"),
													Type.bitstring("Search"),
												],
												[
													Type.bitstring("screening.admin.add_option"),
													Type.bitstring("Add option"),
												],
												[
													Type.bitstring("screening.admin.additional_settings"),
													Type.bitstring("Additional saved settings"),
												],
												[
													Type.bitstring(
														"screening.admin.additional_settings_help",
													),
													Type.bitstring(
														"These settings are preserved when you save. The current answer control may not use them.",
													),
												],
												[
													Type.bitstring("screening.admin.all_locales"),
													Type.bitstring("All locales"),
												],
												[
													Type.bitstring("screening.admin.availability"),
													Type.bitstring("Availability for new question sets"),
												],
												[
													Type.bitstring("screening.admin.availability_help"),
													Type.bitstring(
														"Inactive questions remain usable in existing question sets and keep their saved answers. Reactivating offers the same question for new sets again.",
													),
												],
												[
													Type.bitstring("screening.admin.back_to_app"),
													Type.bitstring("Back to Leaply"),
												],
												[
													Type.bitstring("screening.admin.cancel"),
													Type.bitstring("Cancel"),
												],
												[
													Type.bitstring("screening.admin.choice_options_help"),
													Type.bitstring(
														"One option per line as stable_key | Applicant label.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.choice_options_placeholder",
													),
													Type.bitstring("yes | Yes\nno | No"),
												],
												[
													Type.bitstring("screening.admin.choice_rows_help"),
													Type.bitstring(
														"Add an applicant-facing label and a unique, stable key for each option. Suggested keys can be edited before saving. Existing labels and keys stay fixed once the question has attachments or answers.",
													),
												],
												[
													Type.bitstring("screening.admin.choose_locale"),
													Type.bitstring("Choose an available locale"),
												],
												[
													Type.bitstring("screening.admin.clear_filters"),
													Type.bitstring("Clear filters"),
												],
												[
													Type.bitstring("screening.admin.config.max"),
													Type.bitstring("Maximum value"),
												],
												[
													Type.bitstring("screening.admin.config.max_length"),
													Type.bitstring("Maximum characters"),
												],
												[
													Type.bitstring("screening.admin.config.min"),
													Type.bitstring("Minimum value"),
												],
												[
													Type.bitstring("screening.admin.config.min_length"),
													Type.bitstring("Minimum characters"),
												],
												[
													Type.bitstring(
														"screening.admin.configuration_edit_help",
													),
													Type.bitstring(
														"Leave a limit blank to use the default. New limits apply when answers are submitted or updated; existing saved answers stay unchanged.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.configuration_heading",
													),
													Type.bitstring("View saved configuration"),
												],
												[
													Type.bitstring("screening.admin.confirm_deactivate"),
													Type.bitstring("Confirm deactivation"),
												],
												[
													Type.bitstring("screening.admin.create_eyebrow"),
													Type.bitstring("New library item"),
												],
												[
													Type.bitstring("screening.admin.create_heading"),
													Type.bitstring("Create system question"),
												],
												[
													Type.bitstring("screening.admin.create_locale_help"),
													Type.bitstring(
														"Choose the locale this question is authored for. Listing teams only see questions matching their listing locale.",
													),
												],
												[
													Type.bitstring("screening.admin.description"),
													Type.bitstring(
														"Create reusable, locale-specific questions that listing teams can discover before authoring duplicates.",
													),
												],
												[
													Type.bitstring("screening.admin.detail_heading"),
													Type.bitstring("System question"),
												],
												[
													Type.bitstring("screening.admin.draft_failed"),
													Type.bitstring(
														"The form could not be updated. Your input is still here. Try the form action again when your connection returns.",
													),
												],
												[
													Type.bitstring("screening.admin.edit_heading"),
													Type.bitstring("Edit question definition"),
												],
												[
													Type.bitstring("screening.admin.edit_wording"),
													Type.bitstring("Edit question"),
												],
												[
													Type.bitstring("screening.admin.empty_help"),
													Type.bitstring(
														"Broaden the locale, status, or search filters, or create a new library item.",
													),
												],
												[
													Type.bitstring("screening.admin.empty_library"),
													Type.bitstring("Your system library is empty"),
												],
												[
													Type.bitstring("screening.admin.empty_library_help"),
													Type.bitstring(
														"Create the first reusable question for listing teams to use across Leaply.",
													),
												],
												[
													Type.bitstring("screening.admin.empty_title"),
													Type.bitstring(
														"No system questions match these filters",
													),
												],
												[
													Type.bitstring("screening.admin.eyebrow"),
													Type.bitstring("Shared screening library"),
												],
												[
													Type.bitstring("screening.admin.filters_eyebrow"),
													Type.bitstring("Library controls"),
												],
												[
													Type.bitstring("screening.admin.filters_heading"),
													Type.bitstring("Find system questions"),
												],
												[
													Type.bitstring("screening.admin.filters_help"),
													Type.bitstring(
														"These filters only change this admin library. They never change your profile or application language.",
													),
												],
												[
													Type.bitstring("screening.admin.heading"),
													Type.bitstring("System questions"),
												],
												[
													Type.bitstring("screening.admin.help_placeholder"),
													Type.bitstring(
														"Add context shown below the question",
													),
												],
												[
													Type.bitstring("screening.admin.home"),
													Type.bitstring("Leaply admin"),
												],
												[
													Type.bitstring("screening.admin.identity_heading"),
													Type.bitstring("Fixed answer identity"),
												],
												[
													Type.bitstring("screening.admin.length_limits"),
													Type.bitstring("Character limits"),
												],
												[
													Type.bitstring("screening.admin.library_heading"),
													Type.bitstring("Reusable questions"),
												],
												[
													Type.bitstring("screening.admin.locale_label"),
													Type.bitstring("Locale"),
												],
												[
													Type.bitstring("screening.admin.navigation"),
													Type.bitstring("Employee navigation"),
												],
												[
													Type.bitstring("screening.admin.new_question"),
													Type.bitstring("New question"),
												],
												[
													Type.bitstring("screening.admin.no_configuration"),
													Type.bitstring(
														"This answer type has no additional settings.",
													),
												],
												[
													Type.bitstring("screening.admin.number_limits"),
													Type.bitstring("Number limits"),
												],
												[
													Type.bitstring("screening.admin.operations"),
													Type.bitstring("Leaply Operations"),
												],
												[
													Type.bitstring("screening.admin.option_key"),
													Type.bitstring("Option %{number} key"),
												],
												[
													Type.bitstring("screening.admin.option_label"),
													Type.bitstring("Option %{number} label"),
												],
												[
													Type.bitstring("screening.admin.page_title"),
													Type.bitstring("System screening questions"),
												],
												[
													Type.bitstring("screening.admin.pending"),
													Type.bitstring("Submitting… Please wait."),
												],
												[
													Type.bitstring("screening.admin.preview_empty_label"),
													Type.bitstring("Your question will appear here"),
												],
												[
													Type.bitstring("screening.admin.preview_heading"),
													Type.bitstring("Preview"),
												],
												[
													Type.bitstring("screening.admin.preview_help"),
													Type.bitstring(
														"Try the question as an applicant would see it. Preview answers are not saved. Whether an answer is required is set by the listing.",
													),
												],
												[
													Type.bitstring("screening.admin.questions"),
													Type.bitstring("Questions"),
												],
												[
													Type.bitstring("screening.admin.rejected_response"),
													Type.bitstring(
														"The question was not saved because validation failed. Your input is still here. Submit again to review the validation errors.",
													),
												],
												[
													Type.bitstring("screening.admin.remove"),
													Type.bitstring("Remove"),
												],
												[
													Type.bitstring("screening.admin.remove_option"),
													Type.bitstring("Remove option %{number}"),
												],
												[
													Type.bitstring("screening.admin.search_label"),
													Type.bitstring("Search"),
												],
												[
													Type.bitstring("screening.admin.search_placeholder"),
													Type.bitstring("Wording, help text, or question UID"),
												],
												[
													Type.bitstring("screening.admin.settings_heading"),
													Type.bitstring("Answer settings"),
												],
												[
													Type.bitstring("screening.admin.shared_edit_help"),
													Type.bitstring(
														"Changes are shared wherever this question is used. Review the preview before saving.",
													),
												],
												[
													Type.bitstring("screening.admin.skip_to_content"),
													Type.bitstring("Skip to main content"),
												],
												[
													Type.bitstring("screening.admin.stable"),
													Type.bitstring("stable"),
												],
												[
													Type.bitstring(
														"screening.admin.stable_identity_help",
													),
													Type.bitstring(
														"Locale and answer type are fixed after creation. Answer settings can be edited while preserving existing answers and choice keys.",
													),
												],
												[
													Type.bitstring("screening.admin.status_active"),
													Type.bitstring("Active only"),
												],
												[
													Type.bitstring("screening.admin.status_all"),
													Type.bitstring("Active and inactive"),
												],
												[
													Type.bitstring("screening.admin.status_inactive"),
													Type.bitstring("Inactive only"),
												],
												[
													Type.bitstring("screening.admin.status_label"),
													Type.bitstring("Status"),
												],
												[
													Type.bitstring("screening.admin.uid"),
													Type.bitstring("Question UID"),
												],
												[
													Type.bitstring("screening.admin.unknown_outcome"),
													Type.bitstring(
														"We could not confirm the result. Your input is still here, and submitting again is blocked. Use the Questions breadcrumb to check whether the change was saved before starting another request.",
													),
												],
												[
													Type.bitstring("screening.admin.update_answer_type"),
													Type.bitstring("Update answer type"),
												],
												[
													Type.bitstring("screening.admin.update_preview"),
													Type.bitstring("Update preview"),
												],
												[
													Type.bitstring(
														"screening.admin.validation.configuration_bounds",
													),
													Type.bitstring(
														"Enter limits within the supported range, with the minimum no greater than the maximum.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.configuration_fields",
													),
													Type.bitstring(
														"Only the supported settings for this answer type can be edited.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.configuration_in_use",
													),
													Type.bitstring(
														"This question is in use. You can add options, but keep existing options and their keys, labels, and order unchanged.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.configuration_limits",
													),
													Type.bitstring(
														"Enter valid limits: the minimum must not exceed the maximum or the supported range.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.configuration_unsupported",
													),
													Type.bitstring(
														"Additional saved settings cannot be changed through this editor.",
													),
												],
												[
													Type.bitstring("screening.admin.validation.heading"),
													Type.bitstring("Review the question details"),
												],
												[
													Type.bitstring("screening.admin.validation.intent"),
													Type.bitstring(
														"Choose a form action to continue. The question has not been saved.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.locale_unavailable",
													),
													Type.bitstring(
														"Choose a locale available in this library.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.option_key",
													),
													Type.bitstring(
														"Enter a non-empty key that is unique in this question.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.option_label",
													),
													Type.bitstring("Enter an option label."),
												],
												[
													Type.bitstring("screening.admin.validation.stale"),
													Type.bitstring(
														"This question changed while you were editing. Reload it and review the latest values before saving.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.stale_question",
													),
													Type.bitstring(
														"This question changed since you opened it. Reload the question and review the latest values before saving.",
													),
												],
												[
													Type.bitstring(
														"screening.admin.validation.system_question_required",
													),
													Type.bitstring("Select a system question to edit."),
												],
												[
													Type.bitstring("screening.admin.wording_placeholder"),
													Type.bitstring("What should every applicant answer?"),
												],
												[
													Type.bitstring("screening.answer_kind.boolean"),
													Type.bitstring("Yes or no"),
												],
												[
													Type.bitstring("screening.answer_kind.choice"),
													Type.bitstring("Multiple choice"),
												],
												[
													Type.bitstring("screening.answer_kind.date"),
													Type.bitstring("Date"),
												],
												[
													Type.bitstring("screening.answer_kind.email"),
													Type.bitstring("Email address"),
												],
												[
													Type.bitstring("screening.answer_kind.file"),
													Type.bitstring("File upload"),
												],
												[
													Type.bitstring(
														"screening.answer_kind.file_non_primary",
													),
													Type.bitstring(
														"File upload (non-primary flows only)",
													),
												],
												[
													Type.bitstring("screening.answer_kind.number"),
													Type.bitstring("Number"),
												],
												[
													Type.bitstring("screening.answer_kind.phone"),
													Type.bitstring("Phone number"),
												],
												[
													Type.bitstring("screening.answer_kind.string"),
													Type.bitstring("Single-line text"),
												],
												[
													Type.bitstring("screening.answer_kind.text"),
													Type.bitstring("Paragraph text"),
												],
												[
													Type.bitstring("screening.answer_kind.unknown"),
													Type.bitstring("Question"),
												],
												[
													Type.bitstring("screening.choice.fallback"),
													Type.bitstring("Choice"),
												],
												[
													Type.bitstring("screening.common.no"),
													Type.bitstring("No"),
												],
												[
													Type.bitstring("screening.common.optional"),
													Type.bitstring("optional"),
												],
												[
													Type.bitstring("screening.common.yes"),
													Type.bitstring("Yes"),
												],
												[
													Type.bitstring("screening.condition.always_show"),
													Type.bitstring("Always show this question"),
												],
												[
													Type.bitstring(
														"screening.condition.available_choice_keys",
													),
													Type.bitstring("Available choice keys"),
												],
												[
													Type.bitstring(
														"screening.condition.boolean_placeholder",
													),
													Type.bitstring(
														"For yes/no questions, use true or false",
													),
												],
												[
													Type.bitstring("screening.condition.choose_source"),
													Type.bitstring("Choose an earlier question first"),
												],
												[
													Type.bitstring("screening.condition.choose_value"),
													Type.bitstring("Choose a value"),
												],
												[
													Type.bitstring(
														"screening.condition.earlier_question",
													),
													Type.bitstring("Earlier question"),
												],
												[
													Type.bitstring("screening.condition.no_value_needed"),
													Type.bitstring(
														"No comparison value is needed for this rule.",
													),
												],
												[
													Type.bitstring("screening.condition.operator_label"),
													Type.bitstring("Comparison"),
												],
												[
													Type.bitstring("screening.condition.saved_heading"),
													Type.bitstring("When applicants see this question"),
												],
												[
													Type.bitstring("screening.condition.saved_summary"),
													Type.bitstring(
														"Earlier question: %{question}. %{condition}",
													),
												],
												[
													Type.bitstring("screening.condition.source_label"),
													Type.bitstring("Show only after"),
												],
												[
													Type.bitstring("screening.condition.summary.always"),
													Type.bitstring("Always shown"),
												],
												[
													Type.bitstring(
														"screening.condition.summary.conditional",
													),
													Type.bitstring("Conditional visibility"),
												],
												[
													Type.bitstring(
														"screening.condition.summary.contains",
													),
													Type.bitstring(
														"Shown when the earlier answer contains %{value}",
													),
												],
												[
													Type.bitstring("screening.condition.summary.eq"),
													Type.bitstring(
														"Shown when the earlier answer is %{value}",
													),
												],
												[
													Type.bitstring("screening.condition.summary.exists"),
													Type.bitstring(
														"Shown after the selected question is answered",
													),
												],
												[
													Type.bitstring("screening.condition.summary.in"),
													Type.bitstring(
														"Shown when the earlier answer is one of %{value}",
													),
												],
												[
													Type.bitstring("screening.condition.summary.neq"),
													Type.bitstring(
														"Shown when the earlier answer is not %{value}",
													),
												],
												[
													Type.bitstring(
														"screening.condition.summary.not_exists",
													),
													Type.bitstring(
														"Shown while the selected question is unanswered",
													),
												],
												[
													Type.bitstring("screening.condition.value_help"),
													Type.bitstring(
														"For “one of”, separate values with commas. Choice rules use the stable keys shown below. “Has any answer” and “has no answer” do not need a value.",
													),
												],
												[
													Type.bitstring("screening.condition.value_label"),
													Type.bitstring("Comparison value"),
												],
												[
													Type.bitstring(
														"screening.condition.value_placeholder",
													),
													Type.bitstring("Enter the comparison value"),
												],
												[
													Type.bitstring(
														"screening.condition.values_placeholder",
													),
													Type.bitstring("Separate typed values with commas"),
												],
												[
													Type.bitstring(
														"screening.condition.visibility_heading",
													),
													Type.bitstring("When to show this question"),
												],
												[
													Type.bitstring("screening.confirm.group_delete"),
													Type.bitstring(
														"Delete the %{group} group and its %{question_count}? Reusable questions stay available.",
													),
												],
												[
													Type.bitstring(
														"screening.confirm.group_delete_primary",
													),
													Type.bitstring(
														"Delete the primary %{group} group and its %{question_count}? Future applications will have no saved primary screening flow until you configure one. Reusable questions stay available.",
													),
												],
												[
													Type.bitstring("screening.confirm.group_discard"),
													Type.bitstring(
														"Discard the %{group} draft group? It has not been saved yet.",
													),
												],
												[
													Type.bitstring("screening.confirm.group_primary"),
													Type.bitstring(
														"Make %{group} the primary application flow? The current primary group will be replaced for future applicants.",
													),
												],
												[
													Type.bitstring("screening.confirm.prompt_remove"),
													Type.bitstring(
														"Remove “%{question}” from %{group}? This removes it from this group only; the reusable question stays available.",
													),
												],
												[
													Type.bitstring(
														"screening.confirm.prompt_remove_last",
													),
													Type.bitstring(
														"Remove “%{question}” from %{group}? This is the last configured question, so the group will return to draft state. The reusable question stays available.",
													),
												],
												[
													Type.bitstring(
														"screening.confirm.question_deactivate",
													),
													Type.bitstring(
														"Deactivate “%{question}”? It will no longer be offered for new prompt groups, while existing prompt and answer history stays available.",
													),
												],
												[
													Type.bitstring(
														"screening.confirm.question_deactivate_fallback",
													),
													Type.bitstring(
														"Deactivate this question? Existing prompt and answer history stays available.",
													),
												],
												[
													Type.bitstring(
														"screening.current_eligibility.generic_failure",
													),
													Type.bitstring(
														"Your current information no longer meets this opportunity's screening conditions.",
													),
												],
												[
													Type.bitstring(
														"screening.current_eligibility.prompt_failure",
													),
													Type.bitstring(
														'Your current answer to "%{question}" no longer meets this opportunity\'s screening conditions.',
													),
												],
												[
													Type.bitstring("screening.eligibility.add_rule"),
													Type.bitstring("Add eligibility rule"),
												],
												[
													Type.bitstring("screening.eligibility.empty"),
													Type.bitstring(
														"No eligibility rule uses this answer. The question collects information without screening anyone out.",
													),
												],
												[
													Type.bitstring("screening.eligibility.heading"),
													Type.bitstring("Eligibility rules"),
												],
												[
													Type.bitstring("screening.eligibility.help"),
													Type.bitstring(
														"Every rule must pass for an applicant to meet this group’s requirements.",
													),
												],
												[
													Type.bitstring("screening.eligibility.move_down"),
													Type.bitstring("Move rule down"),
												],
												[
													Type.bitstring("screening.eligibility.move_up"),
													Type.bitstring("Move rule up"),
												],
												[
													Type.bitstring(
														"screening.eligibility.required_notice",
													),
													Type.bitstring(
														"Questions with eligibility rules are required automatically. Applicants are never shown which answer caused an unsuccessful screening result.",
													),
												],
												[
													Type.bitstring("screening.eligibility.rule"),
													Type.bitstring("Rule"),
												],
												[
													Type.bitstring("screening.eligibility.rules_label"),
													Type.bitstring("rules"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.active",
													),
													Type.bitstring("Active"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.all_phases",
													),
													Type.bitstring("View all phases"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.applicant_fallback",
													),
													Type.bitstring("Applicant"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.applied",
													),
													Type.bitstring("Applied"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.back",
													),
													Type.bitstring("Back to screening setup"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.changed",
													),
													Type.bitstring("Eligibility updated"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.empty_help",
													),
													Type.bitstring(
														"Active applications will appear here. Pending interest remains visible in the summary above.",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.empty_title",
													),
													Type.bitstring("No current applications"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.eyebrow",
													),
													Type.bitstring("Screening operations"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.heading",
													),
													Type.bitstring("Eligibility outcomes"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.ineligible",
													),
													Type.bitstring("Currently ineligible"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.no_phase",
													),
													Type.bitstring("No phase"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.not_available",
													),
													Type.bitstring("Not available"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.not_overrideable",
													),
													Type.bitstring(
														"Only active, currently ineligible applications can be overridden.",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.outcomes_heading",
													),
													Type.bitstring("Current application outcomes"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.outcomes_help",
													),
													Type.bitstring(
														"Review pending and active applications. Active applicants remain visible when their current eligibility changes.",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.override_action",
													),
													Type.bitstring("Force eligibility"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.override_failed",
													),
													Type.bitstring(
														"We could not save the eligibility override.",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.override_saved",
													),
													Type.bitstring("Eligibility override saved."),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.page_title",
													),
													Type.bitstring("Screening eligibility"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.pending",
													),
													Type.bitstring("Pending"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.phase",
													),
													Type.bitstring("Phase"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.reason_label",
													),
													Type.bitstring("Internal override reason"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.reason_placeholder",
													),
													Type.bitstring(
														"Explain why this applicant should be treated as eligible",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.reason_required",
													),
													Type.bitstring(
														"Enter an internal reason before forcing eligibility.",
													),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.status_currently_ineligible",
													),
													Type.bitstring("Active · currently ineligible"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.status_eligible",
													),
													Type.bitstring("Eligible"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.status_not_evaluated",
													),
													Type.bitstring("Not evaluated"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.status_overridden",
													),
													Type.bitstring("Eligible · manually reviewed"),
												],
												[
													Type.bitstring(
														"screening.eligibility_management.summary",
													),
													Type.bitstring("Eligibility summary"),
												],
												[
													Type.bitstring("screening.error.access_unverified"),
													Type.bitstring(
														"Your access could not be verified. Refresh the page and try again.",
													),
												],
												[
													Type.bitstring("screening.error.action_unsupported"),
													Type.bitstring(
														"That screening action is not supported.",
													),
												],
												[
													Type.bitstring("screening.error.group_invalid"),
													Type.bitstring("Choose a valid prompt group."),
												],
												[
													Type.bitstring("screening.error.group_missing"),
													Type.bitstring("That prompt group no longer exists."),
												],
												[
													Type.bitstring("screening.error.prompt_move_failed"),
													Type.bitstring("Unable to move that prompt."),
												],
												[
													Type.bitstring(
														"screening.error.prompt_remove_failed",
													),
													Type.bitstring("Unable to remove that prompt."),
												],
												[
													Type.bitstring(
														"screening.error.prompt_update_failed",
													),
													Type.bitstring(
														"Unable to save those prompt settings.",
													),
												],
												[
													Type.bitstring(
														"screening.error.question_uid_missing",
													),
													Type.bitstring(
														"Question not found for the selected UID.",
													),
												],
												[
													Type.bitstring(
														"screening.error.question_unavailable",
													),
													Type.bitstring(
														"Question not found or unavailable for this listing.",
													),
												],
												[
													Type.bitstring(
														"screening.error.question_update_failed",
													),
													Type.bitstring(
														"Unable to update that reusable question.",
													),
												],
												[
													Type.bitstring("screening.error.request_failed"),
													Type.bitstring("Unable to complete request."),
												],
												[
													Type.bitstring("screening.error.search_failed"),
													Type.bitstring("Unable to search questions."),
												],
												[
													Type.bitstring("screening.error.update_timeout"),
													Type.bitstring(
														"The update is taking longer than expected. Please wait, or refresh to verify the saved state before trying again.",
													),
												],
												[
													Type.bitstring("screening.filter.active_label"),
													Type.bitstring("active"),
												],
												[
													Type.bitstring("screening.filter.add"),
													Type.bitstring("Add answer filter"),
												],
												[
													Type.bitstring("screening.filter.choose_question"),
													Type.bitstring("Choose a screening question"),
												],
												[
													Type.bitstring("screening.filter.clear_all"),
													Type.bitstring("Clear all answer filters"),
												],
												[
													Type.bitstring("screening.filter.empty"),
													Type.bitstring(
														"No screening-answer filter is active.",
													),
												],
												[
													Type.bitstring("screening.filter.heading"),
													Type.bitstring("Screening answers"),
												],
												[
													Type.bitstring("screening.filter.help"),
													Type.bitstring(
														"Match applications against the answers submitted with this listing. Multiple filters must all match and only disclosed answers are searched.",
													),
												],
												[
													Type.bitstring("screening.filter.item_label"),
													Type.bitstring("Answer filter"),
												],
												[
													Type.bitstring("screening.filter.question_label"),
													Type.bitstring("Question"),
												],
												[
													Type.bitstring("screening.flash.group_deleted"),
													Type.bitstring(
														"%{group} was deleted. Its reusable questions are still available.",
													),
												],
												[
													Type.bitstring(
														"screening.flash.group_draft_discarded",
													),
													Type.bitstring("%{group} draft was discarded."),
												],
												[
													Type.bitstring("screening.flash.group_primary"),
													Type.bitstring(
														"%{group} is now the primary application flow.",
													),
												],
												[
													Type.bitstring("screening.flash.group_ready"),
													Type.bitstring(
														"%{group} is ready. Add a question to save it.",
													),
												],
												[
													Type.bitstring("screening.flash.prompt_added"),
													Type.bitstring("Prompt added to %{group}."),
												],
												[
													Type.bitstring("screening.flash.prompt_moved"),
													Type.bitstring("Prompt order updated."),
												],
												[
													Type.bitstring("screening.flash.prompt_removed"),
													Type.bitstring("Prompt removed."),
												],
												[
													Type.bitstring("screening.flash.prompt_updated"),
													Type.bitstring("Prompt settings saved."),
												],
												[
													Type.bitstring("screening.flash.question_created"),
													Type.bitstring(
														"Question created and added to %{group}.",
													),
												],
												[
													Type.bitstring("screening.flash.question_updated"),
													Type.bitstring(
														"Question wording updated everywhere this reusable question appears.",
													),
												],
												[
													Type.bitstring("screening.flash.system_created"),
													Type.bitstring("System question created."),
												],
												[
													Type.bitstring("screening.flash.system_deactivated"),
													Type.bitstring(
														"System question deactivated. Existing prompt history is preserved.",
													),
												],
												[
													Type.bitstring("screening.flash.system_reactivated"),
													Type.bitstring(
														"System question reactivated for future prompt authoring.",
													),
												],
												[
													Type.bitstring("screening.flash.system_updated"),
													Type.bitstring("System question updated."),
												],
												[
													Type.bitstring("screening.flow.empty_help"),
													Type.bitstring(
														"Search the shared library or create the first question above.",
													),
												],
												[
													Type.bitstring("screening.flow.empty_title"),
													Type.bitstring("No questions in this group"),
												],
												[
													Type.bitstring("screening.flow.eyebrow"),
													Type.bitstring("Flow order"),
												],
												[
													Type.bitstring("screening.flow.heading"),
													Type.bitstring("Configured questions"),
												],
												[
													Type.bitstring("screening.group.add_another"),
													Type.bitstring("Add another group"),
												],
												[
													Type.bitstring("screening.group.application"),
													Type.bitstring("Application"),
												],
												[
													Type.bitstring("screening.group.fallback"),
													Type.bitstring("Group"),
												],
												[
													Type.bitstring("screening.group.manage"),
													Type.bitstring("Manage question groups"),
												],
												[
													Type.bitstring("screening.group.name_help"),
													Type.bitstring(
														"The group appears immediately as a draft and is saved with its first question.",
													),
												],
												[
													Type.bitstring("screening.group.name_label"),
													Type.bitstring("Group name"),
												],
												[
													Type.bitstring("screening.group.name_placeholder"),
													Type.bitstring("Interview questions"),
												],
												[
													Type.bitstring("screening.group.order_help"),
													Type.bitstring(
														"Applicants answer these questions from top to bottom.",
													),
												],
												[
													Type.bitstring("screening.group.prompt_group"),
													Type.bitstring("Prompt group"),
												],
												[
													Type.bitstring("screening.groups.aria_label"),
													Type.bitstring("Prompt groups"),
												],
												[
													Type.bitstring("screening.groups.eyebrow"),
													Type.bitstring("Question sets"),
												],
												[
													Type.bitstring("screening.groups.heading"),
													Type.bitstring("Prompt groups"),
												],
												[
													Type.bitstring("screening.library.description"),
													Type.bitstring("System questions appear first."),
												],
												[
													Type.bitstring("screening.library.heading"),
													Type.bitstring("Add a reusable question"),
												],
												[
													Type.bitstring("screening.library.no_results"),
													Type.bitstring(
														"No reusable question matched. Try different wording or create a scoped question below.",
													),
												],
												[
													Type.bitstring(
														"screening.library.results_aria_label",
													),
													Type.bitstring("Reusable questions"),
												],
												[
													Type.bitstring("screening.library.search_help"),
													Type.bitstring(
														"Matches appear as you type. Select a question to add it.",
													),
												],
												[
													Type.bitstring("screening.library.search_label"),
													Type.bitstring("Search reusable questions"),
												],
												[
													Type.bitstring(
														"screening.library.search_placeholder",
													),
													Type.bitstring("Find a question"),
												],
												[
													Type.bitstring("screening.library.searching"),
													Type.bitstring("Searching the question library…"),
												],
												[
													Type.bitstring("screening.listing.back"),
													Type.bitstring("Back to managed listings"),
												],
												[
													Type.bitstring("screening.listing.description"),
													Type.bitstring(
														"Add questions and set the answers applicants need to qualify.",
													),
												],
												[
													Type.bitstring("screening.listing.eyebrow"),
													Type.bitstring("Screening authoring"),
												],
												[
													Type.bitstring("screening.listing.fallback"),
													Type.bitstring("Listing"),
												],
												[
													Type.bitstring("screening.listing.language_help"),
													Type.bitstring(
														"Questions are matched automatically to this listing.",
													),
												],
												[
													Type.bitstring("screening.listing.language_label"),
													Type.bitstring("Listing language"),
												],
												[
													Type.bitstring("screening.listing.no_primary_help"),
													Type.bitstring(
														"Add the first question to the Application group, or make another saved group primary before applicants respond.",
													),
												],
												[
													Type.bitstring("screening.listing.no_primary_title"),
													Type.bitstring(
														"No saved primary application flow yet",
													),
												],
												[
													Type.bitstring("screening.listing.page_title"),
													Type.bitstring("Screening setup"),
												],
												[
													Type.bitstring("screening.locale.fallback"),
													Type.bitstring("Locale"),
												],
												[
													Type.bitstring("screening.operator.contains"),
													Type.bitstring("Answer contains (text questions)"),
												],
												[
													Type.bitstring("screening.operator.eq"),
													Type.bitstring("Answer equals"),
												],
												[
													Type.bitstring("screening.operator.exists"),
													Type.bitstring("Has any answer"),
												],
												[
													Type.bitstring("screening.operator.gt"),
													Type.bitstring("Answer is greater than"),
												],
												[
													Type.bitstring("screening.operator.gte"),
													Type.bitstring("Answer is at least"),
												],
												[
													Type.bitstring("screening.operator.in"),
													Type.bitstring("Answer is one of"),
												],
												[
													Type.bitstring("screening.operator.lt"),
													Type.bitstring("Answer is less than"),
												],
												[
													Type.bitstring("screening.operator.lte"),
													Type.bitstring("Answer is at most"),
												],
												[
													Type.bitstring("screening.operator.neq"),
													Type.bitstring("Answer does not equal"),
												],
												[
													Type.bitstring("screening.operator.not_contains"),
													Type.bitstring("Answer does not contain"),
												],
												[
													Type.bitstring("screening.operator.not_exists"),
													Type.bitstring("Has no answer"),
												],
												[
													Type.bitstring("screening.operator.not_in"),
													Type.bitstring("Answer is none of"),
												],
												[
													Type.bitstring("screening.pagination.aria_label"),
													Type.bitstring("System question pagination"),
												],
												[
													Type.bitstring("screening.pagination.next"),
													Type.bitstring("Next"),
												],
												[
													Type.bitstring("screening.pagination.previous"),
													Type.bitstring("Previous"),
												],
												[
													Type.bitstring("screening.pagination.summary"),
													Type.bitstring("Page %{page} of %{total_pages}"),
												],
												[
													Type.bitstring("screening.phase.back"),
													Type.bitstring("Listing screening"),
												],
												[
													Type.bitstring("screening.phase.empty_help"),
													Type.bitstring(
														"Create the first set from listing screening.",
													),
												],
												[
													Type.bitstring("screening.phase.empty_title"),
													Type.bitstring(
														"No question sets have been configured",
													),
												],
												[
													Type.bitstring("screening.phase.eyebrow"),
													Type.bitstring("Tracker phase"),
												],
												[
													Type.bitstring("screening.phase.fallback"),
													Type.bitstring("Phase"),
												],
												[
													Type.bitstring("screening.phase.manage_sets"),
													Type.bitstring("Manage listing question sets"),
												],
												[
													Type.bitstring("screening.phase.page_title"),
													Type.bitstring("Phase screening setup"),
												],
												[
													Type.bitstring("screening.phase.preview_eyebrow"),
													Type.bitstring("Read-only preview"),
												],
												[
													Type.bitstring("screening.phase.sets_heading"),
													Type.bitstring("Listing question sets"),
												],
												[
													Type.bitstring("screening.phase.unconfigured_help"),
													Type.bitstring(
														"Question sets are currently owned by the listing. This page intentionally stays read-only until the tracker data contract defines which set a phase references.",
													),
												],
												[
													Type.bitstring("screening.phase.unconfigured_title"),
													Type.bitstring(
														"Phase attachment is not configured yet",
													),
												],
												[
													Type.bitstring("screening.prompt.optional_help"),
													Type.bitstring(
														"Questions require an answer by default. Enable this when applicants may continue without answering.",
													),
												],
												[
													Type.bitstring("screening.prompt.optional_label"),
													Type.bitstring("Optional question"),
												],
												[
													Type.bitstring("screening.prompt.reorder_help"),
													Type.bitstring(
														"Drag to reorder, or focus this handle and use the Up and Down arrow keys.",
													),
												],
												[
													Type.bitstring("screening.prompt.required_legend"),
													Type.bitstring("Required question"),
												],
												[
													Type.bitstring("screening.prompt.settings_heading"),
													Type.bitstring("Settings"),
												],
												[
													Type.bitstring(
														"screening.question.answer_type_label",
													),
													Type.bitstring("Answer type"),
												],
												[
													Type.bitstring("screening.question.choice_only"),
													Type.bitstring("choice type only"),
												],
												[
													Type.bitstring(
														"screening.question.choice_options_help",
													),
													Type.bitstring(
														"One option per line. Use stable_key | Applicant label when integrations need a stable value.",
													),
												],
												[
													Type.bitstring(
														"screening.question.choice_options_label",
													),
													Type.bitstring("Choice options"),
												],
												[
													Type.bitstring(
														"screening.question.choice_options_placeholder",
													),
													Type.bitstring("Yes\nNo\nother | Something else"),
												],
												[
													Type.bitstring("screening.question.create_heading"),
													Type.bitstring("Create a question"),
												],
												[
													Type.bitstring("screening.question.edit_wording"),
													Type.bitstring("Edit question wording"),
												],
												[
													Type.bitstring(
														"screening.question.edit_wording_help",
													),
													Type.bitstring(
														"Wording and help update wherever this question appears in this listing. Answer type and saved answers remain unchanged.",
													),
												],
												[
													Type.bitstring("screening.question.help_label"),
													Type.bitstring("Supporting help"),
												],
												[
													Type.bitstring("screening.question.help_placeholder"),
													Type.bitstring(
														"Add context or instructions shown beneath the question",
													),
												],
												[
													Type.bitstring("screening.question.optional_group"),
													Type.bitstring("Make this question optional"),
												],
												[
													Type.bitstring(
														"screening.question.optional_group_help",
													),
													Type.bitstring(
														"Questions require an answer by default. Enable this only when applicants may skip it.",
													),
												],
												[
													Type.bitstring("screening.question.options_label"),
													Type.bitstring("Options"),
												],
												[
													Type.bitstring("screening.question.reuse_account"),
													Type.bitstring("This account"),
												],
												[
													Type.bitstring(
														"screening.question.reuse_account_help",
													),
													Type.bitstring(
														"Future listings for this account may reuse the answer.",
													),
												],
												[
													Type.bitstring("screening.question.reuse_label"),
													Type.bitstring("Answer reuse"),
												],
												[
													Type.bitstring("screening.question.reuse_listing"),
													Type.bitstring("This listing"),
												],
												[
													Type.bitstring(
														"screening.question.reuse_listing_help",
													),
													Type.bitstring(
														"Answers stay local to this opportunity.",
													),
												],
												[
													Type.bitstring("screening.question.this"),
													Type.bitstring("this question"),
												],
												[
													Type.bitstring("screening.question.wording_label"),
													Type.bitstring("Question wording"),
												],
												[
													Type.bitstring(
														"screening.question.wording_placeholder",
													),
													Type.bitstring(
														"What would you like applicants to answer?",
													),
												],
												[
													Type.bitstring("screening.requirement.add"),
													Type.bitstring("Set a requirement"),
												],
												[
													Type.bitstring("screening.requirement.heading"),
													Type.bitstring("Requirement"),
												],
												[
													Type.bitstring("screening.requirement.help"),
													Type.bitstring(
														"Set an answer applicants must meet to qualify, or leave this question without a requirement.",
													),
												],
												[
													Type.bitstring("screening.requirement.locked"),
													Type.bitstring(
														"These requirements are managed by Leaply administrators and cannot be changed here.",
													),
												],
												[
													Type.bitstring("screening.requirement.remove"),
													Type.bitstring("Remove requirement"),
												],
												[
													Type.bitstring(
														"screening.requirement.required_notice",
													),
													Type.bitstring(
														"Questions with a requirement need an answer. Applicants are never shown which answer caused an unsuccessful screening result.",
													),
												],
												[
													Type.bitstring(
														"screening.requirement.summary.configured",
													),
													Type.bitstring("Requirement configured."),
												],
												[
													Type.bitstring(
														"screening.requirement.summary.contains",
													),
													Type.bitstring(
														"Requirement: answer contains %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.eq"),
													Type.bitstring(
														"Requirement: answer equals %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.gt"),
													Type.bitstring(
														"Requirement: answer is greater than %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.gte"),
													Type.bitstring(
														"Requirement: answer is at least %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.in"),
													Type.bitstring(
														"Requirement: answer is one of %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.lt"),
													Type.bitstring(
														"Requirement: answer is less than %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.lte"),
													Type.bitstring(
														"Requirement: answer is at most %{value}.",
													),
												],
												[
													Type.bitstring("screening.requirement.summary.neq"),
													Type.bitstring(
														"Requirement: answer does not equal %{value}.",
													),
												],
												[
													Type.bitstring(
														"screening.requirement.summary.not_contains",
													),
													Type.bitstring(
														"Requirement: answer does not contain %{value}.",
													),
												],
												[
													Type.bitstring(
														"screening.requirement.summary.not_in",
													),
													Type.bitstring(
														"Requirement: answer is none of %{value}.",
													),
												],
												[
													Type.bitstring("screening.response.choose_answer"),
													Type.bitstring("Choose an answer"),
												],
												[
													Type.bitstring(
														"screening.response.constraint.length_range",
													),
													Type.bitstring("Character count: %{min}–%{max}."),
												],
												[
													Type.bitstring(
														"screening.response.constraint.max_length",
													),
													Type.bitstring("Character limit: %{max}."),
												],
												[
													Type.bitstring(
														"screening.response.constraint.number_max",
													),
													Type.bitstring("Maximum %{max}."),
												],
												[
													Type.bitstring(
														"screening.response.constraint.number_min",
													),
													Type.bitstring("Minimum %{min}."),
												],
												[
													Type.bitstring(
														"screening.response.constraint.number_range",
													),
													Type.bitstring("Between %{min} and %{max}."),
												],
												[
													Type.bitstring("screening.response.file_unavailable"),
													Type.bitstring(
														"File answers are not available in the current response flow.",
													),
												],
												[
													Type.bitstring("screening.scope.account"),
													Type.bitstring("Account"),
												],
												[
													Type.bitstring("screening.scope.listing"),
													Type.bitstring("Listing"),
												],
												[
													Type.bitstring("screening.scope.system"),
													Type.bitstring("System"),
												],
												[
													Type.bitstring("screening.scope.unknown"),
													Type.bitstring("Unknown scope"),
												],
												[
													Type.bitstring("screening.status.active"),
													Type.bitstring("Active"),
												],
												[
													Type.bitstring("screening.status.conditional"),
													Type.bitstring("Conditional"),
												],
												[
													Type.bitstring("screening.status.draft_group"),
													Type.bitstring("Draft group"),
												],
												[
													Type.bitstring("screening.status.inactive"),
													Type.bitstring("Inactive"),
												],
												[
													Type.bitstring("screening.status.inactive_history"),
													Type.bitstring("Inactive history"),
												],
												[
													Type.bitstring("screening.status.primary"),
													Type.bitstring("Primary"),
												],
												[
													Type.bitstring(
														"screening.status.primary_after_first",
													),
													Type.bitstring(
														"Will become primary with its first question",
													),
												],
												[
													Type.bitstring("screening.status.primary_flow"),
													Type.bitstring("Primary application flow"),
												],
												[
													Type.bitstring("screening.status.required"),
													Type.bitstring("Required"),
												],
												[
													Type.bitstring("screening.status.unsaved"),
													Type.bitstring("unsaved"),
												],
												[
													Type.bitstring("screening.status.will_be_primary"),
													Type.bitstring("Will be primary"),
												],
												[
													Type.bitstring(
														"screening.tracker.currently_ineligible",
													),
													Type.bitstring("Currently ineligible"),
												],
												[
													Type.bitstring("screening.tracker.ineligible_since"),
													Type.bitstring(
														"Screening eligibility changed on %{date}. This application remains active.",
													),
												],
												[
													Type.bitstring(
														"screening.tracker.ineligible_since_terminal",
													),
													Type.bitstring(
														"Screening eligibility changed on %{date}.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.choice.keys_duplicate",
													),
													Type.bitstring(
														"contains duplicate choice option keys",
													),
												],
												[
													Type.bitstring(
														"screening.validation.choice.option_invalid",
													),
													Type.bitstring("contains an invalid choice option"),
												],
												[
													Type.bitstring(
														"screening.validation.choice.options_required",
													),
													Type.bitstring(
														"must include at least one keyed choice option",
													),
												],
												[
													Type.bitstring(
														"screening.validation.condition.earlier_question",
													),
													Type.bitstring(
														"must reference an earlier question in the same group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.condition.earlier_question_uid",
													),
													Type.bitstring(
														"must reference an earlier question by UID",
													),
												],
												[
													Type.bitstring(
														"screening.validation.condition.invalid",
													),
													Type.bitstring(
														"Choose a valid earlier question and visibility rule.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.configuration.object",
													),
													Type.bitstring("must be an object"),
												],
												[
													Type.bitstring(
														"screening.validation.eligibility_conditions.invalid",
													),
													Type.bitstring(
														"contains invalid eligibility conditions",
													),
												],
												[
													Type.bitstring(
														"screening.validation.eligibility_conditions.required",
													),
													Type.bitstring(
														"must be required when eligibility conditions are configured",
													),
												],
												[
													Type.bitstring(
														"screening.validation.field.account_id",
													),
													Type.bitstring("Account"),
												],
												[
													Type.bitstring(
														"screening.validation.field.answer_kind",
													),
													Type.bitstring("Answer type"),
												],
												[
													Type.bitstring("screening.validation.field.base"),
													Type.bitstring("Request"),
												],
												[
													Type.bitstring(
														"screening.validation.field.configuration",
													),
													Type.bitstring("Configuration"),
												],
												[
													Type.bitstring(
														"screening.validation.field.group_key",
													),
													Type.bitstring("Group"),
												],
												[
													Type.bitstring(
														"screening.validation.field.help_text",
													),
													Type.bitstring("Supporting help"),
												],
												[
													Type.bitstring("screening.validation.field.label"),
													Type.bitstring("Question wording"),
												],
												[
													Type.bitstring(
														"screening.validation.field.listing_id",
													),
													Type.bitstring("Listing"),
												],
												[
													Type.bitstring("screening.validation.field.locale"),
													Type.bitstring("Locale"),
												],
												[
													Type.bitstring("screening.validation.field.logic"),
													Type.bitstring("Visibility rule"),
												],
												[
													Type.bitstring("screening.validation.field.position"),
													Type.bitstring("Position"),
												],
												[
													Type.bitstring("screening.validation.field.primary"),
													Type.bitstring("Primary flow"),
												],
												[
													Type.bitstring(
														"screening.validation.field.question_id",
													),
													Type.bitstring("Question"),
												],
												[
													Type.bitstring("screening.validation.field.scope"),
													Type.bitstring("Answer reuse"),
												],
												[
													Type.bitstring("screening.validation.group.blank"),
													Type.bitstring("can't be blank"),
												],
												[
													Type.bitstring(
														"screening.validation.group.change_after_started",
													),
													Type.bitstring(
														"cannot be changed after applicants have started this group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.group.delete_after_started",
													),
													Type.bitstring(
														"cannot be deleted after applicants have started this group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.group.delete_primary_after_listing_started",
													),
													Type.bitstring(
														"cannot be deleted after applicants have started this listing",
													),
												],
												[
													Type.bitstring("screening.validation.group.missing"),
													Type.bitstring("does not exist"),
												],
												[
													Type.bitstring(
														"screening.validation.group.primary_after_listing_started",
													),
													Type.bitstring(
														"cannot be changed after applicants have started this listing",
													),
												],
												[
													Type.bitstring(
														"screening.validation.group.prompt_required",
													),
													Type.bitstring("must contain a prompt first"),
												],
												[
													Type.bitstring(
														"screening.validation.group_name.blank",
													),
													Type.bitstring("Group name can't be blank."),
												],
												[
													Type.bitstring(
														"screening.validation.group_name.taken",
													),
													Type.bitstring(
														"A group with that name already exists.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.group_name.too_long",
													),
													Type.bitstring(
														"Group name must be 80 characters or fewer.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.listing.missing",
													),
													Type.bitstring(
														"does not identify an existing listing",
													),
												],
												[
													Type.bitstring("screening.validation.locale.invalid"),
													Type.bitstring("is invalid"),
												],
												[
													Type.bitstring(
														"screening.validation.locale.listing_mismatch",
													),
													Type.bitstring(
														"%{question_locale} does not match listing locale %{listing_locale}",
													),
												],
												[
													Type.bitstring(
														"screening.validation.primary.already_exists",
													),
													Type.bitstring(
														"another group is already primary for this listing",
													),
												],
												[
													Type.bitstring(
														"screening.validation.primary.file_unsupported",
													),
													Type.bitstring(
														"file questions are not supported in the primary flow",
													),
												],
												[
													Type.bitstring(
														"screening.validation.primary.group_mismatch",
													),
													Type.bitstring(
														"must match every prompt in this group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.primary.inconsistent",
													),
													Type.bitstring(
														"group has inconsistent primary values",
													),
												],
												[
													Type.bitstring("screening.validation.prompt.missing"),
													Type.bitstring("prompt not found"),
												],
												[
													Type.bitstring(
														"screening.validation.prompt.move_after_started",
													),
													Type.bitstring(
														"cannot be changed after applicants have started this group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.prompt.object_required",
													),
													Type.bitstring("must be an object"),
												],
												[
													Type.bitstring(
														"screening.validation.prompt.remove_after_started",
													),
													Type.bitstring(
														"cannot remove prompts after applicants have started this group",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.duplicate_in_group",
													),
													Type.bitstring("is already in this prompt group"),
												],
												[
													Type.bitstring(
														"screening.validation.question.editable_fields_only",
													),
													Type.bitstring(
														"Only question wording and help text can be updated here.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.inactive",
													),
													Type.bitstring(
														"question is inactive and cannot be added to new prompts",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.locale_mismatch",
													),
													Type.bitstring(
														"question locale %{question_locale} does not match listing locale %{listing_locale}",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.missing",
													),
													Type.bitstring("question not found"),
												],
												[
													Type.bitstring(
														"screening.validation.question.object_required",
													),
													Type.bitstring(
														"Question details must be submitted as an object.",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.outside_boundary",
													),
													Type.bitstring(
														"is outside this listing's reuse boundary",
													),
												],
												[
													Type.bitstring(
														"screening.validation.question.semantics_immutable",
													),
													Type.bitstring(
														"is immutable; create a new question for new semantics",
													),
												],
												[
													Type.bitstring(
														"screening.validation.scope.boundary_mismatch",
													),
													Type.bitstring(
														"does not match the question reuse boundary",
													),
												],
												[
													Type.bitstring(
														"screening.validation.scope.employee_required",
													),
													Type.bitstring(
														"system-scoped questions require a Leaply employee role",
													),
												],
												[
													Type.bitstring(
														"screening.validation.scope.immutable",
													),
													Type.bitstring(
														"cannot be changed after a question has been created",
													),
												],
											]),
										],
									]),
								],
								[
									Type.bitstring("shared"),
									Type.map([
										[Type.atom("plural"), Type.map([])],
										[
											Type.atom("singular"),
											Type.map([
												[
													Type.bitstring("shared.locale.en_GB"),
													Type.bitstring("English (United Kingdom)"),
												],
												[
													Type.bitstring("shared.locale.en_NG"),
													Type.bitstring("English (Nigeria)"),
												],
												[
													Type.bitstring("shared.locale.en_US"),
													Type.bitstring("English (United States)"),
												],
												[
													Type.bitstring("shared.locale.en_ZA"),
													Type.bitstring("English (South Africa)"),
												],
												[
													Type.bitstring(
														"shared.navigation.academic-catalogue",
													),
													Type.bitstring("Academic catalogue"),
												],
												[
													Type.bitstring("shared.navigation.admin"),
													Type.bitstring("Admin"),
												],
												[
													Type.bitstring("shared.navigation.admin_menu"),
													Type.bitstring("Admin menu"),
												],
												[
													Type.bitstring("shared.navigation.administration"),
													Type.bitstring("Administration"),
												],
												[
													Type.bitstring("shared.navigation.applications"),
													Type.bitstring("My applications"),
												],
												[
													Type.bitstring("shared.navigation.breadcrumbs"),
													Type.bitstring("Breadcrumbs"),
												],
												[
													Type.bitstring("shared.navigation.data-browser"),
													Type.bitstring("Data browser"),
												],
												[
													Type.bitstring("shared.navigation.design-system"),
													Type.bitstring("Design system"),
												],
												[
													Type.bitstring("shared.navigation.documentation"),
													Type.bitstring("Documentation"),
												],
												[
													Type.bitstring("shared.navigation.handbooks"),
													Type.bitstring("Faculty handbooks"),
												],
												[
													Type.bitstring("shared.navigation.home"),
													Type.bitstring("Leaply home"),
												],
												[
													Type.bitstring("shared.navigation.identity-reviews"),
													Type.bitstring("Identity reviews"),
												],
												[
													Type.bitstring(
														"shared.navigation.institution-reviews",
													),
													Type.bitstring("Institution reviews"),
												],
												[
													Type.bitstring("shared.navigation.logout"),
													Type.bitstring("Log out"),
												],
												[
													Type.bitstring("shared.navigation.managed-listings"),
													Type.bitstring("Managed listings"),
												],
												[
													Type.bitstring("shared.navigation.menu"),
													Type.bitstring("Leaply menu"),
												],
												[
													Type.bitstring("shared.navigation.opportunities"),
													Type.bitstring("Opportunities"),
												],
												[
													Type.bitstring("shared.navigation.personal"),
													Type.bitstring("Personal"),
												],
												[
													Type.bitstring("shared.navigation.professional"),
													Type.bitstring("Professional profile"),
												],
												[
													Type.bitstring("shared.navigation.settings"),
													Type.bitstring("Settings"),
												],
												[
													Type.bitstring("shared.navigation.signed_in"),
													Type.bitstring("Signed in"),
												],
												[
													Type.bitstring("shared.navigation.system-questions"),
													Type.bitstring("System questions"),
												],
												[
													Type.bitstring("shared.navigation.tools"),
													Type.bitstring("Tools"),
												],
												[
													Type.bitstring("shared.navigation.work"),
													Type.bitstring("Work"),
												],
												[
													Type.bitstring("shared.theme.dark"),
													Type.bitstring("Dark"),
												],
												[
													Type.bitstring("shared.theme.light"),
													Type.bitstring("Light"),
												],
												[
													Type.bitstring("shared.theme.switch_to_dark"),
													Type.bitstring("Switch to dark mode"),
												],
												[
													Type.bitstring("shared.theme.switch_to_light"),
													Type.bitstring("Switch to light mode"),
												],
											]),
										],
									]),
								],
								[
									Type.bitstring("tracker"),
									Type.map([
										[
											Type.atom("plural"),
											Type.map([
												[
													Type.bitstring("tracker.count.application"),
													Type.tuple([
														Type.bitstring("tracker.count.applications"),
														Type.map([
															[
																Type.integer(0n),
																Type.bitstring("%{count} application"),
															],
															[
																Type.integer(1n),
																Type.bitstring("%{count} applications"),
															],
														]),
													]),
												],
											]),
										],
										[
											Type.atom("singular"),
											Type.map([
												[
													Type.bitstring("tracker.action.view_profile"),
													Type.bitstring("View profile"),
												],
												[
													Type.bitstring("tracker.empty.applications"),
													Type.bitstring(
														"No visible applicants or respondents for this listing.",
													),
												],
												[
													Type.bitstring("tracker.fallback.account"),
													Type.bitstring("Account"),
												],
												[
													Type.bitstring("tracker.fallback.applicant"),
													Type.bitstring("Applicant"),
												],
												[
													Type.bitstring("tracker.fallback.title"),
													Type.bitstring("Tracker"),
												],
												[
													Type.bitstring("tracker.filter.apply"),
													Type.bitstring("Apply filters"),
												],
												[
													Type.bitstring("tracker.filter.search_label"),
													Type.bitstring("Search"),
												],
												[
													Type.bitstring("tracker.filter.search_placeholder"),
													Type.bitstring("Search visible names"),
												],
												[
													Type.bitstring("tracker.filter.state_label"),
													Type.bitstring("State"),
												],
												[
													Type.bitstring("tracker.mode.applicants"),
													Type.bitstring("Applicants"),
												],
												[
													Type.bitstring("tracker.mode.applications"),
													Type.bitstring("Applications"),
												],
												[
													Type.bitstring("tracker.mode.respondents"),
													Type.bitstring("Respondents"),
												],
												[
													Type.bitstring("tracker.mode.screening"),
													Type.bitstring("Respondents with screening"),
												],
												[
													Type.bitstring("tracker.notice.applicant"),
													Type.bitstring(
														"Profile details are visible for these applicants.",
													),
												],
												[
													Type.bitstring("tracker.notice.mixed"),
													Type.bitstring(
														"Profile and screening visibility follows the grant recorded on each application.",
													),
												],
												[
													Type.bitstring("tracker.notice.respondent"),
													Type.bitstring(
														"Only basic respondent details are visible.",
													),
												],
												[
													Type.bitstring("tracker.notice.screening"),
													Type.bitstring(
														"Screening answers are visible for these respondents.",
													),
												],
												[
													Type.bitstring("tracker.pagination.next"),
													Type.bitstring("Next"),
												],
												[
													Type.bitstring("tracker.pagination.page"),
													Type.bitstring("Page %{page} of %{total}"),
												],
												[
													Type.bitstring("tracker.pagination.previous"),
													Type.bitstring("Previous"),
												],
												[
													Type.bitstring("tracker.phase.all"),
													Type.bitstring("All phases"),
												],
												[
													Type.bitstring("tracker.phase.heading"),
													Type.bitstring("Phases"),
												],
												[
													Type.bitstring("tracker.phase.hide_hidden"),
													Type.bitstring("Hide hidden phases"),
												],
												[
													Type.bitstring("tracker.phase.show_hidden"),
													Type.bitstring("Show hidden phases"),
												],
												[
													Type.bitstring("tracker.sort.application_time"),
													Type.bitstring("Sorted by application time"),
												],
												[
													Type.bitstring("tracker.sort.phase_movement"),
													Type.bitstring("Sorted by phase movement"),
												],
												[
													Type.bitstring("tracker.state.active"),
													Type.bitstring("Active"),
												],
												[
													Type.bitstring("tracker.state.all"),
													Type.bitstring("All"),
												],
												[
													Type.bitstring("tracker.state.not_set"),
													Type.bitstring("Not set"),
												],
												[
													Type.bitstring("tracker.state.successful"),
													Type.bitstring("Successful"),
												],
												[
													Type.bitstring("tracker.state.unsuccessful"),
													Type.bitstring("Unsuccessful"),
												],
												[
													Type.bitstring("tracker.state.withdrawn"),
													Type.bitstring("Withdrawn"),
												],
											]),
										],
									]),
								],
							]),
							context.vars.domain_0,
							Type.map([[Type.atom("singular"), Type.map([])]]),
						),
						Type.variablePattern("catalog_3"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					Interpreter.matchOperator(
						Elixir_Map["get/3"](
							Elixir_Map["get/3"](
								context.vars.catalog_3,
								Type.atom("singular"),
								Type.map([]),
							),
							context.vars.key_1,
							context.vars.key_1,
						),
						Type.variablePattern("value_4"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Elixir_LeaplyWeb_Hologram_Helpers_CatalogText["interpolate/2"](
						context.vars.value_4,
						context.vars.bindings_2,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Helpers.CatalogText",
		"interpolate",
		2,
		"private",
		[
			{
				params: (context) => [Type.variablePattern("text_0"), Type.list([])],
				guards: [],
				body: (context) => {
					return context.vars.text_0;
				},
			},
			{
				params: (context) => [
					Type.variablePattern("text_0"),
					Type.consPattern(
						Type.tuple([
							Type.variablePattern("key_1"),
							Type.variablePattern("value_2"),
						]),
						Type.variablePattern("bindings_3"),
					),
				],
				guards: [],
				body: (context) => {
					Interpreter.matchOperator(
						Type.bitstring([
							Type.bitstringSegment(Type.string("%{"), { type: "binary" }),
							Type.bitstringSegment(
								Erlang["atom_to_binary/1"](context.vars.key_1),
								{ type: "binary" },
							),
							Type.bitstringSegment(Type.string("}"), { type: "binary" }),
						]),
						Type.variablePattern("placeholder_4"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Elixir_LeaplyWeb_Hologram_Helpers_CatalogText["interpolate/2"](
						Elixir_String["replace/3"](
							context.vars.text_0,
							context.vars.placeholder_4,
							Elixir_String_Chars["to_string/1"](context.vars.value_2),
						),
						context.vars.bindings_3,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Helpers.ProfileText",
		"get",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("key_0")],
				guards: [],
				body: (context) => {
					return Elixir_Map["get/3"](
						Type.map([
							[
								Type.bitstring("academic_record.empty.description"),
								Type.bitstring(
									"Start by uploading a downloaded or scanned transcript. Leaply will store the file, analyze it locally, and only create academic records from trusted evidence.",
								),
							],
							[
								Type.bitstring("academic_record.empty.heading"),
								Type.bitstring("No academic transcripts yet"),
							],
							[
								Type.bitstring("academic_record.nav.aria_label"),
								Type.bitstring("Academic workspace navigation"),
							],
							[
								Type.bitstring("academic_record.nav.profile"),
								Type.bitstring("Profile"),
							],
							[
								Type.bitstring("academic_record.page.badge"),
								Type.bitstring("Academic"),
							],
							[
								Type.bitstring("academic_record.page.description"),
								Type.bitstring(
									"Upload downloaded or scanned transcripts so Leaply can build your academic record from institutional evidence. We prioritize PDFs and scans, while lower-quality captures may be rejected or reviewed before they affect your record.",
								),
							],
							[
								Type.bitstring("academic_record.page.heading"),
								Type.bitstring("Transcript-backed academic history"),
							],
							[
								Type.bitstring(
									"academic_record.qualifications.empty.description",
								),
								Type.bitstring(
									"Your transcript-backed qualifications will appear here after processing completes.",
								),
							],
							[
								Type.bitstring("academic_record.qualifications.empty.heading"),
								Type.bitstring("No structured qualifications yet"),
							],
							[
								Type.bitstring(
									"academic_record.transcript.institution.pending",
								),
								Type.bitstring("Institution detection pending"),
							],
							[
								Type.bitstring(
									"academic_record.transcript.processed_at.pending",
								),
								Type.bitstring("Still processing"),
							],
							[
								Type.bitstring("academic_record.transcripts.heading"),
								Type.bitstring("Transcript evidence"),
							],
							[
								Type.bitstring("academic_record.transcripts.hint"),
								Type.bitstring(
									"Each upload keeps its own trust and processing status so you can see what Leaply is still working on.",
								),
							],
							[
								Type.bitstring("academic_transcript.detail.back"),
								Type.bitstring("Back to academic workspace"),
							],
							[
								Type.bitstring("academic_transcript.flash.uploaded"),
								Type.bitstring(
									"Your transcript has been received and queued for local processing.",
								),
							],
							[
								Type.bitstring("academic_transcript.form.description"),
								Type.bitstring(
									"Prefer downloaded PDFs or clean scans. Mobile-phone photos are accepted, but perspective distortion, glare, blur, and cropped edges may reduce trust and delay processing.",
								),
							],
							[
								Type.bitstring("academic_transcript.form.error.upload_failed"),
								Type.bitstring(
									"We couldn't queue that transcript right now. Please try again.",
								),
							],
							[
								Type.bitstring("academic_transcript.form.file.help"),
								Type.bitstring(
									"Accepted formats: PDF or image. Leaply stores the original file before analysis starts.",
								),
							],
							[
								Type.bitstring("academic_transcript.form.file.label"),
								Type.bitstring("Transcript file"),
							],
							[
								Type.bitstring("academic_transcript.form.heading"),
								Type.bitstring("Upload a transcript"),
							],
							[
								Type.bitstring("academic_transcript.form.submit"),
								Type.bitstring("Upload transcript"),
							],
							[
								Type.bitstring(
									"academic_transcript.validation.mime_type.invalid",
								),
								Type.bitstring("Upload a PDF or image file."),
							],
							[
								Type.bitstring("identity_record.card.disclosure"),
								Type.bitstring("Disclosure"),
							],
							[
								Type.bitstring("identity_record.card.proof_files"),
								Type.bitstring("Proof files"),
							],
							[
								Type.bitstring("identity_record.card.view_details"),
								Type.bitstring("View details"),
							],
							[
								Type.bitstring("identity_record.country.ca"),
								Type.bitstring("Canada"),
							],
							[
								Type.bitstring("identity_record.country.gb"),
								Type.bitstring("United Kingdom"),
							],
							[
								Type.bitstring("identity_record.country.unknown"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.country.us"),
								Type.bitstring("United States"),
							],
							[
								Type.bitstring("identity_record.country.za"),
								Type.bitstring("South Africa"),
							],
							[
								Type.bitstring("identity_record.detail.back"),
								Type.bitstring("Back to identity workspace"),
							],
							[
								Type.bitstring("identity_record.detail.edit.description"),
								Type.bitstring(
									"Changing identity fields or replacing proof will reset any previous verification and send the record back through review.",
								),
							],
							[
								Type.bitstring("identity_record.detail.edit.heading"),
								Type.bitstring("Edit record"),
							],
							[
								Type.bitstring("identity_record.detail.edit.locked"),
								Type.bitstring(
									"This record is locked right now. Reach out to Leaply support if you need help updating it.",
								),
							],
							[
								Type.bitstring("identity_record.detail.edit.submit"),
								Type.bitstring("Update identity record"),
							],
							[
								Type.bitstring(
									"identity_record.detail.owner_guidance.additional_evidence_required",
								),
								Type.bitstring(
									"A reviewer asked for renewed or clearer evidence before this claim can be trusted.",
								),
							],
							[
								Type.bitstring(
									"identity_record.detail.owner_guidance.conflicted",
								),
								Type.bitstring(
									"This identity claim conflicts with other information and cannot be trusted until it is resolved.",
								),
							],
							[
								Type.bitstring("identity_record.detail.owner_guidance.default"),
								Type.bitstring(
									"Review outcomes will appear here as soon as Leaply records them.",
								),
							],
							[
								Type.bitstring("identity_record.detail.owner_guidance.draft"),
								Type.bitstring(
									"Add the remaining proof before Leaply can review this identity claim.",
								),
							],
							[
								Type.bitstring("identity_record.detail.owner_guidance.expired"),
								Type.bitstring(
									"This identity claim has expired. Upload renewed evidence to start review again.",
								),
							],
							[
								Type.bitstring(
									"identity_record.detail.owner_guidance.pending_verification",
								),
								Type.bitstring(
									"Leaply has the required proof and your identity record is waiting for manual review.",
								),
							],
							[
								Type.bitstring(
									"identity_record.detail.owner_guidance.rejected",
								),
								Type.bitstring(
									"Leaply could not trust this identity claim from the submitted proof.",
								),
							],
							[
								Type.bitstring(
									"identity_record.detail.owner_guidance.verified",
								),
								Type.bitstring(
									"Leaply has reviewed this claim at the current verification level shown below.",
								),
							],
							[
								Type.bitstring("identity_record.detail.proof_summary.heading"),
								Type.bitstring("Proof summary"),
							],
							[
								Type.bitstring("identity_record.detail.proof_summary.missing"),
								Type.bitstring(
									"More proof is needed before this record can move into pending verification.",
								),
							],
							[
								Type.bitstring(
									"identity_record.detail.proof_summary.missing_role",
								),
								Type.bitstring("Missing: %{role}"),
							],
							[
								Type.bitstring("identity_record.detail.proof_summary.ready"),
								Type.bitstring(
									"The required proof set is present. This record is ready for verification review.",
								),
							],
							[
								Type.bitstring("identity_record.detail.summary.disclosure"),
								Type.bitstring("Disclosure"),
							],
							[
								Type.bitstring("identity_record.detail.summary.expires_on"),
								Type.bitstring("Expiry date"),
							],
							[
								Type.bitstring("identity_record.detail.summary.issued_on"),
								Type.bitstring("Issue date"),
							],
							[
								Type.bitstring("identity_record.detail.summary.legal_status"),
								Type.bitstring("Legal status"),
							],
							[
								Type.bitstring("identity_record.detail.summary.proof_files"),
								Type.bitstring("Proof files"),
							],
							[
								Type.bitstring("identity_record.detail.summary.status"),
								Type.bitstring("Status"),
							],
							[
								Type.bitstring(
									"identity_record.detail.verification.checked_at",
								),
								Type.bitstring("Checked at"),
							],
							[
								Type.bitstring(
									"identity_record.detail.verification.current_level",
								),
								Type.bitstring("Current verification level"),
							],
							[
								Type.bitstring(
									"identity_record.detail.verification.current_status",
								),
								Type.bitstring("Current status"),
							],
							[
								Type.bitstring("identity_record.detail.verification.empty"),
								Type.bitstring(
									"No verification attempts have been recorded yet. Once Leaply completes a review, the outcome will appear here.",
								),
							],
							[
								Type.bitstring("identity_record.detail.verification.heading"),
								Type.bitstring("Verification history"),
							],
							[
								Type.bitstring(
									"identity_record.detail.verification.last_trusted_at",
								),
								Type.bitstring("Last trusted at"),
							],
							[
								Type.bitstring("identity_record.detail.verification.level"),
								Type.bitstring("Verification level"),
							],
							[
								Type.bitstring(
									"identity_record.detail.verification.reason_code",
								),
								Type.bitstring("Reason code"),
							],
							[
								Type.bitstring("identity_record.disclosure.full"),
								Type.bitstring("Full"),
							],
							[
								Type.bitstring("identity_record.disclosure.masked"),
								Type.bitstring("Masked"),
							],
							[
								Type.bitstring("identity_record.disclosure.private"),
								Type.bitstring("Private"),
							],
							[
								Type.bitstring("identity_record.document_role.back_scan"),
								Type.bitstring("Back scan"),
							],
							[
								Type.bitstring("identity_record.document_role.default"),
								Type.bitstring("Supporting document"),
							],
							[
								Type.bitstring("identity_record.document_role.front_scan"),
								Type.bitstring("Front scan"),
							],
							[
								Type.bitstring("identity_record.document_role.other"),
								Type.bitstring("Other"),
							],
							[
								Type.bitstring(
									"identity_record.document_role.passport_photo_page",
								),
								Type.bitstring("Passport photo page"),
							],
							[
								Type.bitstring(
									"identity_record.document_role.supporting_attachment",
								),
								Type.bitstring("Supporting attachment"),
							],
							[
								Type.bitstring("identity_record.document_status.accepted"),
								Type.bitstring("Accepted"),
							],
							[
								Type.bitstring("identity_record.document_status.default"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.document_status.rejected"),
								Type.bitstring("Rejected"),
							],
							[
								Type.bitstring("identity_record.document_status.superseded"),
								Type.bitstring("Superseded"),
							],
							[
								Type.bitstring("identity_record.document_status.uploaded"),
								Type.bitstring("Uploaded"),
							],
							[
								Type.bitstring("identity_record.field.country_code"),
								Type.bitstring("Country"),
							],
							[
								Type.bitstring("identity_record.field.default"),
								Type.bitstring("Field"),
							],
							[
								Type.bitstring("identity_record.field.disclosure"),
								Type.bitstring("Disclosure"),
							],
							[
								Type.bitstring("identity_record.field.expires_on"),
								Type.bitstring("Expiry date"),
							],
							[
								Type.bitstring("identity_record.field.identifier"),
								Type.bitstring("Identifier"),
							],
							[
								Type.bitstring("identity_record.field.issued_on"),
								Type.bitstring("Issue date"),
							],
							[
								Type.bitstring("identity_record.field.issuing_authority"),
								Type.bitstring("Issuing authority"),
							],
							[
								Type.bitstring("identity_record.field.legal_status"),
								Type.bitstring("Legal status"),
							],
							[
								Type.bitstring("identity_record.field.method"),
								Type.bitstring("Identification method"),
							],
							[
								Type.bitstring("identity_record.field.proof_files"),
								Type.bitstring("Proof files"),
							],
							[
								Type.bitstring("identity_record.field.status"),
								Type.bitstring("Status"),
							],
							[
								Type.bitstring("identity_record.flash.created"),
								Type.bitstring("Your identity record has been saved."),
							],
							[
								Type.bitstring("identity_record.flash.updated"),
								Type.bitstring("Your identity record has been updated."),
							],
							[
								Type.bitstring("identity_record.flash.uploaded"),
								Type.bitstring("Your proof file has been uploaded."),
							],
							[
								Type.bitstring("identity_record.form.country.blank"),
								Type.bitstring("Select a country"),
							],
							[
								Type.bitstring("identity_record.form.country.label"),
								Type.bitstring("Country"),
							],
							[
								Type.bitstring(
									"identity_record.form.disclosure.pending_selection",
								),
								Type.bitstring("Pending selection"),
							],
							[
								Type.bitstring("identity_record.form.error.save_failed"),
								Type.bitstring("We couldn't save this identity record."),
							],
							[
								Type.bitstring("identity_record.form.error.upload_failed"),
								Type.bitstring("We couldn't upload that file."),
							],
							[
								Type.bitstring("identity_record.form.expires_on.label"),
								Type.bitstring("Expiry date"),
							],
							[
								Type.bitstring("identity_record.form.identifier_help.pending"),
								Type.bitstring(
									"Choose a country and method to see the capture rules.",
								),
							],
							[
								Type.bitstring("identity_record.form.identifier_help.selected"),
								Type.bitstring(
									"Required fields: %{fields}. Disclosure policy: %{policy}.",
								),
							],
							[
								Type.bitstring("identity_record.form.issued_on.label"),
								Type.bitstring("Issue date"),
							],
							[
								Type.bitstring("identity_record.form.issuing_authority.label"),
								Type.bitstring("Issuing authority"),
							],
							[
								Type.bitstring("identity_record.form.legal_status.inferred"),
								Type.bitstring(
									"Inferred from the selected country and method: %{legal_status}",
								),
							],
							[
								Type.bitstring("identity_record.form.legal_status.label"),
								Type.bitstring("Legal status"),
							],
							[
								Type.bitstring("identity_record.form.method.blank"),
								Type.bitstring("Select a method"),
							],
							[
								Type.bitstring("identity_record.form.method.label"),
								Type.bitstring("Identification method"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.ca.national_id",
								),
								Type.bitstring("National identifier"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.ca.other_government_document",
								),
								Type.bitstring("Document number"),
							],
							[
								Type.bitstring("identity_record.identifier_label.ca.passport"),
								Type.bitstring("Passport number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.ca.residence_permit",
								),
								Type.bitstring("Permit number"),
							],
							[
								Type.bitstring("identity_record.identifier_label.default"),
								Type.bitstring("Identifier"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.gb.national_id",
								),
								Type.bitstring("National Insurance number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.gb.other_government_document",
								),
								Type.bitstring("Document number"),
							],
							[
								Type.bitstring("identity_record.identifier_label.gb.passport"),
								Type.bitstring("Passport number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.gb.residence_permit",
								),
								Type.bitstring("Residence permit number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.us.national_id",
								),
								Type.bitstring("Social Security number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.us.other_government_document",
								),
								Type.bitstring("Document number"),
							],
							[
								Type.bitstring("identity_record.identifier_label.us.passport"),
								Type.bitstring("Passport number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.us.residence_permit",
								),
								Type.bitstring("Residence permit number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.za.national_id",
								),
								Type.bitstring("South African ID number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.za.other_government_document",
								),
								Type.bitstring("Document number"),
							],
							[
								Type.bitstring("identity_record.identifier_label.za.passport"),
								Type.bitstring("Passport number"),
							],
							[
								Type.bitstring(
									"identity_record.identifier_label.za.residence_permit",
								),
								Type.bitstring("Permit number"),
							],
							[
								Type.bitstring("identity_record.identifier_value.private"),
								Type.bitstring("Withheld"),
							],
							[
								Type.bitstring("identity_record.index.badge"),
								Type.bitstring("Identity workspace"),
							],
							[
								Type.bitstring("identity_record.index.description"),
								Type.bitstring(
									"Add and maintain reusable legal identity records without repeating the same answers in every application. Each record keeps its own country rules, disclosure policy, and proof history.",
								),
							],
							[
								Type.bitstring("identity_record.index.empty.description"),
								Type.bitstring(
									"Start with one country and one document type. You can add more countries or methods later.",
								),
							],
							[
								Type.bitstring("identity_record.index.empty.heading"),
								Type.bitstring("No identity records yet"),
							],
							[
								Type.bitstring("identity_record.index.form.description"),
								Type.bitstring(
									"Choose the country and identity method first. Leaply will normalize the identifier, store the disclosure policy, and keep the record ready for proof upload.",
								),
							],
							[
								Type.bitstring("identity_record.index.form.heading"),
								Type.bitstring("Add an identity record"),
							],
							[
								Type.bitstring("identity_record.index.form.submit"),
								Type.bitstring("Save identity record"),
							],
							[
								Type.bitstring("identity_record.index.heading"),
								Type.bitstring("Manage country-scoped identity evidence"),
							],
							[
								Type.bitstring("identity_record.index.nav.aria_label"),
								Type.bitstring("Identity navigation"),
							],
							[
								Type.bitstring("identity_record.index.nav.profile"),
								Type.bitstring("Profile"),
							],
							[
								Type.bitstring(
									"identity_record.index.records.grouped_by_country",
								),
								Type.bitstring("Grouped by country"),
							],
							[
								Type.bitstring("identity_record.index.records.heading"),
								Type.bitstring("Your identity records"),
							],
							[
								Type.bitstring("identity_record.legal_status.citizen"),
								Type.bitstring("Citizen"),
							],
							[
								Type.bitstring("identity_record.legal_status.foreign_national"),
								Type.bitstring("Foreign national"),
							],
							[
								Type.bitstring(
									"identity_record.legal_status.permanent_resident",
								),
								Type.bitstring("Permanent resident"),
							],
							[
								Type.bitstring(
									"identity_record.legal_status.refugee_or_protected_person",
								),
								Type.bitstring("Refugee or protected person"),
							],
							[
								Type.bitstring(
									"identity_record.legal_status.temporary_resident",
								),
								Type.bitstring("Temporary resident"),
							],
							[
								Type.bitstring("identity_record.legal_status.unknown"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.method.default"),
								Type.bitstring("Identity method"),
							],
							[
								Type.bitstring("identity_record.method.national_id"),
								Type.bitstring("National ID"),
							],
							[
								Type.bitstring(
									"identity_record.method.other_government_document",
								),
								Type.bitstring("Other government document"),
							],
							[
								Type.bitstring("identity_record.method.passport"),
								Type.bitstring("Passport"),
							],
							[
								Type.bitstring("identity_record.method.residence_permit"),
								Type.bitstring("Residence permit"),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.authority_mismatch",
								),
								Type.bitstring(
									"Conflict - authority result does not match the evidence",
								),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.cross_user_conflict",
								),
								Type.bitstring(
									"Conflict - identifier is linked to another user",
								),
							],
							[
								Type.bitstring("identity_record.reason_code.default"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.reason_code.document_expired"),
								Type.bitstring("More evidence needed - document is expired"),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.document_illegible",
								),
								Type.bitstring("More evidence needed - document is illegible"),
							],
							[
								Type.bitstring("identity_record.reason_code.edited_document"),
								Type.bitstring("Reject - document appears edited"),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.identifier_mismatch",
								),
								Type.bitstring("Reject - identifier does not match the claim"),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.legal_status_mismatch",
								),
								Type.bitstring(
									"Conflict - legal status does not match the evidence",
								),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.manual_review_approved",
								),
								Type.bitstring("Approved - documents match the claim"),
							],
							[
								Type.bitstring(
									"identity_record.reason_code.missing_supporting_document",
								),
								Type.bitstring(
									"More evidence needed - missing supporting document",
								),
							],
							[
								Type.bitstring("identity_record.reason_code.none"),
								Type.bitstring("—"),
							],
							[
								Type.bitstring("identity_record.reason_code.wrong_document"),
								Type.bitstring("Reject - wrong document submitted"),
							],
							[
								Type.bitstring("identity_record.reason_code.wrong_person"),
								Type.bitstring("Reject - document belongs to another person"),
							],
							[
								Type.bitstring(
									"identity_record.status.additional_evidence_required",
								),
								Type.bitstring("Additional evidence required"),
							],
							[
								Type.bitstring("identity_record.status.conflicted"),
								Type.bitstring("Conflicted"),
							],
							[
								Type.bitstring("identity_record.status.default"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.status.draft"),
								Type.bitstring("Draft"),
							],
							[
								Type.bitstring("identity_record.status.expired"),
								Type.bitstring("Expired"),
							],
							[
								Type.bitstring("identity_record.status.pending_verification"),
								Type.bitstring("Pending verification"),
							],
							[
								Type.bitstring("identity_record.status.rejected"),
								Type.bitstring("Rejected"),
							],
							[
								Type.bitstring("identity_record.status.superseded"),
								Type.bitstring("Superseded"),
							],
							[
								Type.bitstring("identity_record.status.under_review"),
								Type.bitstring("Under review"),
							],
							[
								Type.bitstring("identity_record.status.verified"),
								Type.bitstring("Verified"),
							],
							[
								Type.bitstring("identity_record.upload.info.missing"),
								Type.bitstring("Upload the remaining proof files: %{roles}."),
							],
							[
								Type.bitstring("identity_record.upload.info.ready"),
								Type.bitstring(
									"This record has the required proof set and is ready for verification review.",
								),
							],
							[
								Type.bitstring("identity_record.uploader.description"),
								Type.bitstring(
									"Upload images or PDFs that prove this identity method. Required roles depend on the selected country and method.",
								),
							],
							[
								Type.bitstring("identity_record.uploader.document_role.label"),
								Type.bitstring("Document role"),
							],
							[
								Type.bitstring("identity_record.uploader.heading"),
								Type.bitstring("Upload proof"),
							],
							[
								Type.bitstring("identity_record.uploader.proof_file.help"),
								Type.bitstring(
									"Accepted file types: PDF, JPG, PNG, and other common image formats.",
								),
							],
							[
								Type.bitstring("identity_record.uploader.proof_file.label"),
								Type.bitstring("Proof file"),
							],
							[
								Type.bitstring("identity_record.uploader.submit"),
								Type.bitstring("Upload proof"),
							],
							[
								Type.bitstring(
									"identity_record.validation.country.unsupported",
								),
								Type.bitstring("Choose a supported country."),
							],
							[
								Type.bitstring(
									"identity_record.validation.expires_on.after_issue_date",
								),
								Type.bitstring("The expiry date must be after the issue date."),
							],
							[
								Type.bitstring("identity_record.validation.length.exact"),
								Type.bitstring(
									"This value must be exactly %{count} characters long.",
								),
							],
							[
								Type.bitstring("identity_record.validation.length.max"),
								Type.bitstring(
									"This value must be at most %{count} characters long.",
								),
							],
							[
								Type.bitstring("identity_record.validation.method.unsupported"),
								Type.bitstring("Choose a supported identification method."),
							],
							[
								Type.bitstring("identity_record.validation.mime_type.invalid"),
								Type.bitstring("Upload a PDF or image file."),
							],
							[
								Type.bitstring("identity_record.validation.required"),
								Type.bitstring("This field is required."),
							],
							[
								Type.bitstring(
									"identity_record.validation.required_for_method",
								),
								Type.bitstring(
									"This field is required for the selected identity method.",
								),
							],
							[
								Type.bitstring(
									"identity_record.verification_level.authority_verified",
								),
								Type.bitstring("Authority verified"),
							],
							[
								Type.bitstring("identity_record.verification_level.default"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring(
									"identity_record.verification_level.document_reviewed",
								),
								Type.bitstring("Document reviewed"),
							],
							[
								Type.bitstring("identity_record.verification_level.none"),
								Type.bitstring("Unverified"),
							],
							[
								Type.bitstring(
									"identity_record.verification_method.authority_api",
								),
								Type.bitstring("Authority API"),
							],
							[
								Type.bitstring("identity_record.verification_method.default"),
								Type.bitstring("Verification method"),
							],
							[
								Type.bitstring(
									"identity_record.verification_method.legacy_import",
								),
								Type.bitstring("Legacy import"),
							],
							[
								Type.bitstring(
									"identity_record.verification_method.manual_employee_review",
								),
								Type.bitstring("Manual employee review"),
							],
							[
								Type.bitstring(
									"identity_record.verification_status.additional_evidence_required",
								),
								Type.bitstring("Additional evidence required"),
							],
							[
								Type.bitstring(
									"identity_record.verification_status.conflicted",
								),
								Type.bitstring("Conflicted"),
							],
							[
								Type.bitstring("identity_record.verification_status.default"),
								Type.bitstring("Unknown"),
							],
							[
								Type.bitstring("identity_record.verification_status.expired"),
								Type.bitstring("Expired"),
							],
							[
								Type.bitstring("identity_record.verification_status.failed"),
								Type.bitstring("Failed"),
							],
							[
								Type.bitstring(
									"identity_record.verification_status.processing",
								),
								Type.bitstring("Processing"),
							],
							[
								Type.bitstring("identity_record.verification_status.queued"),
								Type.bitstring("Queued"),
							],
							[
								Type.bitstring("identity_record.verification_status.rejected"),
								Type.bitstring("Rejected"),
							],
							[
								Type.bitstring("identity_record.verification_status.verified"),
								Type.bitstring("Verified"),
							],
							[
								Type.bitstring("profile.flash.updated"),
								Type.bitstring("Your profile has been updated."),
							],
							[
								Type.bitstring("profile.form.aria_label"),
								Type.bitstring("Profile details form"),
							],
							[
								Type.bitstring("profile.form.avatar.remove"),
								Type.bitstring("Remove current avatar"),
							],
							[
								Type.bitstring("profile.form.avatar_upload.help"),
								Type.bitstring(
									"Upload a PNG, JPEG, GIF, or WebP image to store your avatar on Leaply.",
								),
							],
							[
								Type.bitstring("profile.form.avatar_upload.label"),
								Type.bitstring("Upload avatar"),
							],
							[
								Type.bitstring("profile.form.avatar_url.help"),
								Type.bitstring(
									"Optionally provide a secure https:// image URL and Leaply will import it as your stored avatar.",
								),
							],
							[
								Type.bitstring("profile.form.avatar_url.label"),
								Type.bitstring("Avatar URL"),
							],
							[
								Type.bitstring("profile.form.avatar_url.placeholder"),
								Type.bitstring("https://example.com/avatar.jpg"),
							],
							[
								Type.bitstring("profile.form.description"),
								Type.bitstring(
									"Update the core details stored on your account. Leave the avatar blank if you want to return to the placeholder graphic.",
								),
							],
							[
								Type.bitstring("profile.form.display_name.help"),
								Type.bitstring(
									"Leave this blank if you do not want a public-facing display name yet.",
								),
							],
							[
								Type.bitstring("profile.form.display_name.label"),
								Type.bitstring("Display name"),
							],
							[
								Type.bitstring("profile.form.display_name.placeholder"),
								Type.bitstring("How should your name appear?"),
							],
							[
								Type.bitstring("profile.form.error.save_failed"),
								Type.bitstring(
									"We couldn't save your profile. Please correct the highlighted fields.",
								),
							],
							[
								Type.bitstring("profile.form.footer_hint"),
								Type.bitstring(
									"Changes save to your account immediately after validation.",
								),
							],
							[
								Type.bitstring("profile.form.heading"),
								Type.bitstring("Profile details"),
							],
							[
								Type.bitstring("profile.form.locale.help"),
								Type.bitstring(
									"This controls locale-sensitive profile terms like ethnicity categories. New accounts default to English (South Africa) unless you choose a different locale.",
								),
							],
							[
								Type.bitstring("profile.form.locale.label"),
								Type.bitstring("Locale"),
							],
							[
								Type.bitstring("profile.form.submit"),
								Type.bitstring("Save profile"),
							],
							[
								Type.bitstring("profile.locale.option.en_GB"),
								Type.bitstring("English (United Kingdom)"),
							],
							[
								Type.bitstring("profile.locale.option.en_NG"),
								Type.bitstring("English (Nigeria)"),
							],
							[
								Type.bitstring("profile.locale.option.en_US"),
								Type.bitstring("English (United States)"),
							],
							[
								Type.bitstring("profile.locale.option.en_ZA"),
								Type.bitstring("English (South Africa)"),
							],
							[
								Type.bitstring("profile.login_methods.add.description"),
								Type.bitstring(
									"Add another way to sign in without creating a second account. Verification happens before a new method becomes active.",
								),
							],
							[
								Type.bitstring("profile.login_methods.add.heading"),
								Type.bitstring("Add a login method"),
							],
							[
								Type.bitstring("profile.login_methods.aria_label"),
								Type.bitstring("Linked login methods"),
							],
							[
								Type.bitstring("profile.login_methods.description"),
								Type.bitstring(
									"Review every sign-in method connected to your account, then add email, phone, Google, or Apple access when you need it.",
								),
							],
							[
								Type.bitstring("profile.login_methods.email.card.description"),
								Type.bitstring(
									"Add a passwordless email sign-in link for this account.",
								),
							],
							[
								Type.bitstring("profile.login_methods.email.card.heading"),
								Type.bitstring("Email magic link"),
							],
							[
								Type.bitstring(
									"profile.login_methods.email.error.invalid_email",
								),
								Type.bitstring(
									"Enter a valid email address before requesting a verification link.",
								),
							],
							[
								Type.bitstring("profile.login_methods.email.form.help"),
								Type.bitstring(
									"We'll email you a one-time verification link. If this address is already pending on your account, we'll send a fresh link.",
								),
							],
							[
								Type.bitstring("profile.login_methods.email.form.label"),
								Type.bitstring("Email address"),
							],
							[
								Type.bitstring("profile.login_methods.email.form.placeholder"),
								Type.bitstring("you@example.com"),
							],
							[
								Type.bitstring("profile.login_methods.email.form.submit"),
								Type.bitstring("Send verification link"),
							],
							[
								Type.bitstring("profile.login_methods.empty_state"),
								Type.bitstring(
									"No login methods are linked yet. Add one below so you can sign in more flexibly.",
								),
							],
							[
								Type.bitstring("profile.login_methods.error.generic"),
								Type.bitstring(
									"We couldn't update your login methods right now. Please try again.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.email.already_linked",
								),
								Type.bitstring(
									"That email address is already linked to your account.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.email.challenge_sent",
								),
								Type.bitstring(
									"We sent a verification link for that email address.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.email.conflict"),
								Type.bitstring(
									"We couldn't add that email address to this account.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.email.expired"),
								Type.bitstring(
									"That verification link has expired. Request a new email from your profile.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.email.invalid"),
								Type.bitstring(
									"That verification link is invalid. Request a new email from your profile.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.email.verified"),
								Type.bitstring("That email login method is now verified."),
							],
							[
								Type.bitstring("profile.login_methods.flash.generic_error"),
								Type.bitstring(
									"We couldn't update your login methods right now. Please try again.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.mobile.already_linked",
								),
								Type.bitstring(
									"That phone number is already linked to your account.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.mobile.conflict"),
								Type.bitstring(
									"We couldn't add that phone number to this account.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.mobile.ineligible"),
								Type.bitstring(
									"We couldn't finish linking that phone number. Request a fresh code from your profile.",
								),
							],
							[
								Type.bitstring("profile.login_methods.flash.mobile.verified"),
								Type.bitstring("That phone login method is now verified."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.already_linked",
								),
								Type.bitstring("Apple is already linked to this account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.cancelled",
								),
								Type.bitstring(
									"Apple linking was cancelled before it finished.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.conflict",
								),
								Type.bitstring("We couldn't link Apple to this account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.failed",
								),
								Type.bitstring("We couldn't finish linking Apple right now."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.linked",
								),
								Type.bitstring("Apple is now linked to your account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.session_mismatch",
								),
								Type.bitstring(
									"That Apple linking session is no longer valid. Start the link again from your profile.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.apple.start_failed",
								),
								Type.bitstring("We couldn't start Apple linking right now."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.already_linked",
								),
								Type.bitstring("Google is already linked to this account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.cancelled",
								),
								Type.bitstring(
									"Google linking was cancelled before it finished.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.conflict",
								),
								Type.bitstring("We couldn't link Google to this account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.failed",
								),
								Type.bitstring("We couldn't finish linking Google right now."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.linked",
								),
								Type.bitstring("Google is now linked to your account."),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.session_mismatch",
								),
								Type.bitstring(
									"That Google linking session is no longer valid. Start the link again from your profile.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.flash.oauth.google.start_failed",
								),
								Type.bitstring("We couldn't start Google linking right now."),
							],
							[
								Type.bitstring("profile.login_methods.heading"),
								Type.bitstring("Login methods"),
							],
							[
								Type.bitstring("profile.login_methods.method.summary"),
								Type.bitstring(
									"Status: %{status} · Verification: %{verification}",
								),
							],
							[
								Type.bitstring("profile.login_methods.mobile.card.description"),
								Type.bitstring(
									"Add a one-time-code phone sign-in for this account.",
								),
							],
							[
								Type.bitstring("profile.login_methods.mobile.card.heading"),
								Type.bitstring("Mobile OTP"),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.invalid_phone",
								),
								Type.bitstring(
									"Enter a valid South African mobile number to receive a verification code.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.otp_already_used",
								),
								Type.bitstring(
									"That code has already been used. Request a new one from your profile.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.otp_attempts_exceeded",
								),
								Type.bitstring(
									"Too many incorrect attempts. Request a new verification code to keep going.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.otp_expired",
								),
								Type.bitstring(
									"That verification code has expired. Request a new one from your profile.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.otp_invalid",
								),
								Type.bitstring(
									"That verification code doesn't look right. Check it and try again.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.error.rate_limited",
								),
								Type.bitstring(
									"You've requested too many verification codes. Please wait a moment and try again.",
								),
							],
							[
								Type.bitstring("profile.login_methods.mobile.form.help"),
								Type.bitstring(
									"We'll text you a 5-digit code. If this number is already pending on your account, we'll send a fresh code.",
								),
							],
							[
								Type.bitstring("profile.login_methods.mobile.form.label"),
								Type.bitstring("Mobile number"),
							],
							[
								Type.bitstring("profile.login_methods.mobile.form.placeholder"),
								Type.bitstring("082 123 4567"),
							],
							[
								Type.bitstring("profile.login_methods.mobile.form.submit"),
								Type.bitstring("Send verification code"),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.info.challenge_resent",
								),
								Type.bitstring(
									"We sent a fresh verification code to that number.",
								),
							],
							[
								Type.bitstring(
									"profile.login_methods.mobile.info.challenge_sent",
								),
								Type.bitstring("We sent a verification code to that number."),
							],
							[
								Type.bitstring("profile.login_methods.mobile.otp.intro"),
								Type.bitstring("Enter the 5-digit code we sent to %{phone}."),
							],
							[
								Type.bitstring("profile.login_methods.mobile.otp.label"),
								Type.bitstring("Verification code"),
							],
							[
								Type.bitstring("profile.login_methods.mobile.otp.placeholder"),
								Type.bitstring("12345"),
							],
							[
								Type.bitstring("profile.login_methods.mobile.otp.submit"),
								Type.bitstring("Verify phone login"),
							],
							[
								Type.bitstring("profile.login_methods.oauth.apple.description"),
								Type.bitstring(
									"Link Sign in with Apple to this account without creating a new profile.",
								),
							],
							[
								Type.bitstring("profile.login_methods.oauth.apple.heading"),
								Type.bitstring("Apple"),
							],
							[
								Type.bitstring("profile.login_methods.oauth.apple.submit"),
								Type.bitstring("Link Apple"),
							],
							[
								Type.bitstring(
									"profile.login_methods.oauth.google.description",
								),
								Type.bitstring(
									"Link Google sign-in to this account without creating a new profile.",
								),
							],
							[
								Type.bitstring("profile.login_methods.oauth.google.heading"),
								Type.bitstring("Google"),
							],
							[
								Type.bitstring("profile.login_methods.oauth.google.submit"),
								Type.bitstring("Link Google"),
							],
							[
								Type.bitstring("profile.login_methods.provider.apple"),
								Type.bitstring("Apple"),
							],
							[
								Type.bitstring("profile.login_methods.provider.email"),
								Type.bitstring("Email"),
							],
							[
								Type.bitstring("profile.login_methods.provider.google"),
								Type.bitstring("Google"),
							],
							[
								Type.bitstring(
									"profile.login_methods.provider.hidden_identifier",
								),
								Type.bitstring(
									"%{provider} keeps its provider subject hidden for security.",
								),
							],
							[
								Type.bitstring("profile.login_methods.provider.mobile"),
								Type.bitstring("Mobile"),
							],
							[
								Type.bitstring("profile.login_methods.provider.totp"),
								Type.bitstring("Authenticator app"),
							],
							[
								Type.bitstring("profile.login_methods.reachability.defunct"),
								Type.bitstring("Defunct"),
							],
							[
								Type.bitstring(
									"profile.login_methods.reachability.hard_bounce",
								),
								Type.bitstring("Undeliverable"),
							],
							[
								Type.bitstring("profile.login_methods.reachability.reachable"),
								Type.bitstring("Reachable"),
							],
							[
								Type.bitstring(
									"profile.login_methods.reachability.soft_bounce",
								),
								Type.bitstring("Temporarily failing"),
							],
							[
								Type.bitstring("profile.login_methods.reachability.unknown"),
								Type.bitstring("Reachability unknown"),
							],
							[
								Type.bitstring("profile.login_methods.sessions_link"),
								Type.bitstring("Manage active sessions"),
							],
							[
								Type.bitstring("profile.login_methods.status.active"),
								Type.bitstring("Active"),
							],
							[
								Type.bitstring("profile.login_methods.status.disabled"),
								Type.bitstring("Disabled"),
							],
							[
								Type.bitstring("profile.login_methods.status.revoked"),
								Type.bitstring("Revoked"),
							],
							[
								Type.bitstring("profile.login_methods.verification.expired"),
								Type.bitstring("Expired"),
							],
							[
								Type.bitstring("profile.login_methods.verification.failed"),
								Type.bitstring("Failed"),
							],
							[
								Type.bitstring("profile.login_methods.verification.pending"),
								Type.bitstring("Pending verification"),
							],
							[
								Type.bitstring("profile.login_methods.verification.verified"),
								Type.bitstring("Verified"),
							],
							[
								Type.bitstring("profile.page.complete_state"),
								Type.bitstring("Profile details on file"),
							],
							[
								Type.bitstring("profile.page.display_name.empty_state"),
								Type.bitstring(
									"Add a display name so people can recognize your account.",
								),
							],
							[
								Type.bitstring("profile.page.subtitle.default"),
								Type.bitstring("Keep your display name and avatar up to date."),
							],
							[
								Type.bitstring("profile.page.subtitle.missing_identity"),
								Type.bitstring(
									"Add a display name and avatar so your account feels more like you.",
								),
							],
							[
								Type.bitstring("profile.page.subtitle.with_identifier"),
								Type.bitstring(
									"Signed in as %{current_user_identifier}. Add a display name and avatar so your account feels more like you.",
								),
							],
							[
								Type.bitstring("profile.page.title.default"),
								Type.bitstring("Your profile"),
							],
							[
								Type.bitstring("profile.validation.avatar.absolute_https_url"),
								Type.bitstring("must be an absolute https:// URL"),
							],
							[
								Type.bitstring("profile.validation.avatar_upload.invalid_type"),
								Type.bitstring("Upload a PNG, JPEG, GIF, or WebP image."),
							],
							[
								Type.bitstring("profile.validation.display_name.max_length"),
								Type.bitstring("must be %{count} characters or fewer"),
							],
							[
								Type.bitstring("profile.validation.locale.invalid"),
								Type.bitstring("Choose a valid locale."),
							],
							[
								Type.bitstring("social_profile.achievements.add"),
								Type.bitstring("Add achievement"),
							],
							[
								Type.bitstring("social_profile.achievements.back"),
								Type.bitstring("Back"),
							],
							[
								Type.bitstring("social_profile.achievements.cancel"),
								Type.bitstring("Keep achievement"),
							],
							[
								Type.bitstring("social_profile.achievements.confirm_remove"),
								Type.bitstring("Remove achievement"),
							],
							[
								Type.bitstring("social_profile.achievements.date_format_hint"),
								Type.bitstring("Use YYYY-MM-DD."),
							],
							[
								Type.bitstring("social_profile.achievements.date_hint"),
								Type.bitstring("Leave blank if you do not know the date."),
							],
							[
								Type.bitstring("social_profile.achievements.date_label"),
								Type.bitstring("Date (optional)"),
							],
							[
								Type.bitstring("social_profile.achievements.description"),
								Type.bitstring(
									"Add personal or professional achievements. A title is required; the description and date are optional.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.description_label"),
								Type.bitstring("Description (optional)"),
							],
							[
								Type.bitstring("social_profile.achievements.dirty"),
								Type.bitstring("You have unsaved changes."),
							],
							[
								Type.bitstring("social_profile.achievements.down"),
								Type.bitstring("Move down %{name}"),
							],
							[
								Type.bitstring("social_profile.achievements.edit"),
								Type.bitstring("Edit %{name}"),
							],
							[
								Type.bitstring("social_profile.achievements.empty"),
								Type.bitstring(
									"Add an achievement you would like to include in your applications.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.invalid_date"),
								Type.bitstring("Enter a valid calendar date."),
							],
							[
								Type.bitstring("social_profile.achievements.invalid_order"),
								Type.bitstring(
									"Your achievements changed. Reload the saved list before ordering them again.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.invalid_value"),
								Type.bitstring("Enter a valid value."),
							],
							[
								Type.bitstring("social_profile.achievements.limit"),
								Type.bitstring(
									"You can save up to 100 achievements. Edit or remove an entry to add another.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.move_status"),
								Type.bitstring("Moved %{name} to position %{position}."),
							],
							[
								Type.bitstring("social_profile.achievements.order_saved"),
								Type.bitstring("Achievement order saved."),
							],
							[
								Type.bitstring("social_profile.achievements.reload"),
								Type.bitstring("Reload saved achievements"),
							],
							[
								Type.bitstring("social_profile.achievements.remove"),
								Type.bitstring("Remove achievement"),
							],
							[
								Type.bitstring(
									"social_profile.achievements.remove_description",
								),
								Type.bitstring(
									"Remove “%{name}”? This permanently removes it from your profile and applications.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.removed"),
								Type.bitstring("Achievement removed."),
							],
							[
								Type.bitstring("social_profile.achievements.reorder"),
								Type.bitstring("Reorder achievements"),
							],
							[
								Type.bitstring("social_profile.achievements.retry"),
								Type.bitstring("Try saving again"),
							],
							[
								Type.bitstring("social_profile.achievements.save"),
								Type.bitstring("Save changes"),
							],
							[
								Type.bitstring("social_profile.achievements.save_error"),
								Type.bitstring(
									"We could not save this achievement. Review the highlighted fields.",
								),
							],
							[
								Type.bitstring("social_profile.achievements.saved"),
								Type.bitstring("Achievement saved."),
							],
							[
								Type.bitstring("social_profile.achievements.saving"),
								Type.bitstring("Saving changes…"),
							],
							[
								Type.bitstring("social_profile.achievements.title"),
								Type.bitstring("Achievements"),
							],
							[
								Type.bitstring("social_profile.achievements.title_label"),
								Type.bitstring("Title"),
							],
							[
								Type.bitstring("social_profile.achievements.unknown"),
								Type.bitstring("Unavailable achievement"),
							],
							[
								Type.bitstring("social_profile.achievements.up"),
								Type.bitstring("Move up %{name}"),
							],
							[
								Type.bitstring(
									"social_profile.achievements.validation.future_date",
								),
								Type.bitstring("Choose today or an earlier date."),
							],
							[
								Type.bitstring("social_profile.achievements.validation.limit"),
								Type.bitstring("You can add up to %{count} achievements."),
							],
							[
								Type.bitstring("social_profile.application_details.save"),
								Type.bitstring("Save application details"),
							],
							[
								Type.bitstring(
									"social_profile.application_details.save_default",
								),
								Type.bitstring(
									"Disclosure defaults remain separate from public sharing.",
								),
							],
							[
								Type.bitstring("social_profile.application_details.save_error"),
								Type.bitstring("We could not save these application details."),
							],
							[
								Type.bitstring(
									"social_profile.application_details.save_error_help",
								),
								Type.bitstring(
									"Review the highlighted field. No partial disclosure changes were saved.",
								),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.add"),
								Type.bitstring("Add %{name}"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.added_status",
								),
								Type.bitstring("Added %{name}. Unsaved choices."),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.back"),
								Type.bitstring("Back to profile"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.achievement_orientation.description",
								),
								Type.bitstring(
									"Focusing and guiding self and others in accomplishing work objectives.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.achievement_orientation.name",
								),
								Type.bitstring("Achievement Orientation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.adaptability.description",
								),
								Type.bitstring(
									"Maintaining effectiveness when experiencing major changes in work tasks or the work environment; adjusting effectively to work within new work structures, processes, requirements, or cultures.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.adaptability.name",
								),
								Type.bitstring("Adaptability"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.analytical_thinking.description",
								),
								Type.bitstring(
									"Identifying and understanding issues, problems, and opportunities; comparing data from different sources to draw conclusions; using effective approaches for choosing a course of action or developing appropriate solutions; taking action that is consistent with available facts, constraints and probable consequences.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.analytical_thinking.name",
								),
								Type.bitstring("Analytical Thinking"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.applied_learning.description",
								),
								Type.bitstring(
									"Assimilating and applying new job-related information in a timely manner.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.applied_learning.name",
								),
								Type.bitstring("Applied Learning"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.attention_to_detail.description",
								),
								Type.bitstring(
									"Accomplishing tasks by considering all areas involved, no matter how small; showing concern for all aspects of the job; accurately checking processes and tasks; being watchful over a period of time.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.attention_to_detail.name",
								),
								Type.bitstring("Attention to Detail"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.building_trust.description",
								),
								Type.bitstring(
									"Interacting with others in a way that gives them confidence in one’s intentions and those of the organisation.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.building_trust.name",
								),
								Type.bitstring("Building Trust"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.client_focus.description",
								),
								Type.bitstring(
									"Effectively meeting customer needs; building productive customer relationships; taking responsibility for customer satisfaction and loyalty.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.client_focus.name",
								),
								Type.bitstring("Client Focus"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.coaching.description",
								),
								Type.bitstring(
									"Providing timely guidance and feedback to help others strengthen specific knowledge/skill areas needed to accomplish a task or solve a problem.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.coaching.name",
								),
								Type.bitstring("Coaching"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.communication_oral.description",
								),
								Type.bitstring(
									"Clearly conveying information and ideas to individuals or groups in a verbal manner that engages the audience and helps them understand and retain the message.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.communication_oral.name",
								),
								Type.bitstring("Communication (oral)"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.communication_written.description",
								),
								Type.bitstring(
									"Clearly conveying information and ideas through word based communication to individuals or groups in a manner that engages the audience and helps them understand and retain the message.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.communication_written.name",
								),
								Type.bitstring("Communication (written)"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.concern_for_safety.description",
								),
								Type.bitstring(
									"Identifying and correcting conditions that affect employee safety; upholding safety standards.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.concern_for_safety.name",
								),
								Type.bitstring("Concern for Safety"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.conflict_management.description",
								),
								Type.bitstring(
									"Dealing effectively with others in an antagonistic situation; using appropriate inter-personal styles and methods to reduce tension or conflict between two or more people.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.conflict_management.name",
								),
								Type.bitstring("Conflict Management"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.continuous_learning.description",
								),
								Type.bitstring(
									"Actively identifying new areas for learning; regularly creating and taking advantage of learning opportunities; using newly gained knowledge and skill on the job and learning through their application.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.continuous_learning.name",
								),
								Type.bitstring("Continuous Learning"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.creativity_and_innovation.description",
								),
								Type.bitstring(
									"Generating innovative solutions in work situations; trying different and novel ways to deal with work problems and opportunities.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.creativity_and_innovation.name",
								),
								Type.bitstring("Creativity and Innovation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.critical_judgment.description",
								),
								Type.bitstring(
									"Identifying and understanding issues, problems, and opportunities; comparing data from different sources to draw conclusions; using effective approaches for choosing a course of action or developing appropriate solutions; taking action that is consistent with available facts, constraints, and probable consequences.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.critical_judgment.name",
								),
								Type.bitstring("Critical Judgment"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.decision_making.description",
								),
								Type.bitstring(
									"Identifying and understanding issues, problems, and opportunities; comparing data from different sources to draw conclusions; using effective approaches for choosing a course of action or developing appropriate solutions; taking action that is consistent with available facts, constraints and probable consequences.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.decision_making.name",
								),
								Type.bitstring("Decision Making"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.delegation.description",
								),
								Type.bitstring(
									"Allocating decision-making authority and/or task responsibility to appropriate others to maximise the organisation’s and individuals’ effectiveness.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.delegation.name",
								),
								Type.bitstring("Delegation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.developing_others.description",
								),
								Type.bitstring(
									"Planning and supporting the development of individuals’ skills and abilities so that they can fulfil current or future job/role responsibilities more effectively.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.developing_others.name",
								),
								Type.bitstring("Developing Others"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.energy.description",
								),
								Type.bitstring(
									"Consistently maintaining high levels of activity or productivity; sustaining long working hours when necessary; operating with vigour, effectiveness, and determination over extended periods of time.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.energy.name",
								),
								Type.bitstring("Energy"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.entrepreneurial_thinking.description",
								),
								Type.bitstring(
									"Imagining possible new ends using a given set of means.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.entrepreneurial_thinking.name",
								),
								Type.bitstring("Entrepreneurial thinking"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.environmental_awareness.description",
								),
								Type.bitstring(
									"Advocacy for or work toward protecting the natural environment from destruction or pollution.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.environmental_awareness.name",
								),
								Type.bitstring("Environmental awareness"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.facilitation.description",
								),
								Type.bitstring(
									"Facilitating and organising resources and support.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.facilitation.name",
								),
								Type.bitstring("Facilitation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.gaining_commitment.description",
								),
								Type.bitstring(
									"Using appropriate interpersonal styles and techniques to gain acceptance of ideas or plans; modifying one’s own behaviour to accommodate tasks, situations, and individuals involved.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.gaining_commitment.name",
								),
								Type.bitstring("Gaining Commitment"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.impact_and_influence.description",
								),
								Type.bitstring(
									"Creating a good first impression, commanding attention and respect, showing an air of confidence.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.impact_and_influence.name",
								),
								Type.bitstring("Impact and Influence"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.initiative.description",
								),
								Type.bitstring(
									"Taking prompt action to accomplish objectives; taking action to achieve goals beyond what is required; being proactive.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.initiative.name",
								),
								Type.bitstring("Initiative"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.leading_and_managing_change.description",
								),
								Type.bitstring(
									"Encouraging others to seek opportunities for different and innovative approaches to addressing problems and opportunities; facilitating the implementation and acceptance of change within the workplace.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.leading_and_managing_change.name",
								),
								Type.bitstring("Leading and Managing Change"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.meeting_leadership.description",
								),
								Type.bitstring(
									"Ensuring that a meeting serves its business objectives while using appropriate interpersonal styles and methods and considering the needs and potential contributions of others.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.meeting_leadership.name",
								),
								Type.bitstring("Meeting Leadership"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.meeting_participation.description",
								),
								Type.bitstring(
									"Using appropriate interpersonal styles and methods to help reach a meeting’s goals while considering the needs and potential contributions of others.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.meeting_participation.name",
								),
								Type.bitstring("Meeting Participation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.motivating_others.description",
								),
								Type.bitstring(
									"The ability to lead, motivate, inspire, and encourage people.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.motivating_others.name",
								),
								Type.bitstring("Motivating Others"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.negotiating_and_mediating.description",
								),
								Type.bitstring(
									"Effectively exploring alternatives and positions to reach outcomes that gain the support and acceptance of all parties.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.negotiating_and_mediating.name",
								),
								Type.bitstring("Negotiating and Mediating"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.performance_management.description",
								),
								Type.bitstring(
									"Displaying a commitment to activities which ensure that goals are consistently being met in an effective and efficient manner.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.performance_management.name",
								),
								Type.bitstring("Performance Management"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.planning_and_organizing.description",
								),
								Type.bitstring(
									"Establishing courses of action for self and others to ensure that work is completed efficiently.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.planning_and_organizing.name",
								),
								Type.bitstring("Planning and Organizing"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.presentation.description",
								),
								Type.bitstring(
									"Presenting ideas effectively to individuals or groups when given time to prepare; delivering presentations suited to the characteristics and needs of the audience.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.presentation.name",
								),
								Type.bitstring("Presentation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.proactivity_self_motivation.description",
								),
								Type.bitstring(
									"Displaying behaviour that involves acting in advance of a future situation, rather than just reacting.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.proactivity_self_motivation.name",
								),
								Type.bitstring("Proactivity & Self-Motivation"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.problem_solving.description",
								),
								Type.bitstring(
									"The ability to find solutions to difficult or complex issues.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.problem_solving.name",
								),
								Type.bitstring("Problem Solving"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.quality_focus.description",
								),
								Type.bitstring("Displaying a focus of continuous improvement."),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.quality_focus.name",
								),
								Type.bitstring("Quality Focus"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.relationship_building.description",
								),
								Type.bitstring(
									"Identifying opportunities and taking action to build strategic relationships between one’s area and other areas, teams, departments, units, or organisations to help achieve business goals.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.relationship_building.name",
								),
								Type.bitstring("Relationship building"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.reliability.description",
								),
								Type.bitstring(
									"Personally responsible; completes work in a timely, consistent manner; works hours necessary to complete assigned work; is regularly present and punctual; arrives prepared for work; is committed to doing the best job possible; keeps commitments.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.reliability.name",
								),
								Type.bitstring("Reliability"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.resilience_tenacity.description",
								),
								Type.bitstring(
									"Staying with a position or plan of action until the desired objective is obtained or is no longer reasonably attainable.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.resilience_tenacity.name",
								),
								Type.bitstring("Resilience / Tenacity"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.risk_taking.description",
								),
								Type.bitstring(
									"The ability to do something that involves danger or risk in order to achieve a goal.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.risk_taking.name",
								),
								Type.bitstring("Risk Taking"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.sales_ability_persuasiveness.description",
								),
								Type.bitstring(
									"Using appropriate interpersonal styles and communication methods to gain acceptance of a product, service, or idea from prospects and clients",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.sales_ability_persuasiveness.name",
								),
								Type.bitstring("Sales Ability/Persuasiveness"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.self_awareness.description",
								),
								Type.bitstring(
									"Displaying a conscious knowledge of one's own character, feelings, motives, and desires.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.self_awareness.name",
								),
								Type.bitstring("Self-Awareness"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.self_confidence.description",
								),
								Type.bitstring(
									"Displaying confidence in oneself and in one's powers and abilities.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.self_confidence.name",
								),
								Type.bitstring("Self-Confidence"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.social_responsibility.description",
								),
								Type.bitstring(
									"An obligation to act for the benefit of society at large.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.social_responsibility.name",
								),
								Type.bitstring("Social Responsibility"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.stress_management.description",
								),
								Type.bitstring(
									"Maintaining stable performance under pressure or opposition (such as time pressure or job ambiguity); handling stress in a manner that is acceptable to others and to the organisation.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.stress_management.name",
								),
								Type.bitstring("Stress Management"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.team_leadership.description",
								),
								Type.bitstring(
									"Using appropriate methods and a flexible interpersonal style to help build a cohesive team; facilitating the completion of team goals",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.team_leadership.name",
								),
								Type.bitstring("Team Leadership"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.teamwork.description",
								),
								Type.bitstring(
									"Actively participating as a member of a team to move the team toward the completion of goals.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.teamwork.name",
								),
								Type.bitstring("Teamwork"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.thinking_strategically.description",
								),
								Type.bitstring(
									"Identifying and understanding issues, problems, and opportunities; comparing data from different sources to draw conclusions; using effective approaches for choosing a course of action or developing appropriate solutions; taking action that is consistent with available facts, constraints, and probable consequences",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.thinking_strategically.name",
								),
								Type.bitstring("Thinking Strategically"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.time_management_priority_setting.description",
								),
								Type.bitstring(
									"Effectively managing one’s time and resources to ensure that work is completed efficiently.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.time_management_priority_setting.name",
								),
								Type.bitstring("Time Management/Priority Setting"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.valuing_cultural_diversity.description",
								),
								Type.bitstring(
									"Appreciating and leveraging the capabilities, insights, and ideas of all individuals; working effectively with individuals of diverse style, ability, and motivation.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.valuing_cultural_diversity.name",
								),
								Type.bitstring("Valuing Cultural Diversity"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.visioning_and_alignment.description",
								),
								Type.bitstring(
									"Keeping the organisation’s vision and values at the forefront of decision-making and action.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.visioning_and_alignment.name",
								),
								Type.bitstring("Visioning and Alignment"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.wellness_focus.description",
								),
								Type.bitstring(
									"Displaying a quality or state of being healthy in body and mind.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.wellness_focus.name",
								),
								Type.bitstring("Wellness focus"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.work_ethic.description",
								),
								Type.bitstring(
									"A belief in the moral benefit and importance of work and its inherentability to strengthen character.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.work_ethic.name",
								),
								Type.bitstring("Work ethic"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.work_standards.description",
								),
								Type.bitstring(
									"Setting high standards of performance for self and others; assuming responsibility and accountability for successfully completing assignments or tasks; self-imposing standards of excellence rather than having standards imposed.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.catalogue.work_standards.name",
								),
								Type.bitstring("Work Standards"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.count"),
								Type.bitstring("%{count} of 5 selected"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.description",
								),
								Type.bitstring(
									"Choose up to five strengths that describe you. These are self-described competencies, not assessment results. Save when you are ready.",
								),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.dirty"),
								Type.bitstring("Unsaved choices"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.down"),
								Type.bitstring("Move %{name} down"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.edit"),
								Type.bitstring("Edit behavioral competencies"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.empty"),
								Type.bitstring(
									"No behavioral competencies selected. This section is optional.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.invalid_search",
								),
								Type.bitstring(
									"Enter a search of up to 120 characters and a valid page. Your choices are kept; try searching again.",
								),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.limit"),
								Type.bitstring(
									"Choose up to five behavioral competencies. Remove a choice before adding another.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.move_status",
								),
								Type.bitstring("Moved %{name} to position %{position}."),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.next"),
								Type.bitstring("Next page"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.no_results",
								),
								Type.bitstring(
									"No competencies match this search. Try another word; your choices are kept.",
								),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.previous",
								),
								Type.bitstring("Previous page"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.remove"),
								Type.bitstring("Remove %{name}"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.removed_status",
								),
								Type.bitstring("Removed %{name}. Unsaved choices."),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.results",
								),
								Type.bitstring("Catalogue"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.retired",
								),
								Type.bitstring("Unavailable for new selection"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.retry"),
								Type.bitstring("Try saving again"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.save"),
								Type.bitstring("Save competencies"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.saved"),
								Type.bitstring("Behavioral competencies saved."),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.saving"),
								Type.bitstring("Saving competencies…"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.search"),
								Type.bitstring("Search competencies"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.selected",
								),
								Type.bitstring("Your choices"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.selected_action",
								),
								Type.bitstring("Selected"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.title"),
								Type.bitstring("Behavioral competencies"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.unknown",
								),
								Type.bitstring("Unknown competency"),
							],
							[
								Type.bitstring("social_profile.behavioral_competencies.up"),
								Type.bitstring("Move %{name} up"),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.validation.duplicate",
								),
								Type.bitstring("Choose each behavioral competency only once."),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.validation.invalid",
								),
								Type.bitstring("Choose valid competency entries."),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.validation.limit",
								),
								Type.bitstring("Choose up to five behavioral competencies."),
							],
							[
								Type.bitstring(
									"social_profile.behavioral_competencies.validation.unavailable",
								),
								Type.bitstring(
									"A selected competency is unavailable. Review your choices and try again.",
								),
							],
							[
								Type.bitstring(
									"social_profile.date_of_birth.choose_replacement",
								),
								Type.bitstring("Or choose a replacement date"),
							],
							[
								Type.bitstring("social_profile.date_of_birth.error_hint"),
								Type.bitstring(
									"Use YYYY-MM-DD, or leave blank if you do not know the date.",
								),
							],
							[
								Type.bitstring("social_profile.date_of_birth.hint"),
								Type.bitstring("Leave blank if you do not know the date."),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.autism_spectrum_disorder",
								),
								Type.bitstring("Autism Spectrum Disorder"),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.hearing_impairment",
								),
								Type.bitstring("Hearing Impairment"),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.mental_health_condition",
								),
								Type.bitstring("Mental Health Condition"),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.physical_disability",
								),
								Type.bitstring("Physical Disability"),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.progressive_chronic_condition",
								),
								Type.bitstring("Progressive Chronic Condition"),
							],
							[
								Type.bitstring(
									"social_profile.disability_category.vision_impairment",
								),
								Type.bitstring("Vision Impairment"),
							],
							[
								Type.bitstring("social_profile.ethnicity.asian"),
								Type.bitstring("Asian"),
							],
							[
								Type.bitstring("social_profile.ethnicity.black_african"),
								Type.bitstring("Black African"),
							],
							[
								Type.bitstring(
									"social_profile.ethnicity.black_or_african_descent",
								),
								Type.bitstring("Black or African descent"),
							],
							[
								Type.bitstring("social_profile.ethnicity.hispanic_or_latino"),
								Type.bitstring("Hispanic or Latino"),
							],
							[
								Type.bitstring("social_profile.ethnicity.indian"),
								Type.bitstring("Indian"),
							],
							[
								Type.bitstring(
									"social_profile.ethnicity.middle_eastern_or_north_african",
								),
								Type.bitstring("Middle Eastern or North African"),
							],
							[
								Type.bitstring("social_profile.ethnicity.mixed_or_multiracial"),
								Type.bitstring("Mixed or multiracial"),
							],
							[
								Type.bitstring("social_profile.ethnicity.native_or_indigenous"),
								Type.bitstring("Native or Indigenous"),
							],
							[
								Type.bitstring("social_profile.ethnicity.other"),
								Type.bitstring("Other"),
							],
							[
								Type.bitstring("social_profile.ethnicity.pacific_islander"),
								Type.bitstring("Pacific Islander"),
							],
							[
								Type.bitstring(
									"social_profile.ethnicity.south_african_coloured",
								),
								Type.bitstring("South African Coloured"),
							],
							[
								Type.bitstring(
									"social_profile.ethnicity.south_african_coloured.za",
								),
								Type.bitstring("Coloured"),
							],
							[
								Type.bitstring("social_profile.ethnicity.white"),
								Type.bitstring("White"),
							],
							[
								Type.bitstring("social_profile.experience.description"),
								Type.bitstring(
									"Add work history, volunteering, memberships, and other experience you may want to share.",
								),
							],
							[
								Type.bitstring("social_profile.experience.empty.action"),
								Type.bitstring("Add experience"),
							],
							[
								Type.bitstring("social_profile.experience.empty.description"),
								Type.bitstring(
									"Add a role when you are ready. Draft records stay private until you deliberately include them in a profile or application.",
								),
							],
							[
								Type.bitstring("social_profile.experience.empty.heading"),
								Type.bitstring("No experience records yet"),
							],
							[
								Type.bitstring("social_profile.experience.empty.unavailable"),
								Type.bitstring("Experience tools coming soon"),
							],
							[
								Type.bitstring("social_profile.experience.heading"),
								Type.bitstring("Experience"),
							],
							[
								Type.bitstring("social_profile.flash.saved"),
								Type.bitstring("Your professional profile has been saved."),
							],
							[
								Type.bitstring("social_profile.form.error.save_failed"),
								Type.bitstring("We couldn't save your professional profile."),
							],
							[
								Type.bitstring("social_profile.form.social_links.website"),
								Type.bitstring("Website"),
							],
							[
								Type.bitstring("social_profile.gender_identity.female"),
								Type.bitstring("Female"),
							],
							[
								Type.bitstring("social_profile.gender_identity.male"),
								Type.bitstring("Male"),
							],
							[
								Type.bitstring("social_profile.gender_identity.non_binary"),
								Type.bitstring("Non-binary"),
							],
							[
								Type.bitstring("social_profile.gender_identity.other"),
								Type.bitstring("Other"),
							],
							[
								Type.bitstring(
									"social_profile.gender_identity.prefer_not_to_say",
								),
								Type.bitstring("Prefer not to say"),
							],
							[
								Type.bitstring("social_profile.gender_identity.trans_man"),
								Type.bitstring("Trans man"),
							],
							[
								Type.bitstring("social_profile.gender_identity.trans_woman"),
								Type.bitstring("Trans woman"),
							],
							[
								Type.bitstring("social_profile.languages.add"),
								Type.bitstring("Add a language"),
							],
							[
								Type.bitstring("social_profile.languages.back"),
								Type.bitstring("Back"),
							],
							[
								Type.bitstring("social_profile.languages.cancel"),
								Type.bitstring("Cancel"),
							],
							[
								Type.bitstring("social_profile.languages.change_language"),
								Type.bitstring("Change language"),
							],
							[
								Type.bitstring("social_profile.languages.choose"),
								Type.bitstring("Choose %{name}"),
							],
							[
								Type.bitstring("social_profile.languages.choose_level"),
								Type.bitstring("Choose a proficiency level"),
							],
							[
								Type.bitstring("social_profile.languages.choose_prompt"),
								Type.bitstring("Choose a language"),
							],
							[
								Type.bitstring("social_profile.languages.confirm_remove"),
								Type.bitstring("Yes, remove language"),
							],
							[
								Type.bitstring("social_profile.languages.description"),
								Type.bitstring(
									"Choose the languages you use and describe your proficiency yourself. These answers are independent of the language used to display Leaply.",
								),
							],
							[
								Type.bitstring("social_profile.languages.dirty"),
								Type.bitstring("You have unsaved changes."),
							],
							[
								Type.bitstring("social_profile.languages.down"),
								Type.bitstring("Move %{name} down"),
							],
							[
								Type.bitstring("social_profile.languages.duplicate_link"),
								Type.bitstring("Edit your existing language entry"),
							],
							[
								Type.bitstring("social_profile.languages.edit"),
								Type.bitstring("Edit %{name}"),
							],
							[
								Type.bitstring("social_profile.languages.empty"),
								Type.bitstring("No languages added yet."),
							],
							[
								Type.bitstring("social_profile.languages.invalid_order"),
								Type.bitstring(
									"Your saved languages have changed. Reload the list, then arrange it again.",
								),
							],
							[
								Type.bitstring("social_profile.languages.invalid_search"),
								Type.bitstring(
									"Use up to 120 characters and a valid results page.",
								),
							],
							[
								Type.bitstring("social_profile.languages.language"),
								Type.bitstring("Language"),
							],
							[
								Type.bitstring("social_profile.languages.manage"),
								Type.bitstring("Edit languages"),
							],
							[
								Type.bitstring("social_profile.languages.move_status"),
								Type.bitstring(
									"%{name} moved to position %{position}. Save to keep this order.",
								),
							],
							[
								Type.bitstring("social_profile.languages.next"),
								Type.bitstring("Next"),
							],
							[
								Type.bitstring("social_profile.languages.no_results"),
								Type.bitstring(
									"No languages match this search. Try another name.",
								),
							],
							[
								Type.bitstring("social_profile.languages.order_saved"),
								Type.bitstring("Language order saved."),
							],
							[
								Type.bitstring("social_profile.languages.previous"),
								Type.bitstring("Previous"),
							],
							[
								Type.bitstring("social_profile.languages.proficiency"),
								Type.bitstring("Your proficiency"),
							],
							[
								Type.bitstring("social_profile.languages.proficiency.basic"),
								Type.bitstring("Basic proficiency"),
							],
							[
								Type.bitstring(
									"social_profile.languages.proficiency.limited_working",
								),
								Type.bitstring("Limited working proficiency"),
							],
							[
								Type.bitstring("social_profile.languages.proficiency.native"),
								Type.bitstring("Native language"),
							],
							[
								Type.bitstring(
									"social_profile.languages.proficiency.professional_working",
								),
								Type.bitstring("Professional working proficiency"),
							],
							[
								Type.bitstring("social_profile.languages.reload"),
								Type.bitstring("Reload saved languages"),
							],
							[
								Type.bitstring("social_profile.languages.remove"),
								Type.bitstring("Remove language"),
							],
							[
								Type.bitstring("social_profile.languages.remove_description"),
								Type.bitstring(
									"Remove this language from your profile? It will also disappear from applications and shared profile views.",
								),
							],
							[
								Type.bitstring("social_profile.languages.removed"),
								Type.bitstring("Language removed."),
							],
							[
								Type.bitstring("social_profile.languages.results"),
								Type.bitstring("Available languages"),
							],
							[
								Type.bitstring("social_profile.languages.retry"),
								Type.bitstring("Try saving again"),
							],
							[
								Type.bitstring("social_profile.languages.save"),
								Type.bitstring("Save"),
							],
							[
								Type.bitstring("social_profile.languages.saved"),
								Type.bitstring("Language saved."),
							],
							[
								Type.bitstring("social_profile.languages.saving"),
								Type.bitstring("Saving your languages…"),
							],
							[
								Type.bitstring("social_profile.languages.search"),
								Type.bitstring("Search languages"),
							],
							[
								Type.bitstring("social_profile.languages.search_failed"),
								Type.bitstring(
									"Search could not be completed. Select Search to try again.",
								),
							],
							[
								Type.bitstring("social_profile.languages.search_results"),
								Type.bitstring("Languages shown: %{count}."),
							],
							[
								Type.bitstring("social_profile.languages.searching"),
								Type.bitstring("Searching languages…"),
							],
							[
								Type.bitstring("social_profile.languages.selected"),
								Type.bitstring("Selected"),
							],
							[
								Type.bitstring("social_profile.languages.title"),
								Type.bitstring("Languages"),
							],
							[
								Type.bitstring("social_profile.languages.unknown"),
								Type.bitstring("Unavailable language"),
							],
							[
								Type.bitstring("social_profile.languages.up"),
								Type.bitstring("Move %{name} up"),
							],
							[
								Type.bitstring("social_profile.languages.use_selected"),
								Type.bitstring("Continue with selected language"),
							],
							[
								Type.bitstring("social_profile.languages.validation.duplicate"),
								Type.bitstring("This language is already on your profile."),
							],
							[
								Type.bitstring("social_profile.languages.validation.immutable"),
								Type.bitstring(
									"Remove this entry and add a new one to change its language.",
								),
							],
							[
								Type.bitstring("social_profile.languages.validation.limit"),
								Type.bitstring("You can add up to %{count} languages."),
							],
							[
								Type.bitstring("social_profile.languages.validation.unknown"),
								Type.bitstring("Choose a language from the list."),
							],
							[
								Type.bitstring("social_profile.people.description"),
								Type.bitstring(
									"Discover public professional profiles shared by the Leaply community.",
								),
							],
							[
								Type.bitstring("social_profile.people.empty.description"),
								Type.bitstring(
									"No public profiles are available yet. Check back soon.",
								),
							],
							[
								Type.bitstring("social_profile.people.empty.heading"),
								Type.bitstring("No public profiles yet"),
							],
							[
								Type.bitstring("social_profile.people.heading"),
								Type.bitstring("People"),
							],
							[
								Type.bitstring("social_profile.people.manage_profile"),
								Type.bitstring("Manage profile"),
							],
							[
								Type.bitstring("social_profile.people.opportunities"),
								Type.bitstring("Open to opportunities"),
							],
							[
								Type.bitstring("social_profile.people.pagination.next"),
								Type.bitstring("Next page"),
							],
							[
								Type.bitstring("social_profile.people.pagination.previous"),
								Type.bitstring("Previous page"),
							],
							[
								Type.bitstring("social_profile.public.back_to_people"),
								Type.bitstring("Back to people"),
							],
							[
								Type.bitstring("social_profile.public.competencies.heading"),
								Type.bitstring("Technical competencies"),
							],
							[
								Type.bitstring("social_profile.public.date_of_birth"),
								Type.bitstring("Date of birth"),
							],
							[
								Type.bitstring("social_profile.public.disability"),
								Type.bitstring("Limiting disability disclosed"),
							],
							[
								Type.bitstring("social_profile.public.empty"),
								Type.bitstring("Nothing to show yet."),
							],
							[
								Type.bitstring("social_profile.public.ethnicity"),
								Type.bitstring("Ethnicity"),
							],
							[
								Type.bitstring("social_profile.public.experience.heading"),
								Type.bitstring("Experience"),
							],
							[
								Type.bitstring("social_profile.public.gender_identity"),
								Type.bitstring("Gender identity"),
							],
							[
								Type.bitstring("social_profile.public.interests.heading"),
								Type.bitstring("Interests"),
							],
							[
								Type.bitstring("social_profile.public.links.heading"),
								Type.bitstring("Links"),
							],
							[
								Type.bitstring("social_profile.public.opportunities"),
								Type.bitstring("Open to opportunities"),
							],
							[
								Type.bitstring(
									"social_profile.public.personal_details.heading",
								),
								Type.bitstring("Personal details"),
							],
							[
								Type.bitstring("social_profile.public.profile.label"),
								Type.bitstring("Professional profile"),
							],
							[
								Type.bitstring("social_profile.public.qualifications.heading"),
								Type.bitstring("Qualifications"),
							],
							[
								Type.bitstring("social_profile.public.skills.heading"),
								Type.bitstring("Skills"),
							],
							[
								Type.bitstring("social_profile.public.summary.heading"),
								Type.bitstring("About"),
							],
							[
								Type.bitstring("social_profile.public.tools.heading"),
								Type.bitstring("Professional tools"),
							],
							[
								Type.bitstring("social_profile.qualifications.description"),
								Type.bitstring(
									"Academic history will appear here when transcript-backed qualifications are available.",
								),
							],
							[
								Type.bitstring("social_profile.qualifications.empty.action"),
								Type.bitstring("Add academic history"),
							],
							[
								Type.bitstring(
									"social_profile.qualifications.empty.description",
								),
								Type.bitstring(
									"Add transcript-backed academic history here. You can return to review or update it at any time.",
								),
							],
							[
								Type.bitstring("social_profile.qualifications.empty.heading"),
								Type.bitstring("No academic records yet"),
							],
							[
								Type.bitstring(
									"social_profile.qualifications.empty.unavailable",
								),
								Type.bitstring("Academic tools coming soon"),
							],
							[
								Type.bitstring("social_profile.qualifications.heading"),
								Type.bitstring("Qualifications"),
							],
							[
								Type.bitstring("social_profile.save.dirty"),
								Type.bitstring("Unsaved changes"),
							],
							[
								Type.bitstring("social_profile.save.failed"),
								Type.bitstring(
									"Save failed. Review the highlighted fields and try again.",
								),
							],
							[
								Type.bitstring("social_profile.save.pending_label"),
								Type.bitstring("Saving…"),
							],
							[
								Type.bitstring("social_profile.save.pending_status"),
								Type.bitstring("Saving changes…"),
							],
							[
								Type.bitstring("social_profile.save.retry"),
								Type.bitstring("Try saving again"),
							],
							[
								Type.bitstring(
									"social_profile.validation.date_of_birth.future",
								),
								Type.bitstring("Date of birth cannot be in the future."),
							],
							[
								Type.bitstring(
									"social_profile.validation.date_of_birth.invalid",
								),
								Type.bitstring("Enter a valid calendar date."),
							],
							[
								Type.bitstring(
									"social_profile.validation.disability_category.required",
								),
								Type.bitstring("Choose a disability category."),
							],
							[
								Type.bitstring("social_profile.validation.handle.invalid"),
								Type.bitstring(
									"Use lowercase letters, numbers, dots, underscores, or hyphens for the public handle.",
								),
							],
							[
								Type.bitstring("social_profile.validation.handle.taken"),
								Type.bitstring("This public handle is already in use."),
							],
							[
								Type.bitstring("social_profile.validation.list.max_items"),
								Type.bitstring("Use at most %{count} items."),
							],
							[
								Type.bitstring(
									"social_profile.validation.list.value_max_length",
								),
								Type.bitstring(
									"Each item must be at most %{count} characters.",
								),
							],
							[
								Type.bitstring("social_profile.validation.max_length"),
								Type.bitstring("must be at most %{count} characters"),
							],
							[
								Type.bitstring(
									"social_profile.validation.public_disclosure.confirmation_required",
								),
								Type.bitstring(
									"Confirm the public disclosure change before saving.",
								),
							],
							[
								Type.bitstring("social_profile.validation.required"),
								Type.bitstring("This field is required."),
							],
							[
								Type.bitstring("social_profile.validation.selection.invalid"),
								Type.bitstring("Choose one of the available options."),
							],
							[
								Type.bitstring(
									"social_profile.validation.social_links.invalid",
								),
								Type.bitstring("Enter a valid %{platform} URL."),
							],
							[
								Type.bitstring("social_profile.visibility.description"),
								Type.bitstring("Choose how this profile is shared."),
							],
							[
								Type.bitstring("social_profile.visibility.heading"),
								Type.bitstring("Visibility"),
							],
							[
								Type.bitstring("social_profile.visibility.private.description"),
								Type.bitstring(
									"Only you can view or edit this profile. Public routes return not found.",
								),
							],
							[
								Type.bitstring("social_profile.visibility.private.label"),
								Type.bitstring("Private"),
							],
							[
								Type.bitstring("social_profile.visibility.public.description"),
								Type.bitstring(
									"Anyone can discover this profile under /people and open it directly.",
								),
							],
							[
								Type.bitstring("social_profile.visibility.public.label"),
								Type.bitstring("Public"),
							],
							[
								Type.bitstring(
									"social_profile.visibility.unlisted.description",
								),
								Type.bitstring(
									"Anyone with the direct link can open this profile, but it stays out of discovery.",
								),
							],
							[
								Type.bitstring("social_profile.visibility.unlisted.label"),
								Type.bitstring("Unlisted"),
							],
							[
								Type.bitstring("social_profile_item.validation.source_owner"),
								Type.bitstring(
									"The selected record does not belong to this profile.",
								),
							],
						]),
						context.vars.key_0,
						context.vars.key_0,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Helpers.ThemeText",
		"get",
		1,
		"public",
		[
			{
				params: (context) => [Type.variablePattern("key_0")],
				guards: [],
				body: (context) => {
					return Elixir_Map["get/3"](
						Type.map([
							[Type.bitstring("shared.theme.dark"), Type.bitstring("Dark")],
							[Type.bitstring("shared.theme.light"), Type.bitstring("Light")],
							[
								Type.bitstring("shared.theme.switch_to_dark"),
								Type.bitstring("Switch to dark mode"),
							],
							[
								Type.bitstring("shared.theme.switch_to_light"),
								Type.bitstring("Switch to light mode"),
							],
						]),
						context.vars.key_0,
						context.vars.key_0,
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Layouts.RootLayout",
		"__props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](
						Type.list([
							Type.tuple([
								Type.atom("show_global_flash"),
								Type.atom("boolean"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("true")]),
								]),
							]),
							Type.tuple([
								Type.atom("flash_error"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("flash_info"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("csrf_token"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.Hologram.Runtime"),
											Type.atom("csrf_token"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("structured_metadata"),
								Type.atom("map"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("profile_location"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.LeaplyWeb.ProfileLocation"),
											Type.atom("url"),
										]),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("robots"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("open_graph_image"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("open_graph_url"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("open_graph_description"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("open_graph_title"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("canonical_url"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("page_description"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
								]),
							]),
							Type.tuple([
								Type.atom("interface_context"),
								Type.atom("string"),
								Type.list([
									Type.tuple([
										Type.atom("default"),
										Type.bitstring("applicant"),
									]),
								]),
							]),
							Type.tuple([
								Type.atom("page_title"),
								Type.atom("string"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.bitstring("Leaply")]),
								]),
							]),
							Type.tuple([
								Type.atom("navigation"),
								Type.atom("map"),
								Type.list([
									Type.tuple([Type.atom("default"), Type.atom("nil")]),
									Type.tuple([
										Type.atom("from_context"),
										Type.tuple([
											Type.atom("Elixir.LeaplyWeb.ApplicationNavigation"),
											Type.atom("navigation"),
										]),
									]),
								]),
							]),
						]),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Layouts.RootLayout",
		"action",
		3,
		"public",
		[
			{
				params: (context) => [
					Type.atom("restore_profile_location"),
					Type.matchPlaceholder(),
					Type.variablePattern("component_1"),
				],
				guards: [],
				body: (context) => {
					Elixir_Hologram_JS["exec/1"](
						Type.bitstring(
							'document.dispatchEvent(new CustomEvent("leaply:profile-location-ready"));\n',
						),
					);
					return context.vars.component_1;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Layouts.RootLayout",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Type.tuple([Type.atom("doctype"), Type.bitstring("html")]),
										Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("html"),
											Type.list([
												Type.tuple([
													Type.bitstring("lang"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("en"),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-interface-context"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("interface_context"),
																),
															]),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("data-theme"),
													Type.list([
														Type.tuple([
															Type.atom("expression"),
															Type.tuple([
																Type.bitstring([
																	Type.bitstringSegment(
																		Type.string("leaply-"),
																		{ type: "binary" },
																	),
																	Type.bitstringSegment(
																		Elixir_String_Chars["to_string/1"](
																			Interpreter.dotOperator(
																				context.vars.vars_0,
																				Type.atom("interface_context"),
																			),
																		),
																		{ type: "binary" },
																	),
																	Type.bitstringSegment(Type.string("-light"), {
																		type: "binary",
																	}),
																]),
															]),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("head"),
													Type.list([]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("meta"),
															Type.list([
																Type.tuple([
																	Type.bitstring("charset"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("utf-8"),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("meta"),
															Type.list([
																Type.tuple([
																	Type.bitstring("name"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("viewport"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("content"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"width=device-width, initial-scale=1",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("meta"),
															Type.list([
																Type.tuple([
																	Type.bitstring("name"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("csrf-token"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("content"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("csrf_token"),
																				),
																			]),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("meta"),
															Type.list([
																Type.tuple([
																	Type.bitstring("name"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("color-scheme"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("content"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("light dark"),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("meta"),
															Type.list([
																Type.tuple([
																	Type.bitstring("id"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("theme-color"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("name"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("theme-color"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("content"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("#F7F8FC"),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("title"),
															Type.list([]),
															Type.list([
																Type.tuple([
																	Type.atom("expression"),
																	Type.tuple([
																		Interpreter.dotOperator(
																			context.vars.vars_0,
																			Type.atom("page_title"),
																		),
																	]),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("page_description"),
															),
															[
																{
																	match: Type.variablePattern("x_1"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_1,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_1,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("name"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("description"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"page_description",
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("canonical_url"),
															),
															[
																{
																	match: Type.variablePattern("x_2"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_2,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_2,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("link"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("rel"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("canonical"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("href"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("canonical_url"),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("open_graph_title"),
															),
															[
																{
																	match: Type.variablePattern("x_3"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_3,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_3,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("property"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("og:title"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"open_graph_title",
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("open_graph_description"),
															),
															[
																{
																	match: Type.variablePattern("x_4"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_4,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_4,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("property"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"og:description",
																								),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"open_graph_description",
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("open_graph_url"),
															),
															[
																{
																	match: Type.variablePattern("x_5"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_5,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_5,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("property"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("og:url"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("open_graph_url"),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("open_graph_image"),
															),
															[
																{
																	match: Type.variablePattern("x_6"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_6,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_6,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("property"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("og:image"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"open_graph_image",
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("robots"),
															),
															[
																{
																	match: Type.variablePattern("x_7"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_7,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_7,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("meta"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("name"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("robots"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("content"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom("robots"),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("structured_metadata_json"),
															),
															[
																{
																	match: Type.variablePattern("x_8"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_8,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_8,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("component"),
																				Type.atom(
																					"Elixir.LeaplyWeb.Hologram.Components.Head.StructuredData",
																				),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("json"),
																						Type.list([
																							Type.tuple([
																								Type.atom("expression"),
																								Type.tuple([
																									Interpreter.dotOperator(
																										context.vars.vars_0,
																										Type.atom(
																											"structured_metadata_json",
																										),
																									),
																								]),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("script"),
															Type.list([]),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		'\n      (function () {\n        var storedTheme = null;\n        try { storedTheme = window.localStorage.getItem("leaply:theme"); } catch (_error) {}\n        var mode = storedTheme === "light" || storedTheme === "dark"\n          ? storedTheme\n          : (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");\n        var path = window.location.pathname;\n        var adminPath = path === "/design-system" || path === "/admin" || path.indexOf("/admin/") === 0 || path === "/docs" || path.indexOf("/docs/") === 0;\n        var recruiterPath = path === "/accounts" || path.indexOf("/accounts/") === 0 || path === "/tracker" || path.indexOf("/tracker/") === 0 || new RegExp("^/listings/[^/]+/(screening|phases/[^/]+/screening)(/|$)").test(path);\n        var context = adminPath ? "admin" : (recruiterPath ? "recruiter" : "applicant");\n        var root = document.documentElement;\n        root.classList.toggle("dark", mode === "dark");\n        root.dataset.interfaceContext = context;\n        root.dataset.themeMode = mode;\n        root.dataset.theme = "leaply-" + context + "-" + mode;\n        root.style.colorScheme = mode;\n        var lightColor = context === "recruiter" ? "#F4F7FB" : "#F7F8FC";\n        var darkColor = context === "recruiter" ? "#0B1220" : "#080D19";\n        document.getElementById("theme-color").setAttribute("content", mode === "dark" ? darkColor : lightColor);\n      })();\n    ',
																	),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("link"),
															Type.list([
																Type.tuple([
																	Type.bitstring("rel"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("stylesheet"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("href"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("/assets/css/app.css"),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("script"),
															Type.list([
																Type.tuple([
																	Type.bitstring("defer"),
																	Type.list([]),
																]),
																Type.tuple([
																	Type.bitstring("type"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("text/javascript"),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("src"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("/assets/js/app.js"),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([
													Type.atom("text"),
													Type.bitstring("\n\n  "),
												]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("body"),
													Type.list([
														Type.tuple([
															Type.bitstring("data-profile-location"),
															Type.list([
																Type.tuple([
																	Type.atom("expression"),
																	Type.tuple([
																		Interpreter.dotOperator(
																			context.vars.vars_0,
																			Type.atom("profile_location"),
																		),
																	]),
																]),
															]),
														]),
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"min-h-screen bg-app-canvas font-sans text-app-ink antialiased transition-colors duration-200 motion-reduce:transition-none",
																	),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Interpreter.case(
															Interpreter.dotOperator(
																context.vars.vars_0,
																Type.atom("show_global_flash"),
															),
															[
																{
																	match: Type.variablePattern("x_9"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_9,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_9,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n      "),
																			]),
																			Type.tuple([
																				Type.atom("element"),
																				Type.bitstring("div"),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("class"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"fixed right-4 top-4 z-50 space-y-2",
																								),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("aria-live"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring("polite"),
																							]),
																						]),
																					]),
																					Type.tuple([
																						Type.bitstring("id"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"flash-container",
																								),
																							]),
																						]),
																					]),
																				]),
																				Type.list([
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n        "),
																					]),
																					Interpreter.case(
																						Interpreter.dotOperator(
																							context.vars.vars_0,
																							Type.atom("flash_info"),
																						),
																						[
																							{
																								match:
																									Type.variablePattern("x_10"),
																								guards: [
																									(context) =>
																										Erlang["orelse/2"](
																											(context) =>
																												Erlang["=:=/2"](
																													context.vars.x_10,
																													Type.atom("false"),
																												),
																											(context) =>
																												Erlang["=:=/2"](
																													context.vars.x_10,
																													Type.atom("nil"),
																												),
																											context,
																										),
																								],
																								body: (context) => {
																									return Type.atom("nil");
																								},
																							},
																							{
																								match: Type.matchPlaceholder(),
																								guards: [],
																								body: (context) => {
																									return Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n          ",
																											),
																										]),
																										Type.tuple([
																											Type.atom("element"),
																											Type.bitstring("div"),
																											Type.list([
																												Type.tuple([
																													Type.bitstring(
																														"class",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-100",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring("id"),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"flash-info",
																															),
																														]),
																													]),
																												]),
																											]),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"flash_info",
																															),
																														),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n          ",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n        ",
																											),
																										]),
																									]);
																								},
																							},
																						],
																						context,
																					),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n\n        "),
																					]),
																					Interpreter.case(
																						Interpreter.dotOperator(
																							context.vars.vars_0,
																							Type.atom("flash_error"),
																						),
																						[
																							{
																								match:
																									Type.variablePattern("x_11"),
																								guards: [
																									(context) =>
																										Erlang["orelse/2"](
																											(context) =>
																												Erlang["=:=/2"](
																													context.vars.x_11,
																													Type.atom("false"),
																												),
																											(context) =>
																												Erlang["=:=/2"](
																													context.vars.x_11,
																													Type.atom("nil"),
																												),
																											context,
																										),
																								],
																								body: (context) => {
																									return Type.atom("nil");
																								},
																							},
																							{
																								match: Type.matchPlaceholder(),
																								guards: [],
																								body: (context) => {
																									return Type.list([
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n          ",
																											),
																										]),
																										Type.tuple([
																											Type.atom("element"),
																											Type.bitstring("div"),
																											Type.list([
																												Type.tuple([
																													Type.bitstring(
																														"class",
																													),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-100",
																															),
																														]),
																													]),
																												]),
																												Type.tuple([
																													Type.bitstring("id"),
																													Type.list([
																														Type.tuple([
																															Type.atom("text"),
																															Type.bitstring(
																																"flash-error",
																															),
																														]),
																													]),
																												]),
																											]),
																											Type.list([
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n            ",
																													),
																												]),
																												Type.tuple([
																													Type.atom(
																														"expression",
																													),
																													Type.tuple([
																														Interpreter.dotOperator(
																															context.vars
																																.vars_0,
																															Type.atom(
																																"flash_error",
																															),
																														),
																													]),
																												]),
																												Type.tuple([
																													Type.atom("text"),
																													Type.bitstring(
																														"\n          ",
																													),
																												]),
																											]),
																										]),
																										Type.tuple([
																											Type.atom("text"),
																											Type.bitstring(
																												"\n        ",
																											),
																										]),
																									]);
																								},
																							},
																						],
																						context,
																					),
																					Type.tuple([
																						Type.atom("text"),
																						Type.bitstring("\n      "),
																					]),
																				]),
																			]),
																			Type.tuple([
																				Type.atom("text"),
																				Type.bitstring("\n    "),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n\n    "),
														]),
														Interpreter.case(
															Interpreter.case(
																Interpreter.dotOperator(
																	context.vars.vars_0,
																	Type.atom("navigation"),
																),
																[
																	{
																		match: Type.variablePattern("x_12"),
																		guards: [
																			(context) =>
																				Erlang["orelse/2"](
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_12,
																							Type.atom("false"),
																						),
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.x_12,
																							Type.atom("nil"),
																						),
																					context,
																				),
																		],
																		body: (context) => {
																			return context.vars.x_12;
																		},
																	},
																	{
																		match: Type.matchPlaceholder(),
																		guards: [],
																		body: (context) => {
																			return Interpreter.dotOperator(
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("navigation"),
																				),
																				Type.atom("admin_bar?"),
																			);
																		},
																	},
																],
																context,
															),
															[
																{
																	match: Type.variablePattern("x_13"),
																	guards: [
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_13,
																						Type.atom("false"),
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.x_13,
																						Type.atom("nil"),
																					),
																				context,
																			),
																	],
																	body: (context) => {
																		return Type.atom("nil");
																	},
																},
																{
																	match: Type.matchPlaceholder(),
																	guards: [],
																	body: (context) => {
																		return Type.list([
																			Type.tuple([
																				Type.atom("component"),
																				Type.atom(
																					"Elixir.LeaplyWeb.Hologram.Components.ApplicationBar",
																				),
																				Type.list([
																					Type.tuple([
																						Type.bitstring("cid"),
																						Type.list([
																							Type.tuple([
																								Type.atom("text"),
																								Type.bitstring(
																									"application-bar",
																								),
																							]),
																						]),
																					]),
																				]),
																				Type.list([]),
																			]),
																		]);
																	},
																},
															],
															context,
														),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("slot"),
															Type.list([]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("component"),
															Type.atom("Elixir.Hologram.UI.Runtime"),
															Type.list([]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"__layout_module__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.atom("Elixir.LeaplyWeb.Hologram.Layouts.RootLayout");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"__layout_props__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.list([
						Type.tuple([
							Type.atom("page_title"),
							Type.bitstring("Professional profile — Leaply"),
						]),
						Type.tuple([
							Type.atom("robots"),
							Type.bitstring("noindex,nofollow"),
						]),
					]);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"__params__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_Enum["reverse/1"](Type.list([]));
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"__route__",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Type.bitstring("/_hologram/social/public-profile");
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"back_to_people_label",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					return Elixir_LeaplyWeb_Hologram_Helpers_ProfileText["get/1"](
						Type.bitstring("social_profile.public.back_to_people"),
					);
				},
			},
		],
	);

	Interpreter.defineElixirFunction(
		"LeaplyWeb.Hologram.Pages.Social.PublicProfilePage",
		"template",
		0,
		"public",
		[
			{
				params: (context) => [],
				guards: [],
				body: (context) => {
					globalThis.hologram.return = Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("vars_0")],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										context.vars.vars_0,
										Type.matchPlaceholder(),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.list([
										Type.tuple([
											Type.atom("element"),
											Type.bitstring("div"),
											Type.list([
												Type.tuple([
													Type.bitstring("id"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("social-public-profile-page"),
														]),
													]),
												]),
												Type.tuple([
													Type.bitstring("class"),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring(
																"min-h-screen bg-app-canvas text-app-ink",
															),
														]),
													]),
												]),
											]),
											Type.list([
												Type.tuple([Type.atom("text"), Type.bitstring("\n  ")]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("header"),
													Type.list([
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"border-b border-app-border bg-app-surface",
																	),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("element"),
															Type.bitstring("div"),
															Type.list([
																Type.tuple([
																	Type.bitstring("class"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"mx-auto flex h-18 max-w-6xl items-center justify-between px-4 sm:px-6",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("a"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("href"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("/"),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("class"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring(
																						"rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-500",
																					),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("id"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring(
																						"social-public-profile-logo-link",
																					),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.bitstring("aria-label"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("Leaply home"),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("img"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("src"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"/images/leaply.svg",
																							),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("width"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("120"),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("height"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("40"),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("alt"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("Leaply"),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("h-10 w-auto"),
																						]),
																					]),
																				]),
																			]),
																			Type.list([]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n\n      "),
																]),
																Type.tuple([
																	Type.atom("element"),
																	Type.bitstring("div"),
																	Type.list([
																		Type.tuple([
																			Type.bitstring("class"),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring(
																						"flex items-center gap-2",
																					),
																				]),
																			]),
																		]),
																	]),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("component"),
																			Type.atom(
																				"Elixir.LeaplyWeb.Hologram.Components.ThemeToggle",
																			),
																			Type.list([]),
																			Type.list([]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n        "),
																		]),
																		Type.tuple([
																			Type.atom("element"),
																			Type.bitstring("a"),
																			Type.list([
																				Type.tuple([
																					Type.bitstring("href"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring("/people"),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("class"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"inline-flex min-h-11 items-center rounded-md px-3 text-sm font-bold text-app-muted transition hover:bg-app-surface-muted hover:text-app-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
																							),
																						]),
																					]),
																				]),
																				Type.tuple([
																					Type.bitstring("id"),
																					Type.list([
																						Type.tuple([
																							Type.atom("text"),
																							Type.bitstring(
																								"social-public-profile-back-link",
																							),
																						]),
																					]),
																				]),
																			]),
																			Type.list([
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n          "),
																				]),
																				Type.tuple([
																					Type.atom("expression"),
																					Type.tuple([
																						Elixir_LeaplyWeb_Hologram_Pages_Social_PublicProfilePage[
																							"back_to_people_label/0"
																						](),
																					]),
																				]),
																				Type.tuple([
																					Type.atom("text"),
																					Type.bitstring("\n        "),
																				]),
																			]),
																		]),
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring("\n      "),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("\n    "),
																]),
															]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([
													Type.atom("text"),
													Type.bitstring("\n\n  "),
												]),
												Type.tuple([
													Type.atom("element"),
													Type.bitstring("main"),
													Type.list([
														Type.tuple([
															Type.bitstring("id"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring("main-content"),
																]),
															]),
														]),
														Type.tuple([
															Type.bitstring("class"),
															Type.list([
																Type.tuple([
																	Type.atom("text"),
																	Type.bitstring(
																		"mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10",
																	),
																]),
															]),
														]),
													]),
													Type.list([
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n    "),
														]),
														Type.tuple([
															Type.atom("component"),
															Type.atom(
																"Elixir.LeaplyWeb.Hologram.Components.Social.ProfilePublicSurface",
															),
															Type.list([
																Type.tuple([
																	Type.bitstring("profile"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("profile"),
																				),
																			]),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("display_name"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([Type.atom("nil")]),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("demographic_locale"),
																	Type.list([
																		Type.tuple([
																			Type.atom("expression"),
																			Type.tuple([
																				Interpreter.dotOperator(
																					context.vars.vars_0,
																					Type.atom("demographic_locale"),
																				),
																			]),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("surface_id"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"social-public-profile-surface",
																			),
																		]),
																	]),
																]),
																Type.tuple([
																	Type.bitstring("heading_id"),
																	Type.list([
																		Type.tuple([
																			Type.atom("text"),
																			Type.bitstring(
																				"social-public-profile-heading",
																			),
																		]),
																	]),
																]),
															]),
															Type.list([]),
														]),
														Type.tuple([
															Type.atom("text"),
															Type.bitstring("\n  "),
														]),
													]),
												]),
												Type.tuple([Type.atom("text"), Type.bitstring("\n")]),
											]),
										]),
									]);
								},
							},
						],
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return globalThis.hologram.return;
				},
			},
		],
	);

	Interpreter.defineElixirFunction("String", "build_filler", 5, "private", [
		{
			params: (context) => [
				Type.integer(0n),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.variablePattern("filler_3"),
			],
			guards: [],
			body: (context) => {
				return context.vars.filler_3;
			},
		},
		{
			params: (context) => [
				Type.variablePattern("count_0"),
				Type.variablePattern("source_1"),
				Type.list([]),
				Type.variablePattern("size_2"),
				Type.variablePattern("filler_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_String["build_filler/5"](
						Erlang["rem/2"](context.vars.count_0, context.vars.size_2),
						context.vars.source_1,
						context.vars.source_1,
						Type.integer(0n),
						Type.list([]),
					),
					Type.variablePattern("rem_filler_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_String["duplicate/2"](
						Erlang["iolist_to_binary/1"](context.vars.filler_3),
						Erlang["+/2"](
							Erlang["div/2"](context.vars.count_0, context.vars.size_2),
							Type.integer(1n),
						),
					),
					Type.variablePattern("filler_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.consOperator(
					context.vars.filler_5,
					context.vars.rem_filler_4,
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("count_0"),
				Type.variablePattern("source_1"),
				Type.consPattern(
					Type.variablePattern("elem_2"),
					Type.variablePattern("rest_3"),
				),
				Type.variablePattern("size_4"),
				Type.variablePattern("filler_5"),
			],
			guards: [(context) => Erlang["is_binary/1"](context.vars.elem_2)],
			body: (context) => {
				return Elixir_String["build_filler/5"](
					Erlang["-/2"](context.vars.count_0, Type.integer(1n)),
					context.vars.source_1,
					context.vars.rest_3,
					Erlang["+/2"](context.vars.size_4, Type.integer(1n)),
					Interpreter.consOperator(context.vars.filler_5, context.vars.elem_2),
				);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.consPattern(
					Type.variablePattern("elem_2"),
					Type.matchPlaceholder(),
				),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Erlang["error/1"](
					Elixir_ArgumentError["exception/1"](
						Type.bitstring([
							Type.bitstringSegment(
								Type.string("expected a string padding element, got: "),
								{ type: "binary" },
							),
							Type.bitstringSegment(
								Elixir_Kernel["inspect/1"](context.vars.elem_2),
								{ type: "binary" },
							),
						]),
					),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "capitalize", 1, "public", [
		{
			params: (context) => [Type.variablePattern("x0_0")],
			guards: [],
			body: (context) => {
				return Elixir_String["capitalize/2"](
					context.vars.x0_0,
					Type.atom("default"),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "capitalize", 2, "public", [
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.variablePattern("char_0"), {
						type: "integer",
					}),
					Type.bitstringSegment(Type.variablePattern("rest_1"), {
						type: "binary",
					}),
				]),
				Type.atom("ascii"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Interpreter.case(
							Erlang[">=/2"](context.vars.char_0, Type.integer(97n)),
							[
								{
									match: Type.atom("false"),
									guards: [],
									body: (context) => {
										return Type.atom("false");
									},
								},
								{
									match: Type.atom("true"),
									guards: [],
									body: (context) => {
										return Erlang["=</2"](
											context.vars.char_0,
											Type.integer(122n),
										);
									},
								},
							],
							context,
						),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return context.vars.char_0;
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Erlang["-/2"](context.vars.char_0, Type.integer(32n));
								},
							},
						],
						context,
					),
					Type.variablePattern("char_2"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.bitstring([
					Type.bitstringSegment(context.vars.char_2, { type: "integer" }),
					Type.bitstringSegment(
						Elixir_String["downcase/2"](
							context.vars.rest_1,
							Type.atom("ascii"),
						),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("i"), { type: "binary" }),
					Type.bitstringSegment(Type.variablePattern("right_0"), {
						type: "binary",
					}),
				]),
				Type.variablePattern("mode_1"),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Interpreter.case(
							Erlang["==/2"](context.vars.mode_1, Type.atom("turkic")),
							[
								{
									match: Type.atom("false"),
									guards: [],
									body: (context) => {
										return Type.bitstring("I");
									},
								},
								{
									match: Type.atom("true"),
									guards: [],
									body: (context) => {
										return Type.bitstring("İ");
									},
								},
							],
							context,
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_String["downcase/2"](
							context.vars.right_0,
							context.vars.mode_1,
						),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("string_0"),
				Type.variablePattern("mode_1"),
			],
			guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)],
			body: (context) => {
				return Interpreter.case(
					Erlang_Unicode_Util["gc/1"](context.vars.string_0),
					[
						{
							match: Type.list([Type.variablePattern("gc_2")]),
							guards: [],
							body: (context) => {
								return Elixir_String["grapheme_to_binary/1"](
									Erlang_String["titlecase/1"](Type.list([context.vars.gc_2])),
								);
							},
						},
						{
							match: Type.list([
								Type.variablePattern("gc_3"),
								Type.variablePattern("rest_4"),
							]),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Elixir_String["grapheme_to_binary/1"](
											Erlang_String["titlecase/1"](
												Type.list([context.vars.gc_3]),
											),
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Elixir_String["downcase/2"](
											context.vars.rest_4,
											context.vars.mode_1,
										),
										{ type: "binary" },
									),
								]);
							},
						},
						{
							match: Type.consPattern(
								Type.variablePattern("gc_5"),
								Type.variablePattern("rest_6"),
							),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Elixir_String["grapheme_to_binary/1"](
											Erlang_String["titlecase/1"](
												Type.list([context.vars.gc_5]),
											),
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Elixir_String["downcase/2"](
											context.vars.rest_6,
											context.vars.mode_1,
										),
										{ type: "binary" },
									),
								]);
							},
						},
						{
							match: Type.list([]),
							guards: [],
							body: (context) => {
								return Type.bitstring("");
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.bitstringPattern([
									Type.bitstringSegment(Type.variablePattern("byte_7"), {
										type: "integer",
									}),
									Type.bitstringSegment(Type.variablePattern("rest_8"), {
										type: "bitstring",
									}),
								]),
							]),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(context.vars.byte_7, {
										type: "integer",
									}),
									Type.bitstringSegment(
										Elixir_String["downcase/2"](
											context.vars.rest_8,
											context.vars.mode_1,
										),
										{ type: "binary" },
									),
								]);
							},
						},
					],
					context,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "do_graphemes", 1, "private", [
		{
			params: (context) => [Type.variablePattern("gcs_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Erlang_Unicode_Util["gc/1"](context.vars.gcs_0),
					[
						{
							match: Type.consPattern(
								Type.variablePattern("gc_1"),
								Type.variablePattern("rest_2"),
							),
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									Elixir_String["grapheme_to_binary/1"](context.vars.gc_1),
									Elixir_String["do_graphemes/1"](context.vars.rest_2),
								);
							},
						},
						{
							match: Type.list([]),
							guards: [],
							body: (context) => {
								return Type.list([]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.bitstringPattern([
									Type.bitstringSegment(Type.variablePattern("byte_3"), {
										type: "integer",
									}),
									Type.bitstringSegment(Type.variablePattern("rest_4"), {
										type: "bitstring",
									}),
								]),
							]),
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									Type.bitstring([
										Type.bitstringSegment(context.vars.byte_3, {
											type: "integer",
										}),
									]),
									Elixir_String["do_graphemes/1"](context.vars.rest_4),
								);
							},
						},
					],
					context,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "duplicate", 2, "public", [
		{
			params: (context) => [
				Type.variablePattern("subject_0"),
				Type.variablePattern("n_1"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) => Erlang["is_binary/1"](context.vars.subject_0),
								(context) => Erlang["is_integer/1"](context.vars.n_1),
								context,
							),
						(context) => Erlang[">=/2"](context.vars.n_1, Type.integer(0n)),
						context,
					),
			],
			body: (context) => {
				return Erlang_Binary["copy/2"](
					context.vars.subject_0,
					context.vars.n_1,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "first", 1, "public", [
		{
			params: (context) => [Type.variablePattern("string_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)],
			body: (context) => {
				return Interpreter.case(
					Erlang_Unicode_Util["gc/1"](context.vars.string_0),
					[
						{
							match: Type.consPattern(
								Type.variablePattern("gc_1"),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return Elixir_String["grapheme_to_binary/1"](context.vars.gc_1);
							},
						},
						{
							match: Type.list([]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.bitstringPattern([
									Type.bitstringSegment(Type.variablePattern("byte_2"), {
										type: "integer",
									}),
									Type.bitstringSegment(Type.matchPlaceholder(), {
										type: "bitstring",
									}),
								]),
							]),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(context.vars.byte_2, {
										type: "integer",
									}),
								]);
							},
						},
					],
					context,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "graphemes", 1, "public", [
		{
			params: (context) => [Type.variablePattern("string_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)],
			body: (context) => {
				return Elixir_String["do_graphemes/1"](context.vars.string_0);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "length", 1, "public", [
		{
			params: (context) => [Type.variablePattern("string_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)],
			body: (context) => {
				return Elixir_String["length/2"](
					context.vars.string_0,
					Type.integer(0n),
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "length", 2, "private", [
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("binary_3"),
					Type.bitstringPattern([
						Type.bitstringSegment(Type.variablePattern("byte1_0"), {
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("byte2_1"), {
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("rest_2"), {
							type: "binary",
						}),
					]),
					context,
				),
				Type.variablePattern("acc_4"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang["andalso/2"](
										(context) =>
											Erlang["=</2"](context.vars.byte1_0, Type.integer(127n)),
										(context) =>
											Erlang["/=/2"](context.vars.byte1_0, Type.integer(13n)),
										context,
									),
								(context) =>
									Erlang["=</2"](context.vars.byte2_1, Type.integer(127n)),
								context,
							),
						(context) =>
							Erlang["/=/2"](context.vars.byte2_1, Type.integer(13n)),
						context,
					),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_String["skip_length/2"](context.vars.rest_2, Type.integer(1n)),
					Type.variablePattern("skip_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_String["length/2"](
					Erlang["binary_part/3"](
						context.vars.binary_3,
						context.vars.skip_5,
						Erlang["-/2"](
							Erlang["byte_size/1"](context.vars.binary_3),
							context.vars.skip_5,
						),
					),
					Erlang["+/2"](context.vars.acc_4, context.vars.skip_5),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("gcs_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Erlang_Unicode_Util["gc/1"](context.vars.gcs_0),
					[
						{
							match: Type.consPattern(
								Type.matchPlaceholder(),
								Type.variablePattern("rest_2"),
							),
							guards: [],
							body: (context) => {
								return Elixir_String["length/2"](
									context.vars.rest_2,
									Erlang["+/2"](context.vars.acc_1, Type.integer(1n)),
								);
							},
						},
						{
							match: Type.list([]),
							guards: [],
							body: (context) => {
								return context.vars.acc_1;
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.bitstringPattern([
									Type.bitstringSegment(Type.matchPlaceholder(), {
										type: "integer",
									}),
									Type.bitstringSegment(Type.variablePattern("rest_3"), {
										type: "bitstring",
									}),
								]),
							]),
							guards: [],
							body: (context) => {
								return Elixir_String["length/2"](
									context.vars.rest_3,
									Erlang["+/2"](context.vars.acc_1, Type.integer(1n)),
								);
							},
						},
					],
					context,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "pad", 4, "private", [
		{
			params: (context) => [
				Type.variablePattern("kind_0"),
				Type.variablePattern("string_1"),
				Type.variablePattern("count_2"),
				Type.variablePattern("padding_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_String["length/1"](context.vars.string_1),
					Type.variablePattern("string_length_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				globalThis.hologram.return = Interpreter.case(
					Erlang[">=/2"](context.vars.string_length_4, context.vars.count_2),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Elixir_String["build_filler/5"](
										Erlang["-/2"](
											context.vars.count_2,
											context.vars.string_length_4,
										),
										context.vars.padding_3,
										context.vars.padding_3,
										Type.integer(0n),
										Type.list([]),
									),
									Type.variablePattern("filler_5"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Erlang["iolist_to_binary/1"](
									Interpreter.case(
										context.vars.kind_0,
										[
											{
												match: Type.atom("leading"),
												guards: [],
												body: (context) => {
													return Interpreter.consOperator(
														context.vars.filler_5,
														context.vars.string_1,
													);
												},
											},
											{
												match: Type.atom("trailing"),
												guards: [],
												body: (context) => {
													return Interpreter.consOperator(
														context.vars.string_1,
														context.vars.filler_5,
													);
												},
											},
										],
										context,
									),
								);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return context.vars.string_1;
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.hologram.return;
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "pad_leading", 3, "public", [
		{
			params: (context) => [
				Type.variablePattern("string_0"),
				Type.variablePattern("count_1"),
				Type.variablePattern("padding_2"),
			],
			guards: [(context) => Erlang["is_binary/1"](context.vars.padding_2)],
			body: (context) => {
				return Elixir_String["pad_leading/3"](
					context.vars.string_0,
					context.vars.count_1,
					Elixir_String["graphemes/1"](context.vars.padding_2),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("string_0"),
				Type.variablePattern("count_1"),
				Interpreter.matchOperator(
					Type.variablePattern("padding_2"),
					Type.consPattern(Type.matchPlaceholder(), Type.matchPlaceholder()),
					context,
				),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) => Erlang["is_binary/1"](context.vars.string_0),
								(context) => Erlang["is_integer/1"](context.vars.count_1),
								context,
							),
						(context) => Erlang[">=/2"](context.vars.count_1, Type.integer(0n)),
						context,
					),
			],
			body: (context) => {
				return Elixir_String["pad/4"](
					Type.atom("leading"),
					context.vars.string_0,
					context.vars.count_1,
					context.vars.padding_2,
				);
			},
		},
	]);

	Interpreter.defineElixirFunction("String", "skip_length", 2, "private", [
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.variablePattern("byte_0"), {
						type: "integer",
					}),
					Type.bitstringSegment(Type.variablePattern("rest_1"), {
						type: "binary",
					}),
				]),
				Type.variablePattern("acc_2"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["=</2"](context.vars.byte_0, Type.integer(127n)),
						(context) => Erlang["/=/2"](context.vars.byte_0, Type.integer(13n)),
						context,
					),
			],
			body: (context) => {
				return Elixir_String["skip_length/2"](
					context.vars.rest_1,
					Erlang["+/2"](context.vars.acc_2, Type.integer(1n)),
				);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.variablePattern("acc_1"),
			],
			guards: [],
			body: (context) => {
				return context.vars.acc_1;
			},
		},
	]);
};

globalThis.hologram.pageScriptLoaded = true;
document.dispatchEvent(new CustomEvent("hologram:pageScriptLoaded"));

console.debug(
	"Hologram: page script executed in",
	PerformanceTimer.diff(startTime),
);
